import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search, Users, Globe, MapPin, ArrowUpDown, ArrowUp, ArrowDown,
  ChevronLeft, ChevronRight, Filter, X, BarChart3, Activity,
  Smartphone, Layers, RefreshCw, AlertTriangle, Heart,
  TrendingDown, Zap, ShieldAlert, Star, Eye, ChevronDown,
  LayoutDashboard, Sun, Moon, UserCheck, Phone, MessageCircle, Mail,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { SiteThemeProvider, useSiteTheme } from "../../contexts/SiteTheme";
import mixIndexRaw from "../../data/phone-mix-index.json";
import summitRaw from "../../data/summit-billing.json";
import adminCurrentUsersSnapshotRaw from "../../data/admin-current-users-snapshot.json";
import adminSalonOsDirectoryRaw from "../../data/admin-salonos-directory-snapshot.json";
import summitCustomerAliasesRaw from "../../data/summit-customer-aliases.json";
import summitManualOverridesRaw from "../../data/gtm/manual-overrides.json";
import summitBillingReconciliationRaw from "../../data/summit-billing-reconciliation.json";
import summitStandingOrdersRaw from "../../data/summit-standing-orders.json";
import { UsageImportPanel } from "./UsageImportPanel";
import { ProductCatalogImportPanel } from "./ProductCatalogImportPanel";
import ProductTruthCenterPanel from "./ProductTruthCenterPanel";
import { CatalogBrowserPanel } from "./CatalogBrowserPanel";
import { BeautyIntelligencePanel } from "./BeautyIntelligencePanel";
import { ProductDatabasePage } from "./ProductDatabasePage";
import { ProductResolutionPage } from "./ProductResolutionPage";
import { Upload, Boxes, ShieldCheck, Database, BookOpen, Palette } from "lucide-react";
import { fetchSnapshot } from "../../lib/usageImportClient";

const TrussCatalogPanel = React.lazy(() =>
  import("./TrussCatalogPanel").then((m) => ({ default: m.TrussCatalogPanel })),
);

// ═══════════════════════════════════════════════════════════════════════
// 1. PHONE → COUNTRY INFERENCE
// ═══════════════════════════════════════════════════════════════════════

function inferCountryFromPhone(phone: string): string {
  if (!phone) return "";
  const clean = phone.replace(/[\s\-\(\)]/g, "");
  if (/^05\d{8}$/.test(clean) || /^0[2-9]\d{7,8}$/.test(clean)) return "ISRAEL";
  if (/^972\d{9,10}$/.test(clean)) return "ISRAEL";
  if (/^07[0-9]{9,10}$/.test(clean) || /^447\d{9}$/.test(clean)) return "UK";
  if (/^351\d{9}$/.test(clean) || (/^9[12356]\d{7}$/.test(clean) && clean.length === 9)) return "PORTUGAL";
  if (/^39\d{9,10}$/.test(clean) || /^393\d{9}$/.test(clean)) return "ITALY";
  if (/^81\d{9,10}$/.test(clean)) return "JAPAN";
  if (/^30\d{10}$/.test(clean) || /^003\d{11,12}$/.test(clean)) return "GREECE";
  if (/^375\d{9}$/.test(clean)) return "BELARUS";
  if (/^79\d{9}$/.test(clean)) return "RUSSIA";
  if (/^61\d{9}$/.test(clean) || (/^41\d{7}$/.test(clean) && clean.length === 9)) return "AUSTRALIA";
  if (/^31\d{9}$/.test(clean) || (/^6[0-9]{8}$/.test(clean) && clean.length === 9)) return "NETHERLANDS";
  if (/^353\d{7,9}$/.test(clean) || /^08[0-9]{8}$/.test(clean)) return "IRELAND";
  if (clean.length === 10 && /^[2-9]\d{9}$/.test(clean)) return "USA";
  if (clean.length === 11 && clean.startsWith("1")) return "USA";
  return "";
}

// ═══════════════════════════════════════════════════════════════════════
// 2. DATE PARSING — relative strings → day count
// ═══════════════════════════════════════════════════════════════════════

function parseDaysAgo(dateStr: string | null | undefined): number | null {
  if (!dateStr || dateStr === "-") return null;
  const s = dateStr.trim().toLowerCase();
  if (s === "just now") return 0;
  // Sub-day precision: minutes → fractional days
  const minMatch = s.match(/^(\d+)\s*minutes?\s*ago$/i);
  if (minMatch) return parseInt(minMatch[1], 10) / 1440;
  // Hours → fractional days
  const hrMatch = s.match(/^(\d+)\s*hours?\s*ago$/i);
  if (hrMatch) return parseInt(hrMatch[1], 10) / 24;
  // Fallback catch-all for minute/hour strings
  if (s.includes("minute") || s.includes("hour")) return 0;
  // Days / months / years
  const m = s.match(/^(\d+)\s*(days?|months?|years?)\s*ago$/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  const unit = m[2].replace(/s$/, "");
  if (unit === "day") return n;
  if (unit === "month") return n * 30;
  if (unit === "year") return n * 365;
  return null;
}

// ═══════════════════════════════════════════════════════════════════════
// 3. HEALTH SCORE (0–100)
// ═══════════════════════════════════════════════════════════════════════

interface HealthResult {
  score: number;
  status: "healthy" | "at_risk" | "critical";
  factors: string[];
}

function computeHealth(
  daysInactive: number | null,
  tenureDays: number | null,
  profiles: number,
): HealthResult {
  let recency = 0;
  if (daysInactive === null) recency = 0;
  else if (daysInactive <= 1) recency = 100;
  else if (daysInactive <= 7) recency = 85;
  else if (daysInactive <= 14) recency = 55;
  else if (daysInactive <= 30) recency = 25;
  else recency = Math.max(0, 10 - (daysInactive - 30) / 10);

  let frequency = 0;
  if (tenureDays !== null && tenureDays > 0 && daysInactive !== null) {
    const activeDays = Math.max(0, tenureDays - daysInactive);
    frequency = Math.min(100, (activeDays / tenureDays) * 120);
  } else if (daysInactive === 0) frequency = 80;

  let adoption = 0;
  if (profiles === 0) adoption = 0;
  else if (profiles === 1) adoption = 30;
  else if (profiles <= 3) adoption = 60;
  else if (profiles <= 5) adoption = 80;
  else adoption = 100;

  let engagement = 50;
  if (tenureDays !== null && tenureDays > 90 && daysInactive !== null && daysInactive <= 7) engagement = 100;
  else if (tenureDays !== null && tenureDays > 180 && (daysInactive === null || daysInactive > 30)) engagement = 10;
  else if (daysInactive !== null && daysInactive <= 14) engagement = 70;

  const score = Math.round(recency * 0.40 + frequency * 0.25 + adoption * 0.20 + engagement * 0.15);
  const factors: string[] = [];
  if (daysInactive === null) factors.push("Never mixed");
  else if (daysInactive > 14) factors.push(`${daysInactive}d inactive`);
  else if (daysInactive > 7) factors.push(`${daysInactive}d since last mix`);
  if (profiles === 0) factors.push("No profiles");
  else if (profiles === 1 && tenureDays !== null && tenureDays > 90) factors.push("Only 1 profile after " + Math.round(tenureDays / 30) + " months");
  if (tenureDays !== null && tenureDays > 180 && (daysInactive === null || daysInactive > 60)) factors.push("Long tenure, no engagement");

  const status = score >= 70 ? "healthy" : score >= 40 ? "at_risk" : "critical";
  return { score, status, factors: factors.slice(0, 2) };
}

// ═══════════════════════════════════════════════════════════════════════
// 4. LIFECYCLE CLASSIFICATION
// ═══════════════════════════════════════════════════════════════════════

type Lifecycle = "new" | "activated" | "engaged" | "power_user" | "fading" | "dormant";

function classifyLifecycle(tenureDays: number | null, daysInactive: number | null, profiles: number, hasMixed: boolean): Lifecycle {
  if (!hasMixed) return tenureDays !== null && tenureDays <= 30 ? "new" : "dormant";
  if (daysInactive !== null && daysInactive > 14) return "dormant";
  if (daysInactive !== null && daysInactive > 7) return "fading";
  if (profiles >= 5 && daysInactive !== null && daysInactive <= 7) return "power_user";
  if (daysInactive !== null && daysInactive <= 7 && tenureDays !== null && tenureDays > 30) return "engaged";
  if (hasMixed && tenureDays !== null && tenureDays <= 60) return "activated";
  return "engaged";
}

const LIFECYCLE_CONFIG: Record<Lifecycle, { label: string; color: string; bg: string }> = {
  new:        { label: "New",        color: "text-sky-400",     bg: "bg-sky-500/10 border-sky-500/20" },
  activated:  { label: "Activated",  color: "text-blue-400",    bg: "bg-blue-500/10 border-blue-500/20" },
  engaged:    { label: "Engaged",    color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
  power_user: { label: "Power User", color: "text-violet-400",  bg: "bg-violet-500/10 border-violet-500/20" },
  fading:     { label: "Fading",     color: "text-amber-400",   bg: "bg-amber-500/10 border-amber-500/20" },
  dormant:    { label: "Dormant",    color: "text-gray-400",    bg: "bg-gray-500/10 border-gray-500/20" },
};

// ═══════════════════════════════════════════════════════════════════════
// 5. RISK TAGS
// ═══════════════════════════════════════════════════════════════════════

type RiskTag = "no_first_mix" | "sudden_drop" | "low_adoption" | "high_potential" | "recovered";

function computeRiskTags(hasMixed: boolean, daysInactive: number | null, tenureDays: number | null, profiles: number): RiskTag[] {
  const tags: RiskTag[] = [];
  if (!hasMixed) tags.push("no_first_mix");
  if (profiles >= 5) tags.push("high_potential");
  if (hasMixed && profiles >= 2 && daysInactive !== null && daysInactive > 14) tags.push("sudden_drop");
  if (profiles <= 1 && tenureDays !== null && tenureDays > 90 && hasMixed) tags.push("low_adoption");
  if (tenureDays !== null && tenureDays > 90 && daysInactive !== null && daysInactive <= 7) tags.push("recovered");
  return tags;
}

const RISK_TAG_CONFIG: Record<RiskTag, { label: string; color: string }> = {
  no_first_mix:   { label: "No First Mix",      color: "text-red-400 bg-red-500/10" },
  sudden_drop:    { label: "Sudden Drop",        color: "text-orange-400 bg-orange-500/10" },
  low_adoption:   { label: "Low Adoption",       color: "text-amber-400 bg-amber-500/10" },
  high_potential: { label: "High Potential",     color: "text-violet-400 bg-violet-500/10" },
  recovered:      { label: "Recovered",          color: "text-emerald-400 bg-emerald-500/10" },
};

// ═══════════════════════════════════════════════════════════════════════
// 6. RECOMMENDED CS ACTION
// ═══════════════════════════════════════════════════════════════════════

type CSAction = "send_tip" | "check_in" | "immediate_outreach" | "recovery_followup";

function recommendAction(health: HealthResult, lifecycle: Lifecycle, tags: RiskTag[]): CSAction {
  if (health.status === "critical" || lifecycle === "dormant") return "immediate_outreach";
  if (tags.includes("recovered")) return "recovery_followup";
  if (health.status === "at_risk" || lifecycle === "fading") return "check_in";
  return "send_tip";
}

const ACTION_CONFIG: Record<CSAction, { label: string; icon: typeof Mail; color: string; bg: string }> = {
  send_tip:           { label: "Send Tip",           icon: Zap,            color: "text-emerald-400", bg: "bg-emerald-500/10 hover:bg-emerald-500/20" },
  check_in:           { label: "Schedule Check-in",  icon: Phone,          color: "text-amber-400",   bg: "bg-amber-500/10 hover:bg-amber-500/20" },
  immediate_outreach: { label: "Outreach Now",        icon: ShieldAlert,    color: "text-red-400",     bg: "bg-red-500/10 hover:bg-red-500/20" },
  recovery_followup:  { label: "Recovery Follow-up",  icon: MessageCircle,  color: "text-blue-400",    bg: "bg-blue-500/10 hover:bg-blue-500/20" },
};

// ═══════════════════════════════════════════════════════════════════════
// 7. GEOGRAPHY NORMALIZATION
// ═══════════════════════════════════════════════════════════════════════

const COUNTRY_ALIASES: Record<string, string> = {
  // Israel
  "israel": "ISRAEL", "il": "ISRAEL",
  // UK / England
  "england": "UK", "united kingdom": "UK", "uk": "UK", "great britain": "UK", "gb": "UK",
  // USA + US states / cities that appear in state field
  "usa": "USA", "united states": "USA", "us": "USA",
  "tennesse": "USA", "tennessee": "USA", "texas": "USA", "california": "USA",
  "florida": "USA", "new york": "USA",
  // Ireland
  "irland": "IRELAND", "ireland": "IRELAND",
  // Portugal
  "portugal": "PORTUGAL",
  // Belarus
  "belarus": "BELARUS",
  // Italy
  "italy": "ITALY",
  // Japan
  "japan": "JAPAN",
  // Australia
  "australia": "AUSTRALIA",
  // Netherlands
  "netherlands": "NETHERLANDS",
  // Canada
  "canada": "CANADA",
  // Greece
  "greece": "GREECE",
  // Russia
  "russia": "RUSSIA",
};

// Values that look like city names, technical glitches, or test entries in the State field
const INVALID_STATE_VALUES = new Set([
  "mistake", "sent", "opposite", "ios", "android",
]);

function normalizeAdminCountry(state: string | undefined): string {
  if (!state || state.trim() === "") return "";
  const trimmed = state.trim();
  const lower = trimmed.toLowerCase();
  if (INVALID_STATE_VALUES.has(lower)) return "";
  return COUNTRY_ALIASES[lower] || trimmed.toUpperCase();
}

// ═══════════════════════════════════════════════════════════════════════
// 7b. PHONE → MIX STATS LOOKUP (from phone-mix-index.json)
// ═══════════════════════════════════════════════════════════════════════

interface MixMonthPoint {
  month: string;
  sortIdx: number;
  mixes: number;
  visits: number;
}

interface MixStats {
  userId: string;
  totalMixes: number;
  monthsActive: number;
  firstMonth: string;
  lastMonth: string;
  brandsUsed: number;
  monthlyServices?: MixMonthPoint[];
}

// Directory joins use digits only so formatting and hidden Unicode marks cannot
// prevent the same phone from matching across legacy and SalonOS exports.
function normalizePhone(raw: string | number | undefined | null): string {
  if (raw == null) return "";
  return String(raw).replace(/\D/g, "");
}

// Mutable so we can swap in the live, DB-backed phone-mix after the
// admin uploads a fresh report. Initial value comes from the bundled
// snapshot so first paint stays instant.
let MIX_INDEX: Record<string, MixStats> =
  ((mixIndexRaw as any).byPhone as Record<string, MixStats>) || {};
const mixIndexSubscribers = new Set<() => void>();

function setMixIndex(next: Record<string, MixStats>) {
  MIX_INDEX = next || {};
  mixIndexSubscribers.forEach((cb) => {
    try { cb(); } catch { /* noop */ }
  });
}

function useMixIndexVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const cb = () => setVersion((v) => v + 1);
    mixIndexSubscribers.add(cb);
    return () => { mixIndexSubscribers.delete(cb); };
  }, []);
  return version;
}

function getMixStats(phone: string | undefined | null): MixStats | null {
  if (!phone) return null;
  return MIX_INDEX[normalizePhone(phone)] ?? null;
}

const MIX_MONTH_ORDER: Record<string, number> = {
  jan: 1, january: 1,
  feb: 2, february: 2,
  mar: 3, march: 3,
  apr: 4, april: 4,
  may: 5,
  jun: 6, june: 6,
  jul: 7, july: 7,
  aug: 8, august: 8,
  sep: 9, september: 9,
  oct: 10, october: 10,
  nov: 11, november: 11,
  dec: 12, december: 12,
};

function mixMonthSortValue(label: string | undefined | null): number {
  if (!label) return 0;
  const match = label.trim().toLowerCase().match(/^([a-z]+)\s+(\d{4})$/);
  if (!match) return 0;
  const month = MIX_MONTH_ORDER[match[1]] || 0;
  const year = Number(match[2]);
  return month && Number.isFinite(year) ? year * 100 + month : 0;
}

function latestMixMonth(index: Record<string, MixStats>): number {
  return Object.values(index).reduce(
    (latest, stats) => Math.max(latest, mixMonthSortValue(stats.lastMonth)),
    0,
  );
}

// Pull the latest DB-backed phone mix index and swap it into the
// in-memory MIX_INDEX so every cohort / usage panel re-renders with
// fresh Total Mixes. Safe no-op when the snapshot endpoint is offline —
// the bundled phone-mix-index.json keeps powering the table.
async function refreshGlobalMixIndex(): Promise<void> {
  try {
    const snap = await fetchSnapshot({ includePhone: true });
    const phoneIdx = (snap as any).phoneIndex;
    const byPhone = phoneIdx && phoneIdx.byPhone;
    if (byPhone && typeof byPhone === "object") {
      const next = byPhone as Record<string, MixStats>;
      // Local previews often have a fresher bundled index than the DB snapshot.
      // Do not replace May+ local data with an older live snapshot that only
      // reaches April; after a real import, the DB snapshot will sort newer/equal.
      if (latestMixMonth(next) >= latestMixMonth(MIX_INDEX)) {
        setMixIndex(next);
      }
    }
  } catch {
    // Silent — bundled phone-mix-index keeps working.
  }
}

// ═══════════════════════════════════════════════════════════════════════
// 8. COHORT BUCKETING
// ═══════════════════════════════════════════════════════════════════════

type UsageBucket = "recent14" | "recent30" | "recent90" | "recent180" | "recent365" | "over1year" | "never";

function getCohortBucket(daysInactive: number | null): UsageBucket {
  if (daysInactive === null) return "never";
  if (daysInactive <= 14) return "recent14";
  if (daysInactive <= 30) return "recent30";
  if (daysInactive <= 90) return "recent90";
  if (daysInactive <= 180) return "recent180";
  if (daysInactive <= 365) return "recent365";
  return "over1year";
}

const BUCKET_CONFIG: Record<UsageBucket, { label: string; sublabel: string; colorDark: string; colorLight: string; bgDark: string; bgLight: string; ring: string }> = {
  recent14:  { label: "14 Days",     sublabel: "Last 14 days",       colorDark: "text-emerald-400",  colorLight: "text-emerald-700",  bgDark: "bg-emerald-500/10 border-emerald-500/20",  bgLight: "bg-emerald-50 border-emerald-200",  ring: "ring-emerald-500/30" },
  recent30:  { label: "30 Days",     sublabel: "Last 30 days",       colorDark: "text-blue-400",     colorLight: "text-blue-700",     bgDark: "bg-blue-500/10 border-blue-500/20",        bgLight: "bg-blue-50 border-blue-200",        ring: "ring-blue-500/30" },
  recent90:  { label: "3 Months",    sublabel: "Last 3 months",      colorDark: "text-sky-400",      colorLight: "text-sky-700",      bgDark: "bg-sky-500/10 border-sky-500/20",          bgLight: "bg-sky-50 border-sky-200",          ring: "ring-sky-500/30" },
  recent180: { label: "6 Months",    sublabel: "Last 6 months",      colorDark: "text-amber-400",    colorLight: "text-amber-700",    bgDark: "bg-amber-500/10 border-amber-500/20",      bgLight: "bg-amber-50 border-amber-200",      ring: "ring-amber-500/30" },
  recent365: { label: "Last Year",   sublabel: "Last 12 months",     colorDark: "text-orange-400",   colorLight: "text-orange-700",   bgDark: "bg-orange-500/10 border-orange-500/20",    bgLight: "bg-orange-50 border-orange-200",    ring: "ring-orange-500/30" },
  over1year: { label: "1+ Year Ago", sublabel: "Over a year ago",    colorDark: "text-red-400",      colorLight: "text-red-700",      bgDark: "bg-red-500/10 border-red-500/20",          bgLight: "bg-red-50 border-red-200",          ring: "ring-red-500/30" },
  never:     { label: "Never Mixed", sublabel: "No mixes recorded",  colorDark: "text-white/50",     colorLight: "text-gray-400",     bgDark: "bg-white/5 border-white/10",               bgLight: "bg-gray-50 border-gray-200",        ring: "ring-gray-300/30" },
};

// ═══════════════════════════════════════════════════════════════════════
// 8b. RETENTION / CHURN INTELLIGENCE
// ═══════════════════════════════════════════════════════════════════════

type RetentionStatus =
  | "healthy"
  | "growing"
  | "recovered"
  | "at_risk"
  | "declining"
  | "churned"
  | "never_activated";

interface RetentionMetrics {
  status: RetentionStatus;
  totalMixes: number;
  monthsActive: number;
  monthlyMixRate: number;   // avg mixes / active month
  recentAvg: number;        // avg mixes over last 2 months
  baselineAvg: number;      // avg mixes over the 6 months before that
  trendPct: number | null;  // recent vs baseline, e.g. -45 means down 45%
  isPower: boolean;         // historically high-value user
  valueScore: number;       // 0-100, weighted by historical mixes + profiles
  riskScore: number;        // 0-100, weighted by inactivity + decline
  priorityScore: number;    // 0-100, value x risk — who to act on first
  estimatedMixesLost: number; // monthly mixes no longer happening
}

// Historical-value thresholds (in total mixes)
const RETENTION_POWER_THRESHOLD = 30; // "power" / high-value customer
const RETENTION_ACTIVATED_THRESHOLD = 5; // below this = activation problem

function avgMixes(points: MixMonthPoint[]): number {
  if (!points.length) return 0;
  return points.reduce((s, p) => s + (p.mixes || 0), 0) / points.length;
}

function computeRetentionMetrics(args: {
  daysInactive: number | null;
  tenureDays: number | null;
  profiles: number;
  mix: MixStats | null;
}): RetentionMetrics {
  const { daysInactive, profiles, mix } = args;
  const totalMixes = mix?.totalMixes ?? 0;
  const monthsActive = mix?.monthsActive ?? 0;
  const series = (mix?.monthlyServices ?? []).slice().sort((a, b) => a.sortIdx - b.sortIdx);

  const recentWindow = series.slice(-2);
  const baselineWindow = series.slice(-8, -2); // up to 6 months before the recent 2
  const recentAvg = avgMixes(recentWindow);
  const baselineAvg = avgMixes(baselineWindow);
  const peakMonth = series.reduce((mx, m) => Math.max(mx, m.mixes || 0), 0);
  const trendPct = baselineAvg > 0 ? Math.round((recentAvg / baselineAvg - 1) * 100) : null;
  const monthlyMixRate = monthsActive > 0 ? totalMixes / monthsActive : 0;

  const isPower = totalMixes >= RETENTION_POWER_THRESHOLD;
  const hasHistory = totalMixes > 0 && series.length > 0;

  // Recovery: a recent quiet stretch (near zero) directly followed by a strong
  // return in the latest month — a genuine comeback, not just an uneven ramp-up.
  const lastMonthMixes = series.length ? series[series.length - 1].mixes : 0;
  const preReturnWindow = series.slice(-4, -1); // up to 3 months before the last
  const preReturnAvg = avgMixes(preReturnWindow);
  const recoveredNow = hasHistory && daysInactive !== null && daysInactive <= 14 &&
    lastMonthMixes > 0 && peakMonth > 0 &&
    preReturnAvg <= peakMonth * 0.25 && lastMonthMixes >= peakMonth * 0.4;

  const declining = baselineAvg > 0 && recentAvg < baselineAvg * 0.6 && totalMixes >= 10;
  const growing = baselineAvg > 0 && recentAvg > baselineAvg * 1.25;

  let status: RetentionStatus;
  if (!hasHistory) {
    // No Excel mix history for this phone — classify on live recency only so
    // active customers outside the monthly reports are not mislabeled.
    if (daysInactive === null) status = "never_activated";
    else if (daysInactive <= 14) status = "healthy";
    else if (daysInactive <= 30) status = "at_risk";
    else status = "churned";
  } else if (totalMixes < RETENTION_ACTIVATED_THRESHOLD) {
    status = "never_activated";
  } else if (daysInactive === null || daysInactive > 30) {
    status = "churned";
  } else if (recoveredNow) {
    status = "recovered";
  } else if (declining) {
    status = "declining";
  } else if (daysInactive > 14) {
    status = "at_risk";
  } else if (growing) {
    status = "growing";
  } else {
    status = "healthy";
  }

  // ── Scores ──
  // Value: log-ish ramp so 200+ mixes ≈ 100, plus a small profile bonus.
  const valueScore = Math.min(
    100,
    Math.round((totalMixes / 200) * 90 + Math.min(profiles, 10) * 1),
  );

  // Risk: inactivity drives most of it, decline adds on top.
  let recencyRisk = 0;
  if (daysInactive === null) recencyRisk = 60;
  else if (daysInactive <= 7) recencyRisk = 0;
  else if (daysInactive <= 14) recencyRisk = 20;
  else if (daysInactive <= 30) recencyRisk = 45;
  else if (daysInactive <= 60) recencyRisk = 70;
  else if (daysInactive <= 180) recencyRisk = 88;
  else recencyRisk = 100;
  let declineRisk = 0;
  if (trendPct !== null) {
    if (trendPct <= -60) declineRisk = 100;
    else if (trendPct <= -40) declineRisk = 70;
    else if (trendPct <= -20) declineRisk = 40;
  }
  const riskScore = Math.round(Math.min(100, recencyRisk * 0.7 + declineRisk * 0.3));

  // Priority: who to act on first = high value that is at risk.
  const priorityScore = Math.round((valueScore * riskScore) / 100);

  // Estimated mixes no longer happening per month.
  let estimatedMixesLost = 0;
  if (status === "churned") estimatedMixesLost = Math.round(monthlyMixRate);
  else if (status === "declining") estimatedMixesLost = Math.round(Math.max(0, baselineAvg - recentAvg));

  return {
    status,
    totalMixes,
    monthsActive,
    monthlyMixRate,
    recentAvg,
    baselineAvg,
    trendPct,
    isPower,
    valueScore,
    riskScore,
    priorityScore,
    estimatedMixesLost,
  };
}

const RETENTION_CONFIG: Record<RetentionStatus, {
  label: string;
  short: string;
  tone: "positive" | "warning" | "negative" | "neutral";
  color: string;
  bg: string;
  pie: string;
}> = {
  healthy:        { label: "Healthy",          short: "Healthy",   tone: "positive", color: "text-emerald-500", bg: "bg-emerald-500/10 border-emerald-500/20", pie: "#10b981" },
  growing:        { label: "Growing",          short: "Growing",   tone: "positive", color: "text-green-500",   bg: "bg-green-500/10 border-green-500/20",     pie: "#22c55e" },
  recovered:      { label: "Recovered",        short: "Recovered", tone: "positive", color: "text-cyan-500",    bg: "bg-cyan-500/10 border-cyan-500/20",       pie: "#06b6d4" },
  at_risk:        { label: "At Risk",          short: "At Risk",   tone: "warning",  color: "text-amber-500",   bg: "bg-amber-500/10 border-amber-500/20",     pie: "#f59e0b" },
  declining:      { label: "Declining Usage",  short: "Declining", tone: "warning",  color: "text-orange-500",  bg: "bg-orange-500/10 border-orange-500/20",   pie: "#f97316" },
  churned:        { label: "Churned",          short: "Churned",   tone: "negative", color: "text-red-500",     bg: "bg-red-500/10 border-red-500/20",         pie: "#ef4444" },
  never_activated:{ label: "Never Activated",  short: "Never",     tone: "neutral",  color: "text-gray-400",    bg: "bg-gray-500/10 border-gray-500/20",       pie: "#9ca3af" },
};

// ── Predefined churn reasons (admin-assigned per churned salon) ──
type ChurnReasonId =
  | "technical"
  | "team_adoption"
  | "no_crm_integration"
  | "no_meaningful_usage"
  | "unnecessary_expense"
  | "slow_support"
  | "payment";

interface ChurnReasonDef { id: ChurnReasonId; label: string; pie: string; color: string; bg: string; }

const CHURN_REASONS: ChurnReasonDef[] = [
  { id: "technical",            label: "Technical issues / bugs",    pie: "#ef4444", color: "text-red-500",     bg: "bg-red-500/10 border-red-500/20" },
  { id: "team_adoption",        label: "Team adoption",              pie: "#f59e0b", color: "text-amber-500",   bg: "bg-amber-500/10 border-amber-500/20" },
  { id: "no_crm_integration",   label: "No CRM integration",         pie: "#f97316", color: "text-orange-500",  bg: "bg-orange-500/10 border-orange-500/20" },
  { id: "no_meaningful_usage",  label: "No meaningful usage",        pie: "#a855f7", color: "text-purple-500",  bg: "bg-purple-500/10 border-purple-500/20" },
  { id: "unnecessary_expense",  label: "Unnecessary expense",        pie: "#3b82f6", color: "text-blue-500",    bg: "bg-blue-500/10 border-blue-500/20" },
  { id: "slow_support",         label: "Support too slow",           pie: "#06b6d4", color: "text-cyan-500",    bg: "bg-cyan-500/10 border-cyan-500/20" },
  { id: "payment",              label: "Payment issues",             pie: "#ec4899", color: "text-pink-500",    bg: "bg-pink-500/10 border-pink-500/20" },
];

const CHURN_REASON_MAP: Record<string, ChurnReasonDef> =
  CHURN_REASONS.reduce((acc, r) => { acc[r.id] = r; return acc; }, {} as Record<string, ChurnReasonDef>);

const CHURN_UNASSIGNED_COLOR = "#64748b";

// ═══════════════════════════════════════════════════════════════════════
// 9. TYPES
// ═══════════════════════════════════════════════════════════════════════

type SystemCategory = "legacy" | "salonos" | "both" | "migrated" | "test";
type SystemFilter = "all" | SystemCategory;

interface SalonUser {
  id: number;
  salon_name: string;
  phone_number: string;
  profiles: number;
  first_mix_date: string;
  last_mix_date: string;
  monthly_trend: string;
  version: string;
  state: string;
  city: string;
  links: string;
  summit: string;
  instagram: string;
  churn_reason?: string;
  inferred_country?: string;
  system_category?: SystemCategory;
  legacy_roster_status?: "current" | "absent";
  salonos_slug?: string;
  salonos_timezone?: string;
  salonos_currency?: string;
  salonos_created_at?: string;
}

interface EnrichedUser extends SalonUser {
  system_category: SystemCategory;
  legacy_roster_status: "current" | "absent";
  days_inactive: number | null;
  tenure_days: number | null;
  has_mixed: boolean;
  health: HealthResult;
  lifecycle: Lifecycle;
  risk_tags: RiskTag[];
  cs_action: CSAction;
  cohort_bucket: UsageBucket;
  normalized_country: string;
  retention: RetentionMetrics;
}

function enrichUser(u: SalonUser): EnrichedUser {
  const days_inactive = parseDaysAgo(u.last_mix_date);
  const tenure_days = parseDaysAgo(u.first_mix_date);
  const has_mixed = u.first_mix_date !== "-" && u.first_mix_date !== null && u.first_mix_date !== "";
  const health = computeHealth(days_inactive, tenure_days, u.profiles);
  const lifecycle = classifyLifecycle(tenure_days, days_inactive, u.profiles, has_mixed);
  const risk_tags = computeRiskTags(has_mixed, days_inactive, tenure_days, u.profiles);
  const cs_action = recommendAction(health, lifecycle, risk_tags);
  const cohort_bucket = getCohortBucket(days_inactive);
  const normalized_country = normalizeAdminCountry(u.state) || inferCountryFromPhone(u.phone_number);
  const retention = computeRetentionMetrics({
    daysInactive: days_inactive,
    tenureDays: tenure_days,
    profiles: u.profiles,
    mix: getMixStats(u.phone_number),
  });
  return {
    ...u,
    system_category: u.system_category || "legacy",
    legacy_roster_status: u.legacy_roster_status || "current",
    days_inactive,
    tenure_days,
    has_mixed,
    health,
    lifecycle,
    risk_tags,
    cs_action,
    cohort_bucket,
    normalized_country,
    retention,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// 10. API
// ═══════════════════════════════════════════════════════════════════════

interface Stats { total_users: string; total_profiles: string; latest_version_count: string; country_count: string; city_count: string; active_users: string; zero_profile_users: string; }
interface VersionBreakdown { version: string; count: string; }
type SalonUsersPayload = { users: SalonUser[]; stats: Stats; byState: { state: string; count: string; total_profiles: string }[]; byVersion: VersionBreakdown[]; };
type AdminCurrentUserSnapshotRow = Omit<
  SalonUser,
  "id" | "summit" | "instagram" | "system_category" | "legacy_roster_status" |
  "salonos_slug" | "salonos_timezone" | "salonos_currency" | "salonos_created_at"
> & {
  summit?: string;
  instagram?: string;
};

interface AdminSalonOsDirectoryRow {
  name: string;
  slug: string;
  timezone: string;
  currency: string;
  phone: string;
  created_at: string;
  sumit_id?: number;
  is_test?: boolean;
}

const ADMIN_CURRENT_USERS_SNAPSHOT = adminCurrentUsersSnapshotRaw as AdminCurrentUserSnapshotRow[];
const ADMIN_SALONOS_DIRECTORY = adminSalonOsDirectoryRaw as AdminSalonOsDirectoryRow[];

const MIGRATED_TO_SALONOS_PHONES = new Set([
  "0537082022",
  "0502282228",
  "6199519799",
  "0506667777",
  "0504400886",
  "0525402150",
  "0548160140",
  "0544856619",
  "0547422533",
  "0546539111",
  "0546415560",
  "0542512995",
].map(normalizePhone));

const LEGACY_ABSENT_PHONE = normalizePhone("0542058588");
const SUMIT_CUSTOMER_FORM_ID = "144590236";

function summitCustomerUrl(customerId?: number): string {
  return customerId
    ? `https://app.sumit.co.il/f${SUMIT_CUSTOMER_FORM_ID}/c${customerId}/`
    : "";
}

function snapshotSyntheticId(phone: string): number {
  const digits = normalizePhone(phone);
  let hash = 0;
  for (let i = 0; i < digits.length; i += 1) {
    hash = (hash * 31 + digits.charCodeAt(i)) % 900000;
  }
  return -100000 - hash;
}

function mergeAdminCurrentUsersSnapshot(apiUsers: SalonUser[]): SalonUser[] {
  if (!ADMIN_CURRENT_USERS_SNAPSHOT.length) return apiUsers;

  const byPhone = new Map<string, SalonUser>();
  for (const user of apiUsers) {
    const phoneKey = normalizePhone(user.phone_number);
    if (phoneKey) byPhone.set(phoneKey, user);
  }

  for (const snapshot of ADMIN_CURRENT_USERS_SNAPSHOT) {
    const phoneKey = normalizePhone(snapshot.phone_number);
    if (!phoneKey) continue;
    const existing = byPhone.get(phoneKey);
    if (existing) {
      byPhone.set(phoneKey, {
        ...existing,
        ...snapshot,
        id: existing.id,
        summit: existing.summit || snapshot.summit || "",
        instagram: existing.instagram || snapshot.instagram || "",
      });
      continue;
    }
    byPhone.set(phoneKey, {
      id: snapshotSyntheticId(snapshot.phone_number),
      summit: snapshot.summit || "",
      instagram: snapshot.instagram || "",
      ...snapshot,
    });
  }

  const snapshotPhones = new Set(ADMIN_CURRENT_USERS_SNAPSHOT.map((u) => normalizePhone(u.phone_number)));
  const base = apiUsers
    .map((user) => byPhone.get(normalizePhone(user.phone_number)) || user)
    .filter((user, index, arr) => arr.findIndex((u) => normalizePhone(u.phone_number) === normalizePhone(user.phone_number)) === index);
  const appended = Array.from(byPhone.values()).filter(
    (user) => snapshotPhones.has(normalizePhone(user.phone_number)) && !apiUsers.some((apiUser) => normalizePhone(apiUser.phone_number) === normalizePhone(user.phone_number)),
  );
  return [...base, ...appended];
}

function salonOsSyntheticId(phone: string): number {
  return snapshotSyntheticId(phone) - 1000000;
}

function mergeSalonOsDirectory(legacyUsers: SalonUser[]): SalonUser[] {
  const salonOsByPhone = new Map(
    ADMIN_SALONOS_DIRECTORY.map((salon) => [normalizePhone(salon.phone), salon]),
  );
  const legacyPhones = new Set(legacyUsers.map((user) => normalizePhone(user.phone_number)));

  const mergedLegacy = legacyUsers.map((user) => {
    const phoneKey = normalizePhone(user.phone_number);
    const salonOs = salonOsByPhone.get(phoneKey);
    const isMigrated = Boolean(salonOs && MIGRATED_TO_SALONOS_PHONES.has(phoneKey));
    const system_category: SystemCategory = salonOs?.is_test
      ? "test"
      : isMigrated
        ? "migrated"
        : salonOs
          ? "both"
          : "legacy";

    return {
      ...user,
      system_category,
      legacy_roster_status: phoneKey === LEGACY_ABSENT_PHONE || isMigrated ? "absent" as const : "current" as const,
      salonos_slug: salonOs?.slug,
      salonos_timezone: salonOs?.timezone,
      salonos_currency: salonOs?.currency,
      salonos_created_at: salonOs?.created_at,
      summit: user.summit || summitCustomerUrl(salonOs?.sumit_id),
    };
  });

  const salonOsOnly = ADMIN_SALONOS_DIRECTORY
    .filter((salon) => !legacyPhones.has(normalizePhone(salon.phone)))
    .map((salon): SalonUser => ({
      id: salonOsSyntheticId(salon.phone),
      salon_name: salon.name,
      phone_number: salon.phone,
      profiles: 0,
      first_mix_date: "-",
      last_mix_date: "-",
      monthly_trend: "-",
      version: "",
      state: "",
      city: "",
      links: "",
      summit: summitCustomerUrl(salon.sumit_id),
      instagram: "",
      system_category: salon.is_test ? "test" : "salonos",
      legacy_roster_status: "absent",
      salonos_slug: salon.slug,
      salonos_timezone: salon.timezone,
      salonos_currency: salon.currency,
      salonos_created_at: salon.created_at,
    }));

  return [...mergedLegacy, ...salonOsOnly];
}

function mergeAdminDirectoryUsers(apiUsers: SalonUser[]): SalonUser[] {
  return mergeSalonOsDirectory(mergeAdminCurrentUsersSnapshot(apiUsers));
}

async function fetchSalonUsersPayload(): Promise<SalonUsersPayload> {
  const endpoints = import.meta.env.DEV
    ? ["/.netlify/functions/salon-users", "http://localhost:8888/.netlify/functions/salon-users"]
    : ["/.netlify/functions/salon-users"];
  let lastError = "Unknown API error";
  for (const ep of endpoints) {
    try {
      const res = await fetch(ep);
      if (!res.ok) { lastError = `${ep} HTTP ${res.status}`; continue; }
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/json")) { lastError = `${ep} non-JSON`; await res.text(); continue; }
      return (await res.json()) as SalonUsersPayload;
    } catch (err: any) { lastError = `${ep}: ${err?.message}`; }
  }
  throw new Error(`Unable to load data. ${lastError}. Run "npm run dev" for local functions.`);
}

// ═══════════════════════════════════════════════════════════════════════
// 11. CONSTANTS & HELPERS
// ═══════════════════════════════════════════════════════════════════════

const PIE_COLORS = ["#6366f1","#8b5cf6","#a78bfa","#c4b5fd","#818cf8","#7c3aed","#6d28d9","#5b21b6","#4f46e5","#4338ca","#3730a3","#60a5fa","#38bdf8","#22d3ee","#2dd4bf","#34d399","#4ade80","#a3e635","#facc15"];
const PAGE_SIZE = 25;

const SYSTEM_CATEGORY_CONFIG: Record<SystemCategory, { label: string; color: string }> = {
  legacy:   { label: "Legacy",   color: "bg-slate-500/10 text-slate-500 border-slate-500/20" },
  salonos:  { label: "SalonOS",  color: "bg-violet-500/10 text-violet-500 border-violet-500/20" },
  both:     { label: "Both",     color: "bg-cyan-500/10 text-cyan-500 border-cyan-500/20" },
  migrated: { label: "Migrated", color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" },
  test:     { label: "Test",     color: "bg-amber-500/10 text-amber-500 border-amber-500/20" },
};

const SYSTEM_FILTER_ORDER: SystemCategory[] = ["legacy", "salonos", "both", "migrated", "test"];

type OverviewSortField = "salon_name" | "phone_number" | "system_category" | "profiles" | "first_mix_date" | "last_mix_date" | "version" | "state" | "city";
type CSSortField = "salon_name" | "profiles" | "days_inactive" | "health_score" | "version" | "state" | "city";
type CohortSortField = "salon_name" | "system_category" | "profiles" | "days_inactive" | "state" | "city" | "first_mix_date" | "total_mixes" | "version";
type RetentionSortField = "salon_name" | "priority_score" | "total_mixes" | "days_inactive" | "trend" | "profiles" | "value_score";
type SortDir = "asc" | "desc";
type StatusFilter = "all" | "active" | "at_risk" | "critical" | "recovered" | "churned";
type RetentionFilter = "all" | RetentionStatus;
type ActiveTab = "cohorts" | "overview" | "success" | "billing" | "retention" | "import" | "catalog" | "truth" | "products" | "beauty";

// ── Admin domain model ──
type AdminDomain  = "customer" | "data";
type CustomerTab  = "cohorts" | "overview" | "success" | "billing" | "retention";
type DataTab      = "truth" | "products" | "beauty" | "imports" | "review" | "ai" | "operations" | "database" | "resolution" | "truss";

// ── Summit-only billing sub-tab types ──
type SummitSortField =
  | "name"
  | "ltv"
  | "paidMonths"
  | "averagePaidMonth"
  | "lastMonthAmount"
  | "firstPaidMonth"
  | "lastPaidMonth"
  | "churnMonths"
  | "retentionRate";
type SummitStatusFilter = "all" | "active" | "churned" | "new" | "reactivated";

// ─── Summit billing types & helpers ──────────────────────────────────────────

interface SummitCustomer {
  name: string;
  sumitId: number | null;
  monthly: number[];
  ltv: number;
  subscriptionTotal: number;
  equipmentTotal: number;
  apr26: number;
  mar26: number;
  feb26: number;
  jan26: number;
  activeMonths: number;
  paymentType: "subscription" | "equipment" | "both" | "never";
  typicalMonthly: number;
  firstPaidMonth: string | null;
  lastPaidMonth: string | null;
  isActiveSubscriber: boolean;
  stoppedSubscription: boolean;
  currentlyPaying: boolean;
  // ── Summit-only KPIs (new pivot parser) ──
  paidMonths?: number;
  refundMonths?: number;
  averagePaidMonth?: number;
  averageAllMonths?: number;
  maxMonthlyPayment?: number;
  lastMonthAmount?: number;
  churnMonths?: number;
  currentPaymentStreak?: number;
  longestPaymentStreak?: number;
  retentionRate?: number;
  isActivePayer?: boolean;
  churned?: boolean;
  reactivated?: boolean;
  newCustomer?: boolean;
  currency?: string;
  country?: string | null;
  // legacy compat
  total?: number;
  stoppedPaying?: boolean;
}

type BillingNoAutopayCategory =
  | "no_subscription"
  | "no_active_payment_method"
  | "stopped"
  | "payment_issue"
  | "annual_due"
  | "annual_covered"
  | "equipment_or_one_time"
  | "needs_mapping"
  | "distributor_covered"
  | "identity_review";

interface BillingNoAutopayRow {
  key: string;
  user: EnrichedUser | null;
  customer: SummitCustomer | null;
  category: BillingNoAutopayCategory;
  detail: string;
}

const SUMMIT_DATA = summitRaw as {
  months: string[];
  _source: string;
  currency?: string;
  latestMonth?: string | null;
  monthCount?: number;
  summary: {
    total: number;
    activeSubscribers: number;
    stoppedSubscription: number;
    equipmentOnly: number;
    subscriptionAndEquip: number;
    neverPaid: number;
    totalLTV: number;
    // Summit-only KPIs (new pivot parser)
    paidCustomers?: number;
    totalCustomers?: number;
    averageLTV?: number;
    averagePaidMonth?: number;
    averagePaidMonths?: number;
    activePayers?: number;
    churnedCustomers?: number;
    newCustomers?: number;
    reactivatedCustomers?: number;
    lastWindowRevenue?: number;
    prevWindowRevenue?: number;
    revenueRetention?: number;
    customerRetention?: number;
    // legacy compat (old april-only file)
    currentlyPaying?: number;
  };
  customers: SummitCustomer[];
};

const SUMMIT_DISTRIBUTORS = [
  {
    name: "April Morning Lda",
    country: "Portugal",
    annualLicenses: 50,
  },
] as const;

const PORTUGAL_DISTRIBUTOR_APP_NAMES = new Set([
  "ARTZ",
  "ARTZ 2",
  "Artz Estoril",
  "Sete Concept",
  "WE CUT SALON",
]);

interface SummitCustomerAlias {
  appName: string;
  phone: string;
  sumitId: number;
  summitName: string;
  confidence: "confirmed" | "high";
  source: string;
  note: string;
  searchTerms?: string[];
}

interface SummitManualOverride {
  sumitId: number | null;
  name: string;
  status?: "active" | "churned";
  customerType?: "internal" | "brand" | "distributor" | "salon";
  reason?: string;
}

interface SummitBillingReconciliationCase {
  sumitId: number;
  appName: string;
  summitName: string;
  status: "payment_method_update_required" | "annual_subscription" | "annual_renewal_due" | "subscription_never_paid" | "no_active_payment_method" | "active_payment_method_confirmed";
  confirmed: boolean;
  unpaidFromMonth?: string;
  recurringBillingUrl?: string;
  annualPaidMonth?: string;
  paidThroughMonth?: string;
  renewalDueMonth?: string;
  renewalAmountIls?: number;
  note: string;
  source: string;
}

const SUMMIT_CUSTOMER_ALIASES = summitCustomerAliasesRaw as SummitCustomerAlias[];
const SUMMIT_BILLING_RECONCILIATION = (
  summitBillingReconciliationRaw as { cases: SummitBillingReconciliationCase[] }
).cases;
const SUMMIT_STANDING_ORDERS = new Map(
  (summitStandingOrdersRaw as {
    orders: Array<{ sumitId: number; active: boolean }>;
  }).orders.map((order) => [order.sumitId, order]),
);
const SUMMIT_STATUS_OVERRIDES = (
  summitManualOverridesRaw as { overrides: SummitManualOverride[] }
).overrides
  .filter((override) => override.status === "active" || override.status === "churned")
  .map((override) => ({
    sumitId: override.sumitId,
    name: override.name,
    active: override.status === "active",
    note: override.reason || "Manual billing status override",
  }));
const SUMMIT_INTERNAL_CUSTOMERS = (
  summitManualOverridesRaw as { overrides: SummitManualOverride[] }
).overrides.filter((override) => override.customerType === "internal");

function getSummitDistributor(name: string) {
  const normalized = normalizeSummitName(name);
  return SUMMIT_DISTRIBUTORS.find(
    (distributor) => normalizeSummitName(distributor.name) === normalized,
  );
}

function getSummitStatusOverride(customer: SummitCustomer) {
  const normalized = normalizeSummitName(customer.name);
  return SUMMIT_STATUS_OVERRIDES.find(
    (override) =>
      (customer.sumitId != null && override.sumitId === customer.sumitId) ||
      normalizeSummitName(override.name) === normalized,
  );
}

function isSummitInternalCustomer(customer: SummitCustomer): boolean {
  const normalized = normalizeSummitName(customer.name);
  return SUMMIT_INTERNAL_CUSTOMERS.some(
    (override) =>
      (customer.sumitId != null && override.sumitId === customer.sumitId) ||
      normalizeSummitName(override.name) === normalized,
  );
}

function getSummitBillingReconciliation(customer: SummitCustomer) {
  return SUMMIT_BILLING_RECONCILIATION.find(
    (reconciliation) => reconciliation.sumitId === customer.sumitId,
  );
}

function isSummitActivePayer(customer: SummitCustomer): boolean {
  const annualSubscription = getSummitBillingReconciliation(customer);
  if (
    annualSubscription?.confirmed &&
    (annualSubscription.status === "subscription_never_paid" ||
      annualSubscription.status === "annual_renewal_due")
  ) {
    return false;
  }
  if (
    annualSubscription?.confirmed &&
    annualSubscription.status === "annual_subscription" &&
    annualSubscription?.paidThroughMonth &&
    monthLabelKey(SUMMIT_DATA.latestMonth) <= monthLabelKey(annualSubscription.paidThroughMonth)
  ) {
    return true;
  }
  const override = getSummitStatusOverride(customer);
  return override?.active ?? !!customer.isActivePayer;
}

function isSummitChurned(customer: SummitCustomer): boolean {
  if (getSummitStatusOverride(customer)?.active) return false;
  return !!customer.churned;
}

function hasVerifiedActiveMonthlyCharge(customer: SummitCustomer): boolean {
  const reconciliation = getSummitBillingReconciliation(customer);
  if (reconciliation?.status === "active_payment_method_confirmed") return true;
  if (
    reconciliation?.status === "annual_subscription" ||
    reconciliation?.status === "annual_renewal_due" ||
    reconciliation?.status === "subscription_never_paid" ||
    reconciliation?.status === "no_active_payment_method"
  ) {
    return false;
  }
  const standingOrder = customer.sumitId ? SUMMIT_STANDING_ORDERS.get(customer.sumitId) : undefined;
  const latestAmount = customer.monthly[SUMMIT_DATA.months.length - 1] ?? 0;
  if (standingOrder?.active && latestAmount > 0) return true;
  if (customer.paymentType === "equipment") return false;
  if ((customer.currentPaymentStreak || 0) >= 2) return true;

  const latestIndex = SUMMIT_DATA.months.length - 1;
  const lastPaidIndex = customer.lastPaidMonth
    ? SUMMIT_DATA.months.indexOf(customer.lastPaidMonth)
    : -1;
  return (
    lastPaidIndex >= latestIndex - 1 &&
    (customer.paidMonths || 0) >= 3 &&
    (customer.subscriptionTotal || 0) > 0
  );
}

function normalizeSummitName(s: string): string {
  return s
    .toLowerCase()
    .replace(/[\u0591-\u05C7]/g, "") // remove niqqud
    .replace(/['''".,()\-\s]/g, "")
    .trim();
}

function monthLabelKey(label: string | null | undefined): number {
  if (!label) return 0;
  const match = /^(\d{2})\/(\d{4})$/.exec(label);
  return match ? Number(match[2]) * 100 + Number(match[1]) : 0;
}

function normalizeAdminSearch(value: string | number | null | undefined): string {
  return String(value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function summitNameWords(s: string): string[] {
  const genericWords = new Set([
    "salon", "hair", "studio", "beauty", "bar", "boutique", "color", "styles", "the", "and",
    "עיצוב", "שיער", "סלון", "סטודיו", "ביוטי",
  ]);
  const words = s
    .toLowerCase()
    .replace(/[\u0591-\u05C7]/g, "")
    .replace(/['''".,()\-_/\\]/g, " ")
    .match(/[\u0590-\u05FF\w]{3,}/g) || [];
  return words.filter((word) => !genericWords.has(word));
}

function matchSummitToSalon(
  sc: SummitCustomer,
  users: EnrichedUser[]
): EnrichedUser | null {
  // 0. Confirmed owner/legal-name ↔ salon-name aliases.
  const alias = sc.sumitId
    ? SUMMIT_CUSTOMER_ALIASES.find((candidate) => candidate.sumitId === sc.sumitId)
    : undefined;
  if (alias) {
    const aliasPhone = normalizePhone(alias.phone);
    const aliasMatch = users.find(
      (user) =>
        normalizePhone(user.phone_number) === aliasPhone ||
        normalizeSummitName(user.salon_name) === normalizeSummitName(alias.appName),
    );
    if (aliasMatch) return aliasMatch;
  }

  // 0. Stable SUMIT customer id embedded in the customer link.
  if (sc.sumitId) {
    const idMatch = users.find((user) => {
      const linkedId = String(user.summit || "").match(/\/c(\d+)\/?/)?.[1];
      return linkedId === String(sc.sumitId);
    });
    if (idMatch) return idMatch;
  }
  // Never override a stable Summit link with a fuzzy name match to a different
  // customer account (for example, "Andrea" must not match "Andrea Reyes").
  const nameMatchUsers = users.filter((user) => {
    const linkedId = String(user.summit || "").match(/\/c(\d+)\/?/)?.[1];
    return !linkedId || linkedId === String(sc.sumitId);
  });
  const scn = normalizeSummitName(sc.name);
  const scWords = summitNameWords(sc.name);
  // 1. exact normalized match
  let match = nameMatchUsers.find((u) => normalizeSummitName(u.salon_name) === scn);
  if (match) return match;
  // 2. one contains the other (at least 5 chars and two meaningful words).
  // A single first name is not enough evidence to connect two customer records.
  match = nameMatchUsers.find((u) => {
    const un = normalizeSummitName(u.salon_name);
    const unWords = summitNameWords(u.salon_name);
    return (
      scWords.length >= 2 &&
      unWords.length >= 2 &&
      un.length >= 5 &&
      scn.length >= 5 &&
      (scn.includes(un) || un.includes(scn))
    );
  });
  if (match) return match;
  // 3. significant word overlap (>=2 shared tokens of length >=3)
  match = nameMatchUsers.find((u) => {
    const unWords = summitNameWords(u.salon_name);
    const shared = scWords.filter((w) => unWords.includes(w));
    return shared.length >= 2;
  });
  return match || null;
}

function getFlag(country: string): string {
  const f: Record<string, string> = {
    ISRAEL:"\u{1F1EE}\u{1F1F1}", Israel:"\u{1F1EE}\u{1F1F1}",
    USA:"\u{1F1FA}\u{1F1F8}",
    UK:"\u{1F1EC}\u{1F1E7}", ENGLAND:"\u{1F3F4}\u{E0067}\u{E0062}\u{E0065}\u{E006E}\u{E0067}\u{E007F}",
    PORTUGAL:"\u{1F1F5}\u{1F1F9}", Portugal:"\u{1F1F5}\u{1F1F9}",
    ITALY:"\u{1F1EE}\u{1F1F9}", Italy:"\u{1F1EE}\u{1F1F9}",
    JAPAN:"\u{1F1EF}\u{1F1F5}", Japan:"\u{1F1EF}\u{1F1F5}",
    CANADA:"\u{1F1E8}\u{1F1E6}", Canada:"\u{1F1E8}\u{1F1E6}",
    BELARUS:"\u{1F1E7}\u{1F1FE}", Belarus:"\u{1F1E7}\u{1F1FE}",
    NETHERLANDS:"\u{1F1F3}\u{1F1F1}", Netherlands:"\u{1F1F3}\u{1F1F1}",
    AUSTRALIA:"\u{1F1E6}\u{1F1FA}", Australia:"\u{1F1E6}\u{1F1FA}",
    GREECE:"\u{1F1EC}\u{1F1F7}", Greece:"\u{1F1EC}\u{1F1F7}",
    IRELAND:"\u{1F1EE}\u{1F1EA}", IRLAND:"\u{1F1EE}\u{1F1EA}", Ireland:"\u{1F1EE}\u{1F1EA}",
    RUSSIA:"\u{1F1F7}\u{1F1FA}", Russia:"\u{1F1F7}\u{1F1FA}",
  };
  return f[country] || f[country?.toUpperCase()] || "\u{1F310}";
}

// ═══════════════════════════════════════════════════════════════════════
// 12. SHARED SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════

interface ThemeProps { isDark: boolean }

function CountryPieChart({ data, countryFilter, setCountryFilter, isDark }: { data: { name: string; value: number }[]; countryFilter: string; setCountryFilter: (v: string) => void } & ThemeProps) {
  const tooltipStyle = {
    background: isDark ? "#1a1a2e" : "#ffffff",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
    borderRadius: "12px", color: isDark ? "#fff" : "#1A1A1A", fontSize: "13px",
  };
  return (
    <div className={`rounded-2xl border p-5 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-black/[0.08] shadow-sm"}`}>
      <h3 className={`text-sm font-medium mb-4 flex items-center gap-2 ${isDark ? "text-white/70" : "text-[#555555]"}`}><Globe className="w-4 h-4 text-indigo-400" />Country Distribution</h3>
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data.slice(0, 12)} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" stroke="none">
              {data.slice(0, 12).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} formatter={(v: number, n: string) => [`${v} users`, n]} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-2">
        {data.slice(0, 9).map((item, i) => (
          <button key={item.name} onClick={() => setCountryFilter(countryFilter === item.name ? "all" : item.name)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs transition ${
              countryFilter === item.name
                ? "bg-indigo-500/20 text-indigo-500 border border-indigo-500/30"
                : isDark ? "text-white/50 hover:text-white/70 hover:bg-white/5" : "text-[#888] hover:text-[#555] hover:bg-black/[0.04]"
            }`}>
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
            <span className="truncate">{getFlag(item.name)} {item.name}</span>
            <span className={`ml-auto ${isDark ? "text-white/50" : "text-[#999]"}`}>{item.value}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function VersionBarChart({ data, allVersions, versionFilter, setVersionFilter, isDark }: { data: VersionBreakdown[]; allVersions: string[]; versionFilter: string; setVersionFilter: (v: string) => void } & ThemeProps) {
  const tooltipStyle = {
    background: isDark ? "#1a1a2e" : "#ffffff",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
    borderRadius: "12px", color: isDark ? "#fff" : "#1A1A1A", fontSize: "13px",
  };
  const tickColor = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)";
  const gridColor = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.06)";
  return (
    <div className={`rounded-2xl border p-5 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-black/[0.08] shadow-sm"}`}>
      <h3 className={`text-sm font-medium mb-4 flex items-center gap-2 ${isDark ? "text-white/70" : "text-[#555555]"}`}><BarChart3 className="w-4 h-4 text-violet-400" />Version Distribution</h3>
      <div className="h-[250px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.map(v => ({ version: `v${v.version}`, count: Number(v.count) }))} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis type="number" tick={{ fill: tickColor, fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="version" tick={{ fill: tickColor, fontSize: 11 }} axisLine={false} tickLine={false} width={55} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="count" fill="#8b5cf6" radius={[0, 6, 6, 0]} barSize={18} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {allVersions.slice(0, 8).map(v => (
          <button key={v} onClick={() => setVersionFilter(versionFilter === v ? "all" : v)}
            className={`px-2.5 py-1 rounded-full text-xs transition ${
              versionFilter === v
                ? "bg-violet-500/20 text-violet-600 border border-violet-500/30"
                : isDark ? "bg-white/5 text-white/55 hover:text-white/60 hover:bg-white/10" : "bg-black/[0.04] text-[#888] hover:text-[#555] hover:bg-black/[0.07]"
            }`}>
            v{v}
          </button>
        ))}
      </div>
    </div>
  );
}

function Pagination({ page, totalPages, setPage, isDark }: { page: number; totalPages: number; setPage: (fn: (p: number) => number) => void } & ThemeProps) {
  if (totalPages <= 1) return null;
  return (
    <div className={`border-t px-4 py-3 flex items-center justify-between ${isDark ? "border-white/[0.06]" : "border-black/[0.06]"}`}>
      <p className={`text-xs ${isDark ? "text-white/50" : "text-[#999]"}`}>Page {page} of {totalPages}</p>
      <div className="flex items-center gap-1">
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
          className={`p-1.5 rounded-lg disabled:opacity-20 disabled:cursor-not-allowed transition ${isDark ? "text-white/55 hover:text-white hover:bg-white/10" : "text-[#888] hover:text-[#333] hover:bg-black/[0.06]"}`}><ChevronLeft className="w-4 h-4" /></button>
        {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
          const pn = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
          return <button key={pn} onClick={() => setPage(() => pn)}
            className={`w-8 h-8 rounded-lg text-xs font-medium transition ${page === pn ? "bg-indigo-500 text-white" : isDark ? "text-white/55 hover:text-white hover:bg-white/10" : "text-[#888] hover:text-[#333] hover:bg-black/[0.06]"}`}>{pn}</button>;
        })}
        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
          className={`p-1.5 rounded-lg disabled:opacity-20 disabled:cursor-not-allowed transition ${isDark ? "text-white/55 hover:text-white hover:bg-white/10" : "text-[#888] hover:text-[#333] hover:bg-black/[0.06]"}`}><ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 13. MAIN COMPONENT (inner, needs theme)
// ═══════════════════════════════════════════════════════════════════════

const AdminDashboardInner: React.FC = () => {
  const { isDark, toggleTheme } = useSiteTheme();

  // ── Admin theme tokens ──
  const at = {
    page:         isDark ? "bg-[#0a0a0f] text-white"                 : "bg-[#FAFAF8] text-[#1A1A1A]",
    sticky:       isDark ? "bg-[#0a0a0f]/80"                         : "bg-[#FAFAF8]/90",
    card:         isDark ? "bg-white/[0.03] border-white/[0.06]"     : "bg-white border-black/[0.08] shadow-sm",
    cardHover:    isDark ? "hover:bg-white/[0.05]"                    : "hover:bg-gray-50",
    subCard:      isDark ? "bg-white/[0.02] border-white/[0.06]"     : "bg-white/60 border-black/[0.06]",
    stickyBorder: isDark ? "border-white/5"                           : "border-black/[0.06]",
    textPrimary:  isDark ? "text-white"                               : "text-[#1A1A1A]",
    text90:       isDark ? "text-white/90"                            : "text-[#1A1A1A]",
    textSec:      isDark ? "text-white/70"                            : "text-[#555555]",
    textMuted:    isDark ? "text-white/55"                            : "text-[#777777]",
    textFaint:    isDark ? "text-white/50"                            : "text-[#999999]",
    textDim:      isDark ? "text-white/15"                            : "text-[#CCCCCC]",
    border:       isDark ? "border-white/[0.06]"                      : "border-black/[0.08]",
    borderMed:    isDark ? "border-white/[0.08]"                      : "border-black/[0.10]",
    input:        isDark
      ? "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/50 focus:border-indigo-500/50 focus:ring-indigo-500/20"
      : "bg-white border-black/[0.12] text-[#1A1A1A] placeholder:text-[#AAAAAA] focus:border-indigo-400 focus:ring-indigo-400/20",
    select:       isDark ? "bg-white/[0.06] border-white/[0.1] text-white"          : "bg-white border-black/[0.10] text-[#1A1A1A]",
    selectBg:     isDark ? "#1a1a2e"                                  : "#ffffff",
    tabWrap:      isDark ? "bg-white/[0.04] border-white/[0.06]"     : "bg-black/[0.04] border-black/[0.06]",
    tabActive:    isDark ? "bg-white/[0.1] text-white shadow-sm"      : "bg-white text-[#1A1A1A] shadow-sm",
    tabInactive:  isDark ? "text-white/55 hover:text-white/70"         : "text-[#888888] hover:text-[#555555]",
    rowDivide:    isDark ? "divide-white/[0.03]"                      : "divide-black/[0.04]",
    rowHover:     isDark ? "hover:bg-white/[0.03]"                    : "hover:bg-black/[0.02]",
    filterPanel:  isDark ? "bg-white/[0.02] border-white/[0.06]"     : "bg-black/[0.02] border-black/[0.05]",
    tagActive:    isDark ? "bg-indigo-500/15 border-indigo-500/30 text-indigo-300" : "bg-indigo-50 border-indigo-200 text-indigo-700",
    tagInactive:  isDark ? "bg-white/[0.03] border-white/[0.06] text-white/55 hover:text-white/70 hover:bg-white/[0.05]" : "bg-black/[0.03] border-black/[0.06] text-[#888] hover:text-[#555] hover:bg-black/[0.05]",
    filterActive: isDark ? "bg-indigo-500/10 border-indigo-500/30 text-indigo-300" : "bg-indigo-50 border-indigo-200 text-indigo-700",
    filterInactive:isDark ? "bg-white/[0.04] border-white/[0.08] text-white/50 hover:text-white/70" : "bg-white border-black/[0.08] text-[#888] hover:text-[#555]",
    expandRow:    isDark ? "bg-white/[0.02]"                          : "bg-black/[0.02]",
    toggleBtn:    isDark ? "bg-white/[0.06] hover:bg-white/[0.12] text-white/55 hover:text-white/70" : "bg-black/[0.04] hover:bg-black/[0.08] text-black/55 hover:text-black/70",
    spinner:      isDark ? "border-indigo-500/30 border-t-indigo-500" : "border-indigo-400/40 border-t-indigo-500",
    freshnessBg:  isDark ? "bg-white/[0.02] border-white/[0.06]"     : "bg-[#EAB776]/8 border-[#EAB776]/20",
    freshnessDot: "bg-green-400",
  };

  // ── Domain navigation ──
  const [searchParams] = useSearchParams();
  const initialDomain = (searchParams.get("domain") === "data" ? "data" : "customer") as AdminDomain;
  const initialDataTab = (searchParams.get("tab") as DataTab | null);
  const [adminDomain, setAdminDomain]           = useState<AdminDomain>(initialDomain);
  const [activeCustomerTab, setActiveCustomerTab] = useState<CustomerTab>("cohorts");
  const [activeDataTab, setActiveDataTab]         = useState<DataTab>(
    initialDomain === "data" && initialDataTab ? initialDataTab : "truth",
  );

  useEffect(() => {
    const domain = searchParams.get("domain");
    const tab = searchParams.get("tab") as DataTab | null;
    const allowed: DataTab[] = [
      "truth", "products", "beauty", "imports", "review", "ai",
      "operations", "database", "resolution", "truss",
    ];
    if (domain === "data") {
      setAdminDomain("data");
      if (tab && allowed.includes(tab)) setActiveDataTab(tab);
    }
  }, [searchParams]);

  // Derived backwards-compat activeTab for existing panel renders
  const activeTab: ActiveTab = adminDomain === "customer"
    ? activeCustomerTab
    : (activeDataTab === "imports"   ? "import"
     : activeDataTab === "operations" ? "catalog"
     : activeDataTab === "review"     ? "truth"
     : activeDataTab === "ai"         ? "truth"
     : activeDataTab) as ActiveTab;

  const setDomainTab = (domain: AdminDomain, tab: CustomerTab | DataTab) => {
    setAdminDomain(domain);
    if (domain === "customer") setActiveCustomerTab(tab as CustomerTab);
    else setActiveDataTab(tab as DataTab);
  };

  // ── Data ──
  const [rawUsers, setRawUsers] = useState<SalonUser[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [byVersion, setByVersion] = useState<VersionBreakdown[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);

  // Subscribe to live phone-mix updates. As soon as the admin commits
  // a fresh import the in-memory MIX_INDEX is swapped and every cohort
  // / usage panel automatically re-renders with the new data.
  const mixIndexVersion = useMixIndexVersion();
  useEffect(() => {
    void refreshGlobalMixIndex();
  }, []);

  // ── Shared filters ──
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState<string>("all");
  const [versionFilter, setVersionFilter] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);

  // ── Overview-specific ──
  const [systemFilter, setSystemFilter] = useState<SystemFilter>("all");
  const [profileFilter, setProfileFilter] = useState<string>("all");
  const [ovSortField, setOvSortField] = useState<OverviewSortField>("profiles");
  const [ovSortDir, setOvSortDir] = useState<SortDir>("desc");
  const [ovPage, setOvPage] = useState(1);

  // ── CS-specific ──
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [lifecycleFilter, setLifecycleFilter] = useState<string>("all");
  const [quickToggle, setQuickToggle] = useState<"" | "at_risk" | "new" | "high_potential">("");
  const [csSortField, setCsSortField] = useState<CSSortField>("health_score");
  const [csSortDir, setCsSortDir] = useState<SortDir>("asc");
  const [csPage, setCsPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // ── Cohort-specific ──
  const [cohortBucket, setCohortBucket] = useState<UsageBucket | "all">("all");

  // ── Table-wide edits (Summit / Instagram) ──
  const [pendingEdits, setPendingEdits] = useState<Record<number, { summit?: string; instagram?: string }>>({});
  const [isTableEditing, setIsTableEditing] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [tableSavedAt, setTableSavedAt] = useState<number | null>(null);

  function handleEditChange(id: number, field: "summit" | "instagram", value: string) {
    setPendingEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  }

  function hasEdits(user: EnrichedUser) {
    const e = pendingEdits[user.id];
    if (!e) return false;
    return (e.summit !== undefined && e.summit !== (user.summit ?? "")) ||
           (e.instagram !== undefined && e.instagram !== (user.instagram ?? ""));
  }

  function startEditingTable() {
    setIsTableEditing(true);
  }

  function cancelTableEdits() {
    setPendingEdits({});
    setIsTableEditing(false);
  }

  async function patchSalonUser(id: number, summit: string, instagram: string) {
    try {
      const endpoints = import.meta.env.DEV
        ? ["/.netlify/functions/update-salon-user", "http://localhost:8888/.netlify/functions/update-salon-user"]
        : ["/.netlify/functions/update-salon-user"];

      for (const ep of endpoints) {
        try {
          const res = await fetch(ep, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id, summit, instagram }),
          });
          if (res.ok) return true;
        } catch { /* try next */ }
      }
      return false;
    } catch {
      return false;
    }
  }

  async function publishTableEdits() {
    const changedUsers = users.filter(hasEdits);
    if (changedUsers.length === 0) {
      setIsTableEditing(false);
      return;
    }

    setIsPublishing(true);
    const updates: Record<number, { summit: string; instagram: string }> = {};

    try {
      for (const user of changedUsers) {
        const edit = pendingEdits[user.id];
        if (!edit) continue;
        const summit = edit.summit ?? user.summit ?? "";
        const instagram = edit.instagram ?? user.instagram ?? "";
        const ok = await patchSalonUser(user.id, summit, instagram);
        if (ok) updates[user.id] = { summit, instagram };
      }

      if (Object.keys(updates).length > 0) {
        setRawUsers(prev => prev.map(u => updates[u.id] ? { ...u, ...updates[u.id] } : u));
      }

      setPendingEdits({});
      setIsTableEditing(false);
      setTableSavedAt(Date.now());
      setTimeout(() => setTableSavedAt(null), 2500);
    } finally {
      setIsPublishing(false);
    }
  }

  // ── Churn reason: optimistic local update + persist only that field ──
  async function setChurnReason(id: number, churn_reason: string) {
    // Update local state immediately so charts/table refresh in real time.
    setRawUsers(prev => prev.map(u => u.id === id ? { ...u, churn_reason } : u));
    setSavingChurnId(id);

    const endpoints = import.meta.env.DEV
      ? ["/.netlify/functions/update-salon-user", "http://localhost:8888/.netlify/functions/update-salon-user"]
      : ["/.netlify/functions/update-salon-user"];

    let ok = false;
    for (const ep of endpoints) {
      try {
        const res = await fetch(ep, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, churn_reason }),
        });
        if (res.ok) { ok = true; break; }
      } catch { /* try next */ }
    }

    setSavingChurnId(null);
    if (!ok) {
      // Synthetic snapshot rows (negative ids) have no DB backing — keep local
      // value but surface a soft failure for real rows only.
      if (id >= 0) setChurnSaveError(id);
      setTimeout(() => setChurnSaveError(null), 2500);
    }
  }

  function getExternalHref(value: string, type: "summit" | "instagram"): string | null {
    const trimmed = (value || "").trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (type === "instagram") {
      const handle = trimmed.replace(/^@+/, "");
      return handle ? `https://instagram.com/${handle}` : null;
    }
    return `https://${trimmed}`;
  }

  function handleTableWheel(e: React.WheelEvent<HTMLDivElement>) {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault();
      window.scrollBy({ top: e.deltaY, left: 0, behavior: "auto" });
    }
  }
  // null = natural order (no forced sort) so the table can be cleared back
  // to the data's original order.
  const [cohortSortField, setCohortSortField] = useState<CohortSortField | null>(null);
  const [cohortSortDir, setCohortSortDir] = useState<SortDir>("asc");
  const [cohortPage, setCohortPage] = useState(1);

  // ── Retention-specific ──
  const [retentionFilter, setRetentionFilter] = useState<RetentionFilter>("all");
  const [retentionSortField, setRetentionSortField] = useState<RetentionSortField>("priority_score");
  const [retentionSortDir, setRetentionSortDir] = useState<SortDir>("desc");
  const [retentionPage, setRetentionPage] = useState(1);
  // Internal sub-view of the Retention tab: status overview vs churn reasons.
  const [retentionView, setRetentionView] = useState<"overview" | "churn_reasons">("overview");
  const [churnReasonFilter, setChurnReasonFilter] = useState<string>("all");
  const [churnPage, setChurnPage] = useState(1);
  const [savingChurnId, setSavingChurnId] = useState<number | null>(null);
  const [churnSaveError, setChurnSaveError] = useState<number | null>(null);

  // ── Billing tab state (must be at component top level, not inside render) ──
  const [billingView, setBillingView] = useState<
    "summit_customers" | "without_autopay" | "paying_not_using" | "using_not_paying" | "needs_mapping" | "equipment" | "stopped"
  >("summit_customers");
  const [billingNoAutopaySearch, setBillingNoAutopaySearch] = useState("");

  // ── Summit-only sub-tab state (Summit data, no Spectra cross-reference) ──
  const [summitSearch, setSummitSearch]         = useState("");
  const [summitStatus, setSummitStatus]         = useState<SummitStatusFilter>("all");
  const [summitMinLtv, setSummitMinLtv]         = useState<number>(0);
  const [summitSortField, setSummitSortField]   = useState<SummitSortField>("ltv");
  const [summitSortDir, setSummitSortDir]       = useState<SortDir>("desc");
  const [summitPage, setSummitPage]             = useState(1);
  const [summitPageSize, setSummitPageSize]     = useState<number>(50);
  const SUMMIT_CURRENCY_SYMBOL                  = SUMMIT_DATA.currency === "USD" ? "$" : "₪";

  // ── Per-customer KPI exclusions (persisted in localStorage) ──
  const SUMMIT_EXCLUSIONS_KEY = "spectra:summit:excludedCustomers";
  const [summitExcluded, setSummitExcluded] = useState<Set<string>>(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = window.localStorage.getItem(SUMMIT_EXCLUSIONS_KEY);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? new Set(arr.filter((s) => typeof s === "string")) : new Set();
    } catch { return new Set(); }
  });

  const toggleSummitExcluded = useCallback((name: string) => {
    setSummitExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      try {
        window.localStorage.setItem(SUMMIT_EXCLUSIONS_KEY, JSON.stringify(Array.from(next)));
      } catch { /* localStorage unavailable */ }
      return next;
    });
  }, []);

  const clearSummitExclusions = useCallback(() => {
    setSummitExcluded(new Set());
    try { window.localStorage.removeItem(SUMMIT_EXCLUSIONS_KEY); } catch { /* noop */ }
  }, []);

  function handleSummitSort(field: SummitSortField) {
    if (summitSortField === field) {
      setSummitSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSummitSortField(field);
      // First click on any column always sorts descending (large → small,
      // Z → A, newest → oldest). Subsequent clicks toggle to ascending.
      setSummitSortDir("desc");
    }
    setSummitPage(1);
  }

  // Parse "MM/YYYY" into a sortable number (YYYYMM)
  function summitMonthKey(label: string | null | undefined): number {
    if (!label) return 0;
    const m = /^(\d{2})\/(\d{4})$/.exec(label);
    if (!m) return 0;
    return Number(m[2]) * 100 + Number(m[1]);
  }

  // ── Fetch ──
  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await fetchSalonUsersPayload();
      const mergedUsers = mergeAdminDirectoryUsers(data.users || []);
      const enhanced = mergedUsers.map((u: SalonUser) => ({
        ...u, inferred_country: u.state || inferCountryFromPhone(u.phone_number),
      }));
      setRawUsers(enhanced);
      setStats(data.stats);
      setByVersion(data.byVersion || []);
      setLoadedAt(new Date());
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Enriched users ──
  // mixIndexVersion is a dep so retention/Total Mixes recompute when the
  // live DB-backed phone-mix index swaps in after an import.
  const allUsers = useMemo(() => rawUsers.map(enrichUser), [rawUsers, mixIndexVersion]);
  // SalonOS-only rows have no legacy usage feed. Keep them in the directory
  // while excluding them (and explicit test accounts) from legacy usage KPIs.
  const users = useMemo(
    () => allUsers.filter((user) => user.system_category !== "salonos" && user.system_category !== "test"),
    [allUsers],
  );
  const directoryCustomers = useMemo(
    () => allUsers.filter((user) => user.system_category !== "test"),
    [allUsers],
  );

  // ── Derived ──
  const allCountries = useMemo(() => {
    const s = new Set<string>();
    directoryCustomers.forEach(u => { if (u.normalized_country) s.add(u.normalized_country); });
    return Array.from(s).sort();
  }, [directoryCustomers]);

  const allVersions = useMemo(() => {
    const s = new Set<string>();
    users.forEach(u => { if (u.version) s.add(u.version); });
    return Array.from(s).sort((a, b) => b.localeCompare(a));
  }, [users]);

  const countryChartData = useMemo(() => {
    const m = new Map<string, number>();
    users.forEach(u => {
      const c = u.normalized_country || "Unknown";
      m.set(c, (m.get(c) || 0) + 1);
    });
    return Array.from(m.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [users]);

  const overviewCountryChartData = useMemo(() => {
    const m = new Map<string, number>();
    directoryCustomers.forEach((user) => {
      const country = user.normalized_country || "Unknown";
      m.set(country, (m.get(country) || 0) + 1);
    });
    return Array.from(m.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [directoryCustomers]);

  const systemCounts = useMemo(() => {
    const counts: Record<SystemCategory, number> = {
      legacy: 0,
      salonos: 0,
      both: 0,
      migrated: 0,
      test: 0,
    };
    allUsers.forEach((user) => { counts[user.system_category] += 1; });
    return counts;
  }, [allUsers]);

  // ── Summit billing cross-reference (must be useMemo, not inside render) ──
  const summitWithMatch = useMemo(() =>
    SUMMIT_DATA.customers.map((sc) => ({
      sc,
      user: matchSummitToSalon(sc, allUsers),
    })), [allUsers]);

  const summitMatchesByPhone = useMemo(() => {
    const matches = new Map<string, typeof summitWithMatch>();
    summitWithMatch.forEach((entry) => {
      if (!entry.user) return;
      const phone = normalizePhone(entry.user.phone_number);
      matches.set(phone, [...(matches.get(phone) || []), entry]);
    });
    return matches;
  }, [summitWithMatch]);

  const userMatchesSearch = useCallback((user: EnrichedUser, query: string): boolean => {
    const q = normalizeAdminSearch(query);
    if (!q) return true;
    const phoneKey = normalizePhone(user.phone_number);
    const summitEntries = summitMatchesByPhone.get(phoneKey) || [];
    const aliases = SUMMIT_CUSTOMER_ALIASES.filter(
      (alias) =>
        normalizePhone(alias.phone) === phoneKey ||
        normalizeSummitName(alias.appName) === normalizeSummitName(user.salon_name),
    );
    const searchable = [
      user.salon_name,
      user.phone_number,
      user.city,
      user.state,
      user.summit,
      user.instagram,
      user.salonos_slug,
      ...summitEntries.flatMap(({ sc }) => [sc.name, sc.sumitId]),
      ...aliases.flatMap((alias) => [
        alias.appName,
        alias.summitName,
        alias.note,
        ...(alias.searchTerms || []),
      ]),
    ];
    return normalizeAdminSearch(searchable.join(" ")).includes(q);
  }, [summitMatchesByPhone]);

  const billingPayingNotUsing = useMemo(() =>
    summitWithMatch
      .filter(({ sc, user }) => {
        const latestPayment = sc.monthly[SUMMIT_DATA.months.length - 1] ?? sc.lastMonthAmount ?? 0;
        if (latestPayment <= 0) return false;
        if (!user) return false;
        if (user.system_category === "salonos" || user.system_category === "test") return false;
        return user.days_inactive === null || user.days_inactive > 30;
      })
      .sort((a, b) => (b.sc.lastMonthAmount || 0) - (a.sc.lastMonthAmount || 0)),
    [summitWithMatch]);

  const billingUsingNotPaying = useMemo(() =>
    users
      .filter((u) => {
        if (!u.has_mixed) return false;
        if (u.days_inactive === null || u.days_inactive > 60) return false;
        if (PORTUGAL_DISTRIBUTOR_APP_NAMES.has(u.salon_name)) return false;
        const entries = summitMatchesByPhone.get(normalizePhone(u.phone_number)) || [];
        if (entries.length === 0) return false;
        return !entries.some((entry) => isSummitActivePayer(entry.sc));
      })
      .sort((a, b) => (a.days_inactive ?? 999) - (b.days_inactive ?? 999)),
    [users, summitMatchesByPhone]);

  const billingNeedsMapping = useMemo(() =>
    users
      .filter((u) => {
        if (!u.has_mixed) return false;
        if (u.days_inactive === null || u.days_inactive > 60) return false;
        return !(summitMatchesByPhone.get(normalizePhone(u.phone_number))?.length);
      })
      .sort((a, b) => (a.days_inactive ?? 999) - (b.days_inactive ?? 999)),
    [users, summitMatchesByPhone]);

  const billingWithoutActiveAutopay = useMemo(() => {
    const rows: BillingNoAutopayRow[] = [];
    users
      .filter((user) =>
        user.has_mixed &&
        user.days_inactive !== null &&
        user.days_inactive < 60
      )
      .forEach((user) => {
        const entries = summitMatchesByPhone.get(normalizePhone(user.phone_number)) || [];
        if (entries.length > 0 && entries.every(({ sc }) => isSummitInternalCustomer(sc))) return;
        if (PORTUGAL_DISTRIBUTOR_APP_NAMES.has(user.salon_name)) {
          rows.push({
            key: `user-${user.id}-distributor`,
            user,
            customer: entries[0]?.sc ?? null,
            category: "distributor_covered",
            detail: "Covered by April Morning Lda distributor agreement",
          });
          return;
        }
        if (entries.length === 0) {
          rows.push({
            key: `user-${user.id}`,
            user,
            customer: null,
            category: "needs_mapping",
            detail: "SUMIT identity has not been matched",
          });
          return;
        }

        if (entries.some(({ sc }) => hasVerifiedActiveMonthlyCharge(sc))) return;

        const entry = [...entries].sort(
          (a, b) => summitMonthKey(b.sc.lastPaidMonth) - summitMonthKey(a.sc.lastPaidMonth),
        )[0];
        const customer = entry.sc;
        const reconciliation = getSummitBillingReconciliation(customer);
        let category: BillingNoAutopayCategory = "stopped";
        let detail = customer.lastPaidMonth
          ? `No verified active monthly charge · last paid ${customer.lastPaidMonth}`
          : "No verified monthly charge";

        if (reconciliation?.status === "subscription_never_paid") {
          category = "no_subscription";
          detail = `Never paid a subscription · active since ${reconciliation.unpaidFromMonth || "installation"}`;
        } else if (reconciliation?.status === "payment_method_update_required") {
          category = "payment_issue";
          detail = `Payment method update required · unpaid since ${reconciliation.unpaidFromMonth || "unknown"}`;
        } else if (reconciliation?.status === "no_active_payment_method") {
          category = "no_active_payment_method";
          detail = "No active payment method in SUMIT; monthly payments exist through 10/2026";
        } else if (reconciliation?.status === "annual_renewal_due") {
          category = "annual_due";
          detail = `Annual renewal due ${reconciliation.renewalDueMonth || ""}`.trim();
        } else if (reconciliation?.status === "annual_subscription") {
          category = "annual_covered";
          detail = `Annual plan paid through ${reconciliation.paidThroughMonth || "verified term"}`;
        } else if (
          customer.paymentType === "equipment" ||
          (customer.currentPaymentStreak || 0) <= 1 &&
          (customer.paidMonths || 0) <= 2
        ) {
          category = "equipment_or_one_time";
          detail = customer.lastPaidMonth
            ? `Equipment or one-time payment · ${customer.lastPaidMonth}`
            : "Equipment or one-time payment only";
        }

        rows.push({
          key: `user-${user.id}-summit-${customer.sumitId || customer.name}`,
          user,
          customer,
          category,
          detail,
        });
      });

    return rows.sort((a, b) => {
      const aDays = a.user?.days_inactive ?? 999;
      const bDays = b.user?.days_inactive ?? 999;
      return aDays - bDays || (a.user?.salon_name || a.customer?.name || "").localeCompare(
        b.user?.salon_name || b.customer?.name || "",
      );
    });
  }, [users, summitMatchesByPhone]);

  const billingWithoutActiveAutopayFiltered = useMemo(() => {
    const q = normalizeAdminSearch(billingNoAutopaySearch);
    if (!q) return billingWithoutActiveAutopay;
    return billingWithoutActiveAutopay.filter((row) => {
      const alias = row.customer?.sumitId
        ? SUMMIT_CUSTOMER_ALIASES.find((candidate) => candidate.sumitId === row.customer?.sumitId)
        : undefined;
      return normalizeAdminSearch([
        row.user?.salon_name,
        row.user?.phone_number,
        row.customer?.name,
        row.customer?.sumitId,
        row.detail,
        alias?.appName,
        alias?.summitName,
        ...(alias?.searchTerms || []),
      ].join(" ")).includes(q);
    });
  }, [billingWithoutActiveAutopay, billingNoAutopaySearch]);

  const billingConfirmedDebts = useMemo(() => {
    const rows: Array<{
      issue: SummitBillingReconciliationCase;
      customer: SummitCustomer;
      missedMonths: number;
      monthlyEstimate: number;
      estimatedDebt: number;
      basis: string;
    }> = [];
    SUMMIT_BILLING_RECONCILIATION.forEach((issue) => {
      if (!issue.confirmed) return;
      const customer = SUMMIT_DATA.customers.find((candidate) => candidate.sumitId === issue.sumitId);
      if (!customer) return;
      if (issue.status === "annual_renewal_due" && issue.renewalAmountIls && issue.renewalDueMonth) {
        rows.push({
          issue,
          customer,
          missedMonths: 0,
          monthlyEstimate: 0,
          estimatedDebt: issue.renewalAmountIls,
          basis: `annual renewal · due ${issue.renewalDueMonth}`,
        });
        return;
      }
      if (issue.status !== "payment_method_update_required" || !issue.unpaidFromMonth) return;
      const unpaidStartIndex = SUMMIT_DATA.months.indexOf(issue.unpaidFromMonth);
      if (unpaidStartIndex < 0) return;
      const missedMonths = Math.max(0, SUMMIT_DATA.months.length - unpaidStartIndex);
      const monthlyEstimate = Math.round(customer.typicalMonthly || customer.averagePaidMonth || 0);
      rows.push({
        issue,
        customer,
        missedMonths,
        monthlyEstimate,
        estimatedDebt: missedMonths * monthlyEstimate,
        basis: `${missedMonths}mo × ₪${monthlyEstimate}`,
      });
    });
    return rows;
  }, []);

  const billingDebtBySumitId = useMemo(
    () => new Map(billingConfirmedDebts.map((debt) => [debt.issue.sumitId, debt])),
    [billingConfirmedDebts],
  );
  const billingConfirmedDebtTotal = billingConfirmedDebts.reduce(
    (sum, debt) => sum + debt.estimatedDebt,
    0,
  );

  const billingEquipmentOnly = useMemo(() =>
    summitWithMatch
      .filter(({ sc }) => sc.paymentType === "equipment")
      .sort((a, b) => b.sc.ltv - a.sc.ltv),
    [summitWithMatch]);

  const billingStoppedSubs = useMemo(() =>
    summitWithMatch
      .filter(({ sc }) => sc.stoppedSubscription && !isSummitActivePayer(sc))
      .sort((a, b) => b.sc.ltv - a.sc.ltv),
    [summitWithMatch]);

  // ── Summit-only paid customers (no Spectra/app cross-reference) ──
  const summitPaidCustomers = useMemo(
    () => SUMMIT_DATA.customers.filter((c) => (c.ltv || 0) > 0),
    [],
  );

  const summitDistributorCustomers = useMemo(
    () => summitPaidCustomers.filter((c) => !!getSummitDistributor(c.name)),
    [summitPaidCustomers],
  );
  const summitDistributorRevenue = useMemo(
    () => summitDistributorCustomers.reduce((sum, c) => sum + c.ltv, 0),
    [summitDistributorCustomers],
  );
  const summitDistributorLicenses = SUMMIT_DISTRIBUTORS.reduce(
    (sum, distributor) => sum + distributor.annualLicenses,
    0,
  );

  // Customers included in KPI calculations (respects per-customer exclusions)
  const summitKpiCustomers = useMemo(
    () => summitPaidCustomers.filter(
      (c) => !getSummitDistributor(c.name) && !isSummitInternalCustomer(c) && !summitExcluded.has(c.name),
    ),
    [summitPaidCustomers, summitExcluded],
  );
  const summitKpiRevenueCustomers = useMemo(
    () => SUMMIT_DATA.customers.filter(
      (c) => !getSummitDistributor(c.name) && !isSummitInternalCustomer(c) && !summitExcluded.has(c.name),
    ),
    [summitExcluded],
  );

  // Aggregate Summit KPIs — always recomputed from the included customer set,
  // so toggling exclusions updates every card immediately.
  const summitKpis = useMemo(() => {
    const customers = summitKpiCustomers;
    const revenueCustomers = summitKpiRevenueCustomers;
    const months    = SUMMIT_DATA.months;
    const monthCount = months.length;
    const LAST_IDX   = monthCount - 1;
    const lastWinStart = Math.max(0, LAST_IDX - 1);
    const lastWinEnd   = LAST_IDX;
    const prevWinStart = Math.max(0, LAST_IDX - 3);
    const prevWinEnd   = Math.max(0, LAST_IDX - 2);

    const paidCount       = customers.length;
    const totalLTV        = revenueCustomers.reduce((sum, c) => sum + c.ltv, 0);
    const totalPaidMonths = revenueCustomers.reduce(
      (sum, c) => sum + (c.paidMonths ?? c.activeMonths ?? 0),
      0,
    );

    let lastWinRev = 0;
    let prevWinRev = 0;
    let bothWindows = 0;
    let prevWinCustomers = 0;
    customers.forEach((c) => {
      let lw = 0;
      let pw = 0;
      let paidLw = false;
      let paidPw = false;
      for (let i = lastWinStart; i <= lastWinEnd; i++) {
        const v = c.monthly[i] ?? 0;
        if (v > 0) { lw += v; paidLw = true; }
      }
      for (let i = prevWinStart; i <= prevWinEnd; i++) {
        const v = c.monthly[i] ?? 0;
        if (v > 0) { pw += v; paidPw = true; }
      }
      lastWinRev += lw;
      prevWinRev += pw;
      if (paidPw) prevWinCustomers++;
      if (paidPw && paidLw) bothWindows++;
    });

    return {
      paidCount,
      totalLTV:           Math.round(totalLTV),
      avgLtv:             paidCount > 0 ? Math.round(totalLTV / paidCount) : 0,
      avgPaidMonth:       totalPaidMonths > 0 ? Math.round(totalLTV / totalPaidMonths) : 0,
      avgPaidMonths:      paidCount > 0 ? Math.round((totalPaidMonths / paidCount) * 10) / 10 : 0,
      activePayers:       customers.filter(isSummitActivePayer).length,
      churned:            customers.filter(isSummitChurned).length,
      newCustomers:       customers.filter((c) => c.newCustomer).length,
      reactivated:        customers.filter((c) => c.reactivated).length,
      revenueRetention:   prevWinRev > 0 ? lastWinRev / prevWinRev : 0,
      customerRetention:  prevWinCustomers > 0 ? bothWindows / prevWinCustomers : 0,
      latestMonth:        SUMMIT_DATA.latestMonth ?? months[monthCount - 1] ?? null,
    };
  }, [summitKpiCustomers, summitKpiRevenueCustomers]);

  // Filtered + searched Summit customers (status, min LTV, name search)
  const summitFiltered = useMemo(() => {
    const q = summitSearch.trim().toLowerCase();
    return summitPaidCustomers.filter((c) => {
      if (summitMinLtv > 0 && c.ltv < summitMinLtv) return false;
      if (summitStatus === "active"      && !isSummitActivePayer(c)) return false;
      if (summitStatus === "churned"     && !isSummitChurned(c)) return false;
      if (summitStatus === "new"         && !c.newCustomer) return false;
      if (summitStatus === "reactivated" && !c.reactivated) return false;
      if (q && !c.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [summitPaidCustomers, summitStatus, summitMinLtv, summitSearch]);

  // Sorted Summit customers
  const summitSorted = useMemo(() => {
    const dir = summitSortDir === "asc" ? 1 : -1;
    const arr = [...summitFiltered];
    arr.sort((a, b) => {
      let va: number | string = 0;
      let vb: number | string = 0;
      switch (summitSortField) {
        case "name":             va = a.name.toLowerCase();          vb = b.name.toLowerCase();          break;
        case "ltv":              va = a.ltv;                          vb = b.ltv;                          break;
        case "paidMonths":       va = a.paidMonths ?? a.activeMonths ?? 0; vb = b.paidMonths ?? b.activeMonths ?? 0; break;
        case "averagePaidMonth": va = a.averagePaidMonth ?? 0;        vb = b.averagePaidMonth ?? 0;        break;
        case "lastMonthAmount":  va = a.lastMonthAmount ?? 0;         vb = b.lastMonthAmount ?? 0;         break;
        case "firstPaidMonth":   va = summitMonthKey(a.firstPaidMonth); vb = summitMonthKey(b.firstPaidMonth); break;
        case "lastPaidMonth":    va = summitMonthKey(a.lastPaidMonth);  vb = summitMonthKey(b.lastPaidMonth);  break;
        case "churnMonths":      va = a.churnMonths ?? 0;             vb = b.churnMonths ?? 0;             break;
        case "retentionRate":    va = a.retentionRate ?? 0;           vb = b.retentionRate ?? 0;           break;
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [summitFiltered, summitSortField, summitSortDir]);

  const summitTotalPages = Math.max(1, Math.ceil(summitSorted.length / summitPageSize));
  const summitPaged = useMemo(() => {
    const start = (summitPage - 1) * summitPageSize;
    return summitSorted.slice(start, start + summitPageSize);
  }, [summitSorted, summitPage, summitPageSize]);

  // Keep page index valid when filters shrink the result set
  useEffect(() => {
    if (summitPage > summitTotalPages) setSummitPage(1);
  }, [summitPage, summitTotalPages]);

  // ── KPI counts (CS) ──
  const kpis = useMemo(() => {
    let active = 0, atRisk = 0, critical = 0, recovered = 0, churned = 0;
    users.forEach(u => {
      if (u.days_inactive !== null && u.days_inactive <= 7) { if (u.tenure_days !== null && u.tenure_days > 90) recovered++; active++; }
      else if (u.days_inactive !== null && u.days_inactive <= 14) atRisk++;
      else if (u.days_inactive !== null && u.days_inactive <= 30) critical++;
      else churned++;
    });
    return { active, atRisk, critical, recovered, churned };
  }, [users]);

  // ── Cohort counts ──
  const cohortCounts = useMemo(() => {
    const counts: Record<UsageBucket, number> = { recent14: 0, recent30: 0, recent90: 0, recent180: 0, recent365: 0, over1year: 0, never: 0 };
    users.forEach(u => { counts[u.cohort_bucket]++; });
    return counts;
  }, [users]);

  const cohortViewIsDefault = search === "" &&
    systemFilter === "all" &&
    countryFilter === "all" &&
    cohortBucket === "all" &&
    cohortSortField === null &&
    cohortPage === 1;

  function resetCohortView() {
    setSearch("");
    setSystemFilter("all");
    setCountryFilter("all");
    setCohortBucket("all");
    setCohortSortField(null);
    setCohortSortDir("asc");
    setCohortPage(1);
    setPendingEdits({});
    setIsTableEditing(false);
  }

  // ── Attention Today (CS) ──
  const attentionList = useMemo(() => [...users].filter(u => u.health.status !== "healthy").sort((a, b) => {
    if (a.health.score !== b.health.score) return a.health.score - b.health.score;
    return (b.tenure_days || 0) - (a.tenure_days || 0);
  }).slice(0, 10), [users]);

  // ── Insights (CS) ──
  const insights = useMemo(() => {
    const churned = users.filter(u => u.days_inactive === null || u.days_inactive > 30);
    const churnedSingleProfile = churned.filter(u => u.profiles <= 1).length;
    const churnedPct = churned.length > 0 ? Math.round((churnedSingleProfile / churned.length) * 100) : 0;
    const powerUsers = users.filter(u => u.lifecycle === "power_user").length;
    const avgProfiles = users.length > 0 ? (users.reduce((s, u) => s + u.profiles, 0) / users.length).toFixed(1) : "0";
    const noFirstMix = users.filter(u => !u.has_mixed).length;
    const noFirstMixPct = users.length > 0 ? Math.round((noFirstMix / users.length) * 100) : 0;
    const recoveredCount = users.filter(u => u.risk_tags.includes("recovered")).length;
    return { churnedPct, churnedSingleProfile, powerUsers, avgProfiles, noFirstMix, noFirstMixPct, recoveredCount, totalChurned: churned.length };
  }, [users]);

  // ── OVERVIEW — filtered, sorted, paged ──
  const ovFiltered = useMemo(() => {
    let result = [...allUsers];
    if (search) {
      result = result.filter((user) => userMatchesSearch(user, search));
    }
    if (systemFilter !== "all") result = result.filter(u => u.system_category === systemFilter);
    if (countryFilter !== "all") result = result.filter(u => u.normalized_country === countryFilter);
    if (versionFilter !== "all") result = result.filter(u => u.version === versionFilter);
    if (profileFilter === "active") result = result.filter(u => u.profiles > 0);
    else if (profileFilter === "zero") result = result.filter(u => u.profiles === 0);
    else if (profileFilter === "multi") result = result.filter(u => u.profiles > 1);
    result.sort((a, b) => {
      let aV: any;
      let bV: any;
      switch (ovSortField) {
        case "profiles":
          aV = Number(a.profiles) || 0;
          bV = Number(b.profiles) || 0;
          break;
        case "first_mix_date":
          aV = a.tenure_days ?? Number.POSITIVE_INFINITY;
          bV = b.tenure_days ?? Number.POSITIVE_INFINITY;
          break;
        case "last_mix_date":
          aV = a.days_inactive ?? Number.POSITIVE_INFINITY;
          bV = b.days_inactive ?? Number.POSITIVE_INFINITY;
          break;
        case "version":
          aV = Number(a.version) || 0;
          bV = Number(b.version) || 0;
          break;
        default:
          aV = ((a as any)[ovSortField] || "").toString().toLowerCase();
          bV = ((b as any)[ovSortField] || "").toString().toLowerCase();
          break;
      }
      if (aV < bV) return ovSortDir === "asc" ? -1 : 1; if (aV > bV) return ovSortDir === "asc" ? 1 : -1; return 0;
    });
    return result;
  }, [allUsers, search, systemFilter, countryFilter, versionFilter, profileFilter, ovSortField, ovSortDir, userMatchesSearch]);

  const ovTotalPages = Math.ceil(ovFiltered.length / PAGE_SIZE);
  const ovPaged = useMemo(() => ovFiltered.slice((ovPage - 1) * PAGE_SIZE, ovPage * PAGE_SIZE), [ovFiltered, ovPage]);
  useEffect(() => { setOvPage(1); }, [search, systemFilter, countryFilter, versionFilter, profileFilter]);

  // ── CS — filtered, sorted, paged ──
  const csFiltered = useMemo(() => {
    let result = [...users];
    if (search) result = result.filter((user) => userMatchesSearch(user, search));
    if (countryFilter !== "all") result = result.filter(u => u.normalized_country === countryFilter);
    if (versionFilter !== "all") result = result.filter(u => u.version === versionFilter);
    if (lifecycleFilter !== "all") result = result.filter(u => u.lifecycle === lifecycleFilter);
    if (statusFilter === "active") result = result.filter(u => u.days_inactive !== null && u.days_inactive <= 7);
    else if (statusFilter === "at_risk") result = result.filter(u => u.days_inactive !== null && u.days_inactive > 7 && u.days_inactive <= 14);
    else if (statusFilter === "critical") result = result.filter(u => u.days_inactive !== null && u.days_inactive > 14 && u.days_inactive <= 30);
    else if (statusFilter === "recovered") result = result.filter(u => u.risk_tags.includes("recovered"));
    else if (statusFilter === "churned") result = result.filter(u => u.days_inactive === null || u.days_inactive > 30);
    if (quickToggle === "at_risk") result = result.filter(u => u.health.status !== "healthy");
    else if (quickToggle === "new") result = result.filter(u => u.lifecycle === "new" || u.lifecycle === "activated");
    else if (quickToggle === "high_potential") result = result.filter(u => u.profiles >= 5);
    result.sort((a, b) => {
      let aV: any, bV: any;
      switch (csSortField) {
        case "health_score": aV = a.health.score; bV = b.health.score; break;
        case "days_inactive": aV = a.days_inactive ?? 9999; bV = b.days_inactive ?? 9999; break;
        case "profiles": aV = a.profiles; bV = b.profiles; break;
        default: aV = (a as any)[csSortField] || ""; bV = (b as any)[csSortField] || ""; if (typeof aV === "string") { aV = aV.toLowerCase(); bV = (bV as string).toLowerCase(); }
      }
      if (aV < bV) return csSortDir === "asc" ? -1 : 1; if (aV > bV) return csSortDir === "asc" ? 1 : -1; return 0;
    });
    return result;
  }, [users, search, countryFilter, versionFilter, statusFilter, lifecycleFilter, quickToggle, csSortField, csSortDir, userMatchesSearch]);

  const csTotalPages = Math.ceil(csFiltered.length / PAGE_SIZE);
  const csPaged = useMemo(() => csFiltered.slice((csPage - 1) * PAGE_SIZE, csPage * PAGE_SIZE), [csFiltered, csPage]);
  useEffect(() => { setCsPage(1); }, [search, countryFilter, versionFilter, statusFilter, lifecycleFilter, quickToggle]);

  // ── COHORT — filtered, sorted, paged ──
  const cohortFiltered = useMemo(() => {
    let result = [...allUsers];
    if (search) {
      result = result.filter((user) => userMatchesSearch(user, search));
    }
    if (systemFilter !== "all") result = result.filter(u => u.system_category === systemFilter);
    if (countryFilter !== "all") result = result.filter(u => u.normalized_country === countryFilter);
    if (cohortBucket !== "all") result = result.filter(u => u.cohort_bucket === cohortBucket);
    if (cohortSortField === null) {
      // Default (no column marked): setup order — newest-created salon first.
      // DB id is auto-incrementing, so highest id = most recently created.
      result.sort((a, b) => b.id - a.id);
    } else {
      result.sort((a, b) => {
        let aV: any, bV: any;
        switch (cohortSortField) {
          case "days_inactive": aV = a.days_inactive ?? 99999; bV = b.days_inactive ?? 99999; break;
          case "profiles": aV = a.profiles; bV = b.profiles; break;
          // sort by numeric tenure so newest joiners (smallest value) sort first with "asc"
          case "first_mix_date": aV = a.tenure_days ?? 99999; bV = b.tenure_days ?? 99999; break;
          case "total_mixes": aV = getMixStats(a.phone_number)?.totalMixes ?? -1; bV = getMixStats(b.phone_number)?.totalMixes ?? -1; break;
          case "version": aV = a.version || ""; bV = b.version || ""; break;
          default: aV = ((a as any)[cohortSortField] || "").toString().toLowerCase(); bV = ((b as any)[cohortSortField] || "").toString().toLowerCase(); break;
        }
        if (aV < bV) return cohortSortDir === "asc" ? -1 : 1; if (aV > bV) return cohortSortDir === "asc" ? 1 : -1; return 0;
      });
    }
    return result;
  }, [allUsers, search, systemFilter, countryFilter, cohortBucket, cohortSortField, cohortSortDir, mixIndexVersion, userMatchesSearch]);

  const cohortTotalPages = Math.ceil(cohortFiltered.length / PAGE_SIZE);
  const cohortPaged = useMemo(() => cohortFiltered.slice((cohortPage - 1) * PAGE_SIZE, cohortPage * PAGE_SIZE), [cohortFiltered, cohortPage]);
  useEffect(() => { setCohortPage(1); }, [search, systemFilter, countryFilter, cohortBucket]);

  // ── RETENTION — status counts, KPIs, charts ──
  const retentionStatusCounts = useMemo(() => {
    const counts = {
      healthy: 0, growing: 0, recovered: 0, at_risk: 0,
      declining: 0, churned: 0, never_activated: 0,
    } as Record<RetentionStatus, number>;
    users.forEach(u => { counts[u.retention.status]++; });
    return counts;
  }, [users]);

  const retentionKpis = useMemo(() => {
    let healthyGrowing = 0, recovered = 0, activeThisWeek = 0;
    let atRiskHighValue = 0, declining = 0, churnedPower = 0, neverActivated = 0;
    let mixesLost = 0, activeMixes = 0;
    users.forEach(u => {
      const r = u.retention;
      if (r.status === "healthy" || r.status === "growing") healthyGrowing++;
      if (r.status === "recovered") recovered++;
      if (u.days_inactive !== null && u.days_inactive <= 7) activeThisWeek++;
      if (r.status === "at_risk" && (r.isPower || u.profiles >= 3 || r.totalMixes >= 20)) atRiskHighValue++;
      if (r.status === "declining") declining++;
      if (r.status === "churned" && r.isPower) churnedPower++;
      if (r.status === "never_activated") neverActivated++;
      mixesLost += r.estimatedMixesLost;
      if (u.days_inactive !== null && u.days_inactive <= 30) activeMixes += r.totalMixes;
    });
    return {
      healthyGrowing, recovered, activeThisWeek, atRiskHighValue,
      declining, churnedPower, neverActivated,
      mixesLost: Math.round(mixesLost), activeMixes,
    };
  }, [users]);

  const retentionPieData = useMemo(() => (
    (Object.keys(RETENTION_CONFIG) as RetentionStatus[])
      .map(status => ({ name: RETENTION_CONFIG[status].label, value: retentionStatusCounts[status], status }))
      .filter(d => d.value > 0)
  ), [retentionStatusCounts]);

  // Inactivity cohorts weighted by historical value (total mixes at stake).
  const retentionInactivityBars = useMemo(() => {
    const buckets = [
      { key: "0-7", label: "0–7d", min: 0, max: 7 },
      { key: "8-14", label: "8–14d", min: 8, max: 14 },
      { key: "15-30", label: "15–30d", min: 15, max: 30 },
      { key: "31-60", label: "31–60d", min: 31, max: 60 },
      { key: "60+", label: "60d+", min: 61, max: Infinity },
    ];
    return buckets.map(b => {
      let customers = 0, mixesAtRisk = 0;
      users.forEach(u => {
        if (u.days_inactive === null) return;
        if (u.days_inactive >= b.min && u.days_inactive <= b.max) {
          customers++;
          mixesAtRisk += u.retention.totalMixes;
        }
      });
      return { label: b.label, customers, mixesAtRisk };
    });
  }, [users]);

  // Monthly mix trend from the bundled phone-mix history (sum across users).
  const retentionMonthlyTrend = useMemo(() => {
    const byMonth = new Map<string, { month: string; sortIdx: number; mixes: number }>();
    users.forEach(u => {
      const series = getMixStats(u.phone_number)?.monthlyServices;
      if (!series) return;
      series.forEach(p => {
        const cur = byMonth.get(p.month) || { month: p.month, sortIdx: p.sortIdx, mixes: 0 };
        cur.mixes += p.mixes;
        byMonth.set(p.month, cur);
      });
    });
    return Array.from(byMonth.values())
      .sort((a, b) => a.sortIdx - b.sortIdx)
      .slice(-12)
      .map(p => ({ month: p.month, mixes: p.mixes }));
  }, [users, mixIndexVersion]);

  // High-value customers needing attention, ranked by priority score.
  const retentionAttentionList = useMemo(() => (
    [...users]
      .filter(u => (u.retention.status === "churned" || u.retention.status === "declining" || u.retention.status === "at_risk") && u.retention.totalMixes >= 10)
      .sort((a, b) => b.retention.priorityScore - a.retention.priorityScore)
      .slice(0, 10)
  ), [users]);

  // ── RETENTION — filtered, sorted, paged table ──
  const retentionFiltered = useMemo(() => {
    let result = [...users];
    if (search) result = result.filter((user) => userMatchesSearch(user, search));
    if (countryFilter !== "all") result = result.filter(u => u.normalized_country === countryFilter);
    if (versionFilter !== "all") result = result.filter(u => u.version === versionFilter);
    if (retentionFilter !== "all") result = result.filter(u => u.retention.status === retentionFilter);
    result.sort((a, b) => {
      let aV: number | string, bV: number | string;
      switch (retentionSortField) {
        case "priority_score": aV = a.retention.priorityScore; bV = b.retention.priorityScore; break;
        case "value_score": aV = a.retention.valueScore; bV = b.retention.valueScore; break;
        case "total_mixes": aV = a.retention.totalMixes; bV = b.retention.totalMixes; break;
        case "days_inactive": aV = a.days_inactive ?? 999999; bV = b.days_inactive ?? 999999; break;
        case "trend": aV = a.retention.trendPct ?? 9999; bV = b.retention.trendPct ?? 9999; break;
        case "profiles": aV = a.profiles; bV = b.profiles; break;
        default: aV = (a.salon_name || "").toLowerCase(); bV = (b.salon_name || "").toLowerCase(); break;
      }
      if (aV < bV) return retentionSortDir === "asc" ? -1 : 1;
      if (aV > bV) return retentionSortDir === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [users, search, countryFilter, versionFilter, retentionFilter, retentionSortField, retentionSortDir, mixIndexVersion, userMatchesSearch]);

  const retentionTotalPages = Math.ceil(retentionFiltered.length / PAGE_SIZE);
  const retentionPaged = useMemo(() => retentionFiltered.slice((retentionPage - 1) * PAGE_SIZE, retentionPage * PAGE_SIZE), [retentionFiltered, retentionPage]);
  useEffect(() => { setRetentionPage(1); }, [search, countryFilter, versionFilter, retentionFilter]);

  // ── CHURN REASONS — churned-only base, reason aggregation, charts, table ──
  // Base set of churned customers honoring the shared search/country/version filters.
  const churnedBase = useMemo(() => {
    let result = users.filter(u => u.retention.status === "churned");
    if (search) result = result.filter((user) => userMatchesSearch(user, search));
    if (countryFilter !== "all") result = result.filter(u => u.normalized_country === countryFilter);
    if (versionFilter !== "all") result = result.filter(u => u.version === versionFilter);
    return result;
  }, [users, search, countryFilter, versionFilter, userMatchesSearch]);

  // Live reason counts — recomputed whenever a dropdown changes (rawUsers updates).
  const churnReasonCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    CHURN_REASONS.forEach(r => { counts[r.id] = 0; });
    let unassigned = 0;
    churnedBase.forEach(u => {
      const rid = (u.churn_reason || "").trim();
      if (rid && counts[rid] !== undefined) counts[rid]++;
      else unassigned++;
    });
    return { counts, unassigned };
  }, [churnedBase]);

  const churnKpis = useMemo(() => {
    const total = churnedBase.length;
    const assigned = total - churnReasonCounts.unassigned;
    let topId: string | null = null; let topVal = 0;
    CHURN_REASONS.forEach(r => { if (churnReasonCounts.counts[r.id] > topVal) { topVal = churnReasonCounts.counts[r.id]; topId = r.id; } });
    const lostMixes = churnedBase.reduce((s, u) => s + (u.retention.totalMixes || 0), 0);
    return {
      total,
      assigned,
      unassigned: churnReasonCounts.unassigned,
      coveragePct: total > 0 ? Math.round((assigned / total) * 100) : 0,
      topReason: topId ? CHURN_REASON_MAP[topId].label : "—",
      topReasonCount: topVal,
      lostMixes,
    };
  }, [churnedBase, churnReasonCounts]);

  const churnPieData = useMemo(() => {
    const data = CHURN_REASONS
      .map(r => ({ id: r.id as string, name: r.label, value: churnReasonCounts.counts[r.id], pie: r.pie }))
      .filter(d => d.value > 0);
    if (churnReasonCounts.unassigned > 0) {
      data.push({ id: "unassigned", name: "Unassigned", value: churnReasonCounts.unassigned, pie: CHURN_UNASSIGNED_COLOR });
    }
    return data;
  }, [churnReasonCounts]);

  // Horizontal-style ranked bars (all reasons, including zero, for stable layout).
  const churnBarData = useMemo(() => (
    CHURN_REASONS
      .map(r => ({ id: r.id as string, name: r.label, value: churnReasonCounts.counts[r.id], pie: r.pie }))
      .sort((a, b) => b.value - a.value)
  ), [churnReasonCounts]);

  const churnFiltered = useMemo(() => {
    let result = [...churnedBase];
    if (churnReasonFilter !== "all") {
      result = churnReasonFilter === "unassigned"
        ? result.filter(u => !(u.churn_reason || "").trim())
        : result.filter(u => (u.churn_reason || "").trim() === churnReasonFilter);
    }
    // Highest-value churned first so the most important ones surface on top.
    return result.sort((a, b) => b.retention.valueScore - a.retention.valueScore);
  }, [churnedBase, churnReasonFilter]);

  const churnTotalPages = Math.ceil(churnFiltered.length / PAGE_SIZE);
  const churnPaged = useMemo(() => churnFiltered.slice((churnPage - 1) * PAGE_SIZE, churnPage * PAGE_SIZE), [churnFiltered, churnPage]);
  useEffect(() => { setChurnPage(1); }, [search, countryFilter, versionFilter, churnReasonFilter, retentionView]);

  const handleRetentionSort = (field: RetentionSortField) => {
    if (retentionSortField === field) setRetentionSortDir(d => d === "asc" ? "desc" : "asc");
    else { setRetentionSortField(field); setRetentionSortDir(field === "salon_name" ? "asc" : "desc"); }
    setRetentionPage(1);
  };

  // ── Sort handlers ──
  const handleOvSort = (field: OverviewSortField) => {
    if (ovSortField === field) setOvSortDir(d => d === "asc" ? "desc" : "asc");
    else {
      setOvSortField(field);
      setOvSortDir(field === "first_mix_date" || field === "last_mix_date" ? "asc" : "desc");
    }
  };
  const handleCsSort = (field: CSSortField) => {
    if (csSortField === field) setCsSortDir(d => d === "asc" ? "desc" : "asc");
    else { setCsSortField(field); setCsSortDir(field === "health_score" || field === "days_inactive" ? "asc" : "desc"); }
  };
  const handleCohortSort = (field: CohortSortField) => {
    // Tri-state cycle: asc → desc → off (natural order), so the sort can be cleared.
    if (cohortSortField === field) {
      if (cohortSortDir === "asc") setCohortSortDir("desc");
      else { setCohortSortField(null); setCohortSortDir("asc"); }
    } else {
      setCohortSortField(field);
      setCohortSortDir(field === "days_inactive" || field === "first_mix_date" ? "asc" : "desc");
    }
    setCohortPage(1);
  };

  function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
    if (!active) return <ArrowUpDown className="w-3 h-3 opacity-20" />;
    return dir === "asc" ? <ArrowUp className="w-3 h-3 text-indigo-400" /> : <ArrowDown className="w-3 h-3 text-indigo-400" />;
  }

  const HealthBadge = ({ score, status }: { score: number; status: string }) => {
    const cfg = status === "healthy" ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/20" : status === "at_risk" ? "bg-amber-500/15 text-amber-500 border-amber-500/20" : "bg-red-500/15 text-red-500 border-red-500/20";
    return <span className={`inline-flex items-center justify-center w-10 h-7 rounded-lg text-xs font-bold border ${cfg}`}>{score}</span>;
  };

  // ── Freshness label ──
  function freshnessLabel(): string {
    if (!loadedAt) return "Loading…";
    const diffMs = Date.now() - loadedAt.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "Updated just now";
    if (mins < 60) return `Updated ${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Updated ${hrs}h ago`;
    return `Updated ${Math.floor(hrs / 24)}d ago`;
  }

  // ── LOADING & ERROR ──
  if (loading) return (
    <div className={`min-h-[100dvh] flex items-center justify-center ${at.page}`}>
      <div className="flex flex-col items-center gap-4">
        <div className={`w-12 h-12 border-4 rounded-full animate-spin ${at.spinner}`} />
        <p className={`text-sm ${at.textMuted}`}>Loading dashboard…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className={`min-h-[100dvh] flex items-center justify-center ${at.page}`}>
      <div className="text-center max-w-md px-4">
        <ShieldAlert className="w-10 h-10 text-red-400/60 mx-auto mb-3" />
        <p className="text-red-400 mb-4 text-sm">{error}</p>
        <button onClick={fetchData} className="px-5 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-500 transition text-sm">Retry</button>
      </div>
    </div>
  );

  return (
    <div className={`min-h-[100dvh] ${at.page}`}>
      {/* ── Sticky Top Bar ── */}
      <div className={`sticky top-0 z-40 ${at.sticky} backdrop-blur-xl border-b ${at.stickyBorder}`}>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/" className={`flex-shrink-0 transition ${at.textFaint} hover:${at.textMuted}`}><ChevronLeft className="w-5 h-5" /></Link>
            <div className="min-w-0">
              <h1 className={`text-lg font-semibold flex items-center gap-2 ${at.textPrimary}`}>
                <Layers className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                Admin Dashboard
              </h1>
              <p className={`text-xs ${at.textFaint}`}>Salon Users Management</p>
            </div>
          </div>

          {/* ── Domain switcher + inner tabs ── */}
          <div className="flex flex-col gap-1">
            {/* Domain switcher */}
            <div className={`flex items-center gap-1 ${at.tabWrap} rounded-xl p-1 border`}>
              {([
                { id: "customer" as AdminDomain, label: "Customer Operations", icon: Users },
                { id: "data"     as AdminDomain, label: "Data Intelligence",    icon: Database },
              ]).map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setAdminDomain(id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                    adminDomain === id ? at.tabActive : at.tabInactive
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>

            {/* Inner tabs */}
            <div className={`flex items-center gap-1 ${at.tabWrap} rounded-xl p-1 border overflow-x-auto`}>
              {adminDomain === "customer" ? (
                ([
                  { id: "cohorts"  as CustomerTab, label: "Usage Cohorts",  icon: Users },
                  { id: "overview" as CustomerTab, label: "Overview",        icon: LayoutDashboard },
                  { id: "success"  as CustomerTab, label: "CS",              icon: Heart },
                  { id: "retention" as CustomerTab, label: "Retention",      icon: Activity },
                  { id: "billing"  as CustomerTab, label: "Billing",         icon: TrendingDown },
                ] as { id: CustomerTab; label: string; icon: any }[]).map(({ id, label, icon: Icon }) => (
                  <button key={id} onClick={() => setActiveCustomerTab(id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${activeCustomerTab === id ? at.tabActive : at.tabInactive}`}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))
              ) : (
                ([
                  { id: "truth"      as DataTab, label: "Product Truth",       icon: ShieldCheck },
                  { id: "products"   as DataTab, label: "Product Catalog",      icon: Database },
                  { id: "truss"      as DataTab, label: "TRUSS Catalog",        icon: Palette },
                  { id: "beauty"     as DataTab, label: "Beauty Intelligence",  icon: BookOpen },
                  { id: "imports"    as DataTab, label: "Usage Imports",        icon: Upload },
                  { id: "review"     as DataTab, label: "Review Queue",         icon: ShieldAlert },
                  { id: "ai"         as DataTab, label: "AI Analyst",           icon: Zap },
                  { id: "operations" as DataTab, label: "Data Operations",      icon: Boxes },
                  { id: "database"   as DataTab, label: "Product Database",     icon: Layers },
                  { id: "resolution" as DataTab, label: "Resolution Center",    icon: Eye },
                ] as { id: DataTab; label: string; icon: any }[]).map(({ id, label, icon: Icon }) => (
                  <button key={id} onClick={() => setActiveDataTab(id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${activeDataTab === id ? at.tabActive : at.tabInactive}`}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-xs hidden sm:block ${at.textFaint}`}>{allUsers.length} accounts</span>
            <button onClick={toggleTheme}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${at.toggleBtn}`}
              title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}>
              {isDark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
            <button onClick={fetchData} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition ${at.filterInactive}`}>
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* ── Freshness banner ── */}
        <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border text-xs ${at.freshnessBg}`}>
          <span className={`inline-block w-2 h-2 rounded-full ${at.freshnessDot} animate-pulse`} />
          <span className={at.textSec}>{freshnessLabel()} · {allUsers.length} accounts loaded</span>
          <span className={`ml-auto ${at.textFaint}`}>
            {loadedAt ? `${loadedAt.toLocaleDateString()} ${loadedAt.toLocaleTimeString()}` : ""}
          </span>
        </div>

        {/* ── Domain context strip ── */}
        {adminDomain === "customer" && (
          <div className={`flex items-center gap-3 px-4 py-2 rounded-xl border text-xs ${at.subCard}`}>
            <Users className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
            <span className={at.textSec}>
              Customer Operations — salon accounts, usage adoption, retention &amp; billing
            </span>
            <button
              onClick={() => setDomainTab("data", "imports")}
              className={`ml-auto flex items-center gap-1 text-violet-400 hover:text-violet-300 whitespace-nowrap`}
            >
              <ShieldCheck className="w-3 h-3" />Usage Import Quality
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}
        {adminDomain === "data" && (
          <div className={`flex items-center gap-3 px-4 py-2 rounded-xl border text-xs ${at.subCard}`}>
            <Database className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
            <span className={at.textSec}>
              Data Intelligence — canonical product truth, catalog, imports, review &amp; AI analysis
            </span>
            <button
              onClick={() => setDomainTab("customer", "cohorts")}
              className={`ml-auto flex items-center gap-1 text-blue-400 hover:text-blue-300 whitespace-nowrap`}
            >
              <Users className="w-3 h-3" />Customer Insights
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════ */}
        {/*  TAB: USAGE COHORTS (primary/central)                        */}
        {/* ══════════════════════════════════════════════════════════════ */}
        {activeTab === "cohorts" && (
          <>
            {/* ── Cohort Summary Cards ── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              {(Object.keys(BUCKET_CONFIG) as UsageBucket[]).map((bucket) => {
                const cfg = BUCKET_CONFIG[bucket];
                const count = cohortCounts[bucket];
                const isActive = cohortBucket === bucket;
                const color = isDark ? cfg.colorDark : cfg.colorLight;
                const bg = isDark ? cfg.bgDark : cfg.bgLight;
                return (
                  <button key={bucket} onClick={() => setCohortBucket(isActive ? "all" : bucket)}
                    className={`relative overflow-hidden rounded-2xl border p-3 text-left transition-all group ${
                      isActive ? `${bg} ring-2 ${cfg.ring}` : `${at.card} ${at.cardHover}`
                    }`}>
                    <p className={`text-2xl font-bold tracking-tight ${isActive ? color : at.textPrimary}`}>{count}</p>
                    <p className={`text-[11px] font-medium mt-0.5 ${isActive ? color : at.textMuted}`}>{cfg.label}</p>
                    <p className={`text-[10px] mt-0.5 ${at.textFaint}`}>{cfg.sublabel}</p>
                  </button>
                );
              })}
            </div>

            {/* ── Search & Country Filter ── */}
            <div className="space-y-3">
              <div className={`flex flex-wrap items-center gap-2 rounded-2xl border p-2 ${at.card}`}>
                <button
                  onClick={() => setSystemFilter("all")}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition ${
                    systemFilter === "all" ? at.tagActive : at.tagInactive
                  }`}
                >
                  All <span className="ml-1 opacity-70">{allUsers.length}</span>
                </button>
                {SYSTEM_FILTER_ORDER.map((category) => {
                  const config = SYSTEM_CATEGORY_CONFIG[category];
                  return (
                    <button
                      key={category}
                      onClick={() => setSystemFilter(systemFilter === category ? "all" : category)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition ${
                        systemFilter === category ? config.color : at.tagInactive
                      }`}
                    >
                      {config.label} <span className="ml-1 opacity-70">{systemCounts[category]}</span>
                    </button>
                  );
                })}
                <span className={`ml-auto px-2 text-[10px] ${at.textFaint}`}>Usage metrics come from the latest report</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${at.textFaint}`} />
                  <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone, city…"
                    className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-1 transition ${at.input}`} />
                  {search && <button onClick={() => setSearch("")} className={`absolute right-3 top-1/2 -translate-y-1/2 ${at.textFaint}`}><X className="w-4 h-4" /></button>}
                </div>
                <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)}
                  className={`px-3 py-2.5 rounded-xl border text-sm focus:outline-none appearance-none cursor-pointer ${at.select}`}>
                  <option value="all" style={{ background: at.selectBg }}>All Countries</option>
                  {allCountries.map(c => <option key={c} value={c} style={{ background: at.selectBg }}>{getFlag(c)} {c}</option>)}
                </select>
                {isTableEditing ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={cancelTableEdits}
                      className={`px-4 py-2.5 rounded-xl border text-sm transition ${at.filterInactive}`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={publishTableEdits}
                      disabled={isPublishing}
                      className="px-4 py-2.5 rounded-xl border border-indigo-500 bg-indigo-500 text-white text-sm font-medium hover:bg-indigo-400 disabled:opacity-50 transition"
                    >
                      {isPublishing ? "Publishing..." : "Publish Changes"}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={startEditingTable}
                    className={`px-4 py-2.5 rounded-xl border text-sm transition ${at.filterInactive}`}
                  >
                    Edit Links
                  </button>
                )}
                {!cohortViewIsDefault && (
                  <button
                    onClick={resetCohortView}
                    className={`px-4 py-2.5 rounded-xl border text-sm transition ${at.filterInactive}`}
                  >
                    Reset All Filters
                  </button>
                )}
              </div>
              {cohortBucket !== "all" && (
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${at.textMuted}`}>Filtering:</span>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${isDark ? BUCKET_CONFIG[cohortBucket].bgDark : BUCKET_CONFIG[cohortBucket].bgLight} ${isDark ? BUCKET_CONFIG[cohortBucket].colorDark : BUCKET_CONFIG[cohortBucket].colorLight}`}>
                    {BUCKET_CONFIG[cohortBucket].label}
                    <button onClick={() => setCohortBucket("all")}><X className="w-3 h-3 ml-1" /></button>
                  </span>
                </div>
              )}
              <p className={`text-xs ${at.textFaint}`}>
                Showing <span className={`font-medium ${at.textSec}`}>{cohortFiltered.length}</span> of {allUsers.length} accounts
              </p>
              {tableSavedAt && (
                <p className="text-xs text-emerald-500">Changes published</p>
              )}
            </div>

            {/* ── Cohort Table ── */}
            <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
              <div className="overflow-x-auto overflow-y-visible" onWheel={handleTableWheel}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${at.border}`}>
                      {([
                        { field: "salon_name" as CohortSortField, label: "Salon",         w: "min-w-[200px]" },
                        { field: "system_category" as CohortSortField, label: "System",   w: "min-w-[120px]" },
                        { field: "state" as CohortSortField,      label: "Country",       w: "min-w-[110px]" },
                        { field: "city" as CohortSortField,        label: "City",          w: "min-w-[100px]" },
                        { field: "days_inactive" as CohortSortField, label: "Last Mix",   w: "min-w-[110px]" },
                        { field: "first_mix_date" as CohortSortField, label: "First Mix", w: "min-w-[110px]" },
                        { field: "profiles" as CohortSortField,    label: "Profiles",     w: "min-w-[80px]" },
                        { field: "total_mixes" as CohortSortField,  label: "Total Mixes", w: "min-w-[100px]" },
                        { field: "version" as CohortSortField,      label: "Version",     w: "min-w-[80px]" },
                      ]).map(({ field, label, w }) => (
                        <th key={field} onClick={() => handleCohortSort(field)}
                          className={`${w} px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium cursor-pointer transition select-none ${at.textFaint}`}>
                          <span className="flex items-center gap-1.5">{label} <SortIcon active={cohortSortField === field} dir={cohortSortDir} /></span>
                        </th>
                      ))}
                      <th className={`min-w-[130px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Summit</th>
                      <th className={`min-w-[130px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Instagram</th>
                      <th className={`min-w-[90px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Cohort</th>
                    </tr>
                  </thead>
                  <tbody className={`${at.rowDivide} divide-y`}>
                    {cohortPaged.map(user => {
                      const bucket = user.cohort_bucket;
                      const bcfg = BUCKET_CONFIG[bucket];
                      const bucketColor = isDark ? bcfg.colorDark : bcfg.colorLight;
                      const bucketBg = isDark ? bcfg.bgDark : bcfg.bgLight;
                      const systemConfig = SYSTEM_CATEGORY_CONFIG[user.system_category];
                      const hasLegacyUsage = user.system_category !== "salonos" && user.system_category !== "test";
                      const mx = getMixStats(user.phone_number);
                      const editSummit = pendingEdits[user.id]?.summit ?? user.summit ?? "";
                      const editInstagram = pendingEdits[user.id]?.instagram ?? user.instagram ?? "";
                      const isEditing = isTableEditing;
                      const summitHref = getExternalHref(user.summit ?? "", "summit");
                      const instagramHref = getExternalHref(user.instagram ?? "", "instagram");
                      return (
                        <tr key={user.id} className={`transition ${at.rowHover}`}>
                          <td className="px-4 py-3">
                            <span className={`font-medium block truncate max-w-[210px] ${at.text90}`}>{user.salon_name}</span>
                            <span className={`text-[11px] font-mono ${at.textFaint}`}>{user.phone_number}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium border ${systemConfig.color}`}
                              title={user.salonos_slug ? `SalonOS: ${user.salonos_slug}` : "Legacy system"}
                            >
                              {systemConfig.label}
                            </span>
                            {user.system_category === "migrated" && (
                              <span className={`block mt-1 text-[9px] ${at.textFaint}`}>Moved to SalonOS</span>
                            )}
                            {user.system_category === "legacy" && user.legacy_roster_status === "absent" && (
                              <span className="block mt-1 text-[9px] text-amber-500">Missing from latest export</span>
                            )}
                          </td>
                          <td className={`px-4 py-3 text-xs ${at.textSec}`}>
                            {user.normalized_country
                              ? <span className="flex items-center gap-1.5"><span>{getFlag(user.normalized_country)}</span>{user.normalized_country}</span>
                              : <span className={at.textDim}>--</span>}
                          </td>
                          <td className={`px-4 py-3 text-xs ${at.textSec}`}>{user.city || <span className={at.textDim}>--</span>}</td>
                          <td className="px-4 py-3">
                            {!hasLegacyUsage ? (
                              <span className={`text-xs ${at.textDim}`}>No legacy data</span>
                            ) : <span className={`text-xs font-medium ${
                              user.days_inactive === null ? at.textFaint
                              : user.days_inactive <= 14 ? "text-emerald-500"
                              : user.days_inactive <= 30 ? "text-blue-500"
                              : user.days_inactive <= 90 ? "text-sky-500"
                              : user.days_inactive <= 180 ? "text-amber-500"
                              : user.days_inactive <= 365 ? "text-orange-500"
                              : "text-red-500"
                            }`}>
                              {user.last_mix_date === "-" || !user.last_mix_date ? "Never" : user.last_mix_date}
                            </span>}
                          </td>
                          <td className={`px-4 py-3 text-xs ${at.textFaint}`}>
                            {!hasLegacyUsage || user.first_mix_date === "-" || !user.first_mix_date ? <span className={at.textDim}>--</span> : user.first_mix_date}
                          </td>
                          <td className="px-4 py-3">
                            {hasLegacyUsage ? <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold ${
                              user.profiles === 0 ? (isDark ? "bg-white/5 text-white/40" : "bg-gray-100 text-gray-400")
                              : user.profiles >= 5 ? "bg-indigo-500/20 text-indigo-500"
                              : isDark ? "bg-white/[0.06] text-white/60" : "bg-gray-100 text-gray-600"
                            }`}>{user.profiles}</span> : <span className={at.textDim}>--</span>}
                          </td>
                          {/* Total Mixes from market intelligence */}
                          <td className="px-4 py-3">
                            {mx ? (
                              <div>
                                <span className={`text-sm font-semibold ${at.textPrimary}`}>{mx.totalMixes.toLocaleString()}</span>
                                <span className={`block text-[10px] ${at.textFaint}`}>
                                  {mx.monthsActive > 0
                                    ? `~${Math.round(mx.totalMixes / mx.monthsActive).toLocaleString()}/mo`
                                    : "—"}
                                  {" · "}{mx.monthsActive}mo
                                </span>
                              </div>
                            ) : (
                              <span className={`text-xs ${at.textDim}`}>--</span>
                            )}
                          </td>
                          {/* App version */}
                          <td className="px-4 py-3">
                            {user.version ? <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium ${
                              user.version === "1021" || user.version === "1022" ? "bg-emerald-500/10 text-emerald-600"
                              : user.version >= "1020" ? "bg-blue-500/10 text-blue-600"
                              : user.version >= "1017" ? "bg-amber-500/10 text-amber-600"
                              : isDark ? "bg-white/5 text-white/50" : "bg-gray-100 text-gray-500"
                            }`}>v{user.version}</span> : <span className={at.textDim}>--</span>}
                          </td>
                          {/* Summit */}
                          <td className="px-3 py-2.5">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editSummit}
                                onChange={e => handleEditChange(user.id, "summit", e.target.value)}
                                placeholder="Summit…"
                                className={`w-full text-xs rounded-lg px-2 py-1.5 border transition focus:outline-none focus:ring-1 focus:ring-indigo-400/50 ${
                                  isDark
                                    ? "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 focus:border-indigo-500/40"
                                    : "bg-black/[0.03] border-black/[0.08] text-[#1A1A1A] placeholder:text-[#BBBBBB] focus:border-indigo-400/60"
                                }`}
                              />
                            ) : summitHref ? (
                              <a
                                href={summitHref}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-indigo-500 hover:text-indigo-400 underline underline-offset-2 break-all"
                              >
                                {user.summit}
                              </a>
                            ) : (
                              <span className={`text-xs ${at.textDim}`}>--</span>
                            )}
                          </td>
                          {/* Instagram */}
                          <td className="px-3 py-2.5">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editInstagram}
                                onChange={e => handleEditChange(user.id, "instagram", e.target.value)}
                                placeholder="@handle…"
                                className={`w-full text-xs rounded-lg px-2 py-1.5 border transition focus:outline-none focus:ring-1 focus:ring-pink-400/50 ${
                                  isDark
                                    ? "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/25 focus:border-pink-500/40"
                                    : "bg-black/[0.03] border-black/[0.08] text-[#1A1A1A] placeholder:text-[#BBBBBB] focus:border-pink-400/60"
                                }`}
                              />
                            ) : instagramHref ? (
                              <a
                                href={instagramHref}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-pink-500 hover:text-pink-400 underline underline-offset-2 break-all"
                              >
                                {user.instagram}
                              </a>
                            ) : (
                              <span className={`text-xs ${at.textDim}`}>--</span>
                            )}
                          </td>
                          {/* Cohort badge */}
                          <td className="px-4 py-3">
                            {hasLegacyUsage ? <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium border ${bucketBg} ${bucketColor}`}>
                              {bcfg.label}
                            </span> : <span className={`text-[10px] ${at.textDim}`}>--</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {cohortPaged.length === 0 && (
                      <tr><td colSpan={12} className={`py-10 text-center text-sm ${at.textFaint}`}>No users match the current filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination page={cohortPage} totalPages={cohortTotalPages} setPage={setCohortPage} isDark={isDark} />
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════ */}
        {/*  TAB: OVERVIEW                                               */}
        {/* ══════════════════════════════════════════════════════════════ */}
        {activeTab === "overview" && (
          <>
            {/* ── Overview KPI Cards ── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: "Total Users",    value: String(directoryCustomers.length), icon: Users,       color: "from-indigo-500 to-purple-600" },
                { label: "Total Profiles", value: String(directoryCustomers.reduce((sum, user) => sum + user.profiles, 0)), icon: Layers, color: "from-cyan-500 to-blue-600" },
                { label: "Active Users",   value: stats?.active_users || "0",        icon: Activity,    color: "from-emerald-500 to-green-600" },
                { label: "Countries",      value: String(allCountries.length),       icon: Globe,       color: "from-amber-500 to-orange-600" },
                { label: "Cities",         value: stats?.city_count || "0",          icon: MapPin,      color: "from-pink-500 to-rose-600" },
                { label: "Latest Version", value: stats?.latest_version_count || "0",icon: Smartphone,  color: "from-violet-500 to-fuchsia-600" },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className={`relative overflow-hidden rounded-2xl border p-4 ${at.card} ${at.cardHover} transition group`}>
                  <div className={`absolute -top-4 -right-4 w-16 h-16 rounded-full bg-gradient-to-br ${color} opacity-10 group-hover:opacity-20 transition blur-xl`} />
                  <Icon className={`w-4 h-4 mb-2 ${at.textFaint}`} />
                  <p className={`text-2xl font-bold tracking-tight ${at.textPrimary}`}>{Number(value).toLocaleString()}</p>
                  <p className={`text-[11px] mt-0.5 ${at.textMuted}`}>{label}</p>
                </div>
              ))}
            </div>

            {/* ── Charts Row ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <CountryPieChart data={overviewCountryChartData} countryFilter={countryFilter} setCountryFilter={setCountryFilter} isDark={isDark} />
              <VersionBarChart data={byVersion} allVersions={allVersions} versionFilter={versionFilter} setVersionFilter={setVersionFilter} isDark={isDark} />
            </div>

            {/* ── Search & Filters ── */}
            <div className="space-y-3">
              <div className={`flex flex-wrap items-center gap-2 rounded-2xl border p-2 ${at.card}`}>
                <button
                  onClick={() => setSystemFilter("all")}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition ${
                    systemFilter === "all" ? at.tagActive : at.tagInactive
                  }`}
                >
                  All <span className="ml-1 opacity-70">{allUsers.length}</span>
                </button>
                {SYSTEM_FILTER_ORDER.map((category) => {
                  const config = SYSTEM_CATEGORY_CONFIG[category];
                  return (
                    <button
                      key={category}
                      onClick={() => setSystemFilter(systemFilter === category ? "all" : category)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition ${
                        systemFilter === category ? config.color : at.tagInactive
                      }`}
                    >
                      {config.label} <span className="ml-1 opacity-70">{systemCounts[category]}</span>
                    </button>
                  );
                })}
                <span className={`ml-auto px-2 text-[10px] ${at.textFaint}`}>Test accounts are excluded from KPIs</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${at.textFaint}`} />
                  <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone, city…"
                    className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-1 transition ${at.input}`} />
                  {search && <button onClick={() => setSearch("")} className={`absolute right-3 top-1/2 -translate-y-1/2 ${at.textFaint}`}><X className="w-4 h-4" /></button>}
                </div>
                <button onClick={() => setShowFilters(f => !f)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm transition ${
                    showFilters || systemFilter !== "all" || countryFilter !== "all" || versionFilter !== "all" || profileFilter !== "all" ? at.filterActive : at.filterInactive
                  }`}>
                  <Filter className="w-4 h-4" /> Filters
                  {(systemFilter !== "all" || countryFilter !== "all" || versionFilter !== "all" || profileFilter !== "all") && (
                    <span className="w-5 h-5 rounded-full bg-indigo-500 text-white text-[10px] flex items-center justify-center font-bold">
                      {[systemFilter !== "all", countryFilter !== "all", versionFilter !== "all", profileFilter !== "all"].filter(Boolean).length}
                    </span>
                  )}
                </button>
              </div>
              {showFilters && (
                <div className={`flex flex-wrap gap-3 p-4 rounded-xl border ${at.filterPanel}`}>
                  <div className="space-y-1">
                    <label className={`text-[10px] uppercase tracking-wider font-medium ${at.textFaint}`}>Country</label>
                    <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)}
                      className={`block w-44 px-3 py-2 rounded-lg border text-sm focus:outline-none appearance-none cursor-pointer ${at.select}`}>
                      <option value="all" style={{ background: at.selectBg }}>All Countries</option>
                      {allCountries.map(c => <option key={c} value={c} style={{ background: at.selectBg }}>{getFlag(c)} {c}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className={`text-[10px] uppercase tracking-wider font-medium ${at.textFaint}`}>Version</label>
                    <select value={versionFilter} onChange={e => setVersionFilter(e.target.value)}
                      className={`block w-36 px-3 py-2 rounded-lg border text-sm focus:outline-none appearance-none cursor-pointer ${at.select}`}>
                      <option value="all" style={{ background: at.selectBg }}>All Versions</option>
                      {allVersions.map(v => <option key={v} value={v} style={{ background: at.selectBg }}>v{v}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className={`text-[10px] uppercase tracking-wider font-medium ${at.textFaint}`}>Profiles</label>
                    <select value={profileFilter} onChange={e => setProfileFilter(e.target.value)}
                      className={`block w-40 px-3 py-2 rounded-lg border text-sm focus:outline-none appearance-none cursor-pointer ${at.select}`}>
                      <option value="all" style={{ background: at.selectBg }}>All Profiles</option>
                      <option value="active" style={{ background: at.selectBg }}>Has Profiles (&gt;0)</option>
                      <option value="multi" style={{ background: at.selectBg }}>Multi-Profile (&gt;1)</option>
                      <option value="zero" style={{ background: at.selectBg }}>Zero Profiles</option>
                    </select>
                  </div>
                  {(systemFilter !== "all" || countryFilter !== "all" || versionFilter !== "all" || profileFilter !== "all") && (
                    <div className="flex items-end">
                      <button onClick={() => { setSystemFilter("all"); setCountryFilter("all"); setVersionFilter("all"); setProfileFilter("all"); }} className="px-3 py-2 rounded-lg text-sm text-red-500 hover:bg-red-500/10 transition">Clear All</button>
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between">
                <p className={`text-xs ${at.textFaint}`}>
                  Showing <span className={`font-medium ${at.textSec}`}>{ovFiltered.length}</span> of {allUsers.length} accounts
                  {countryFilter !== "all" && <span className="text-indigo-500 ml-1">in {countryFilter}</span>}
                </p>
              </div>
            </div>

            {/* ── Overview Table ── */}
            <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${at.border}`}>
                      {([
                        { field: "salon_name" as OverviewSortField, label: "Salon Name",  w: "min-w-[200px]" },
                        { field: "phone_number" as OverviewSortField, label: "Phone",     w: "min-w-[130px]" },
                        { field: "system_category" as OverviewSortField, label: "System", w: "min-w-[120px]" },
                        { field: "profiles" as OverviewSortField, label: "Profiles",      w: "min-w-[80px]" },
                        { field: "first_mix_date" as OverviewSortField, label: "First Mix", w: "min-w-[110px]" },
                        { field: "last_mix_date" as OverviewSortField, label: "Last Mix", w: "min-w-[110px]" },
                        { field: "version" as OverviewSortField, label: "Version",        w: "min-w-[80px]" },
                        { field: "state" as OverviewSortField, label: "Country",          w: "min-w-[120px]" },
                        { field: "city" as OverviewSortField, label: "City",              w: "min-w-[120px]" },
                      ]).map(({ field, label, w }) => (
                        <th key={field} onClick={() => handleOvSort(field)}
                          className={`${w} px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium cursor-pointer transition select-none ${at.textFaint}`}>
                          <span className="flex items-center gap-1.5">{label} <SortIcon active={ovSortField === field} dir={ovSortDir} /></span>
                        </th>
                      ))}
                      <th className={`min-w-[110px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Summit</th>
                      <th className={`min-w-[110px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Instagram</th>
                    </tr>
                  </thead>
                  <tbody className={`${at.rowDivide} divide-y`}>
                    {ovPaged.map(user => {
                      const systemConfig = SYSTEM_CATEGORY_CONFIG[user.system_category];
                      return (
                      <tr key={user.id} className={`transition group ${at.rowHover}`}>
                        <td className="px-4 py-3"><span className={`font-medium group-hover:${at.textPrimary} transition ${at.text90}`}>{user.salon_name}</span></td>
                        <td className={`px-4 py-3 font-mono text-xs ${at.textFaint}`}>{user.phone_number}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium border ${systemConfig.color}`}
                            title={user.salonos_slug ? `SalonOS: ${user.salonos_slug}` : "Legacy system"}
                          >
                            {systemConfig.label}
                          </span>
                          {user.system_category === "migrated" && (
                            <span className={`block mt-1 text-[9px] ${at.textFaint}`}>Moved to SalonOS</span>
                          )}
                          {user.system_category === "legacy" && user.legacy_roster_status === "absent" && (
                            <span className="block mt-1 text-[9px] text-amber-500">Missing from latest export</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold ${
                            user.profiles === 0 ? (isDark ? "bg-white/5 text-white/50" : "bg-gray-100 text-gray-400")
                            : user.profiles >= 5 ? "bg-indigo-500/20 text-indigo-500"
                            : isDark ? "bg-white/[0.06] text-white/60" : "bg-gray-100 text-gray-600"
                          }`}>{user.profiles}</span>
                        </td>
                        <td className={`px-4 py-3 text-xs ${at.textMuted}`}>{user.first_mix_date === "-" ? <span className={at.textDim}>--</span> : user.first_mix_date}</td>
                        <td className={`px-4 py-3 text-xs ${at.textMuted}`}>
                          {user.last_mix_date === "-" ? <span className={at.textDim}>--</span> : (
                            <span className={
                              user.days_inactive !== null && user.days_inactive <= 1 ? "text-emerald-500"
                              : user.days_inactive !== null && user.days_inactive <= 7 ? "text-blue-500"
                              : user.days_inactive !== null && user.days_inactive <= 14 ? "text-amber-500"
                              : ""
                            }>{user.last_mix_date}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {user.version ? <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium ${
                            user.version === "1021" || user.version === "1022" ? "bg-emerald-500/10 text-emerald-600"
                            : user.version >= "1020" ? "bg-blue-500/10 text-blue-600"
                            : user.version >= "1017" ? "bg-amber-500/10 text-amber-600"
                            : isDark ? "bg-white/5 text-white/50" : "bg-gray-100 text-gray-500"
                          }`}>v{user.version}</span> : <span className={at.textDim}>--</span>}
                        </td>
                        <td className={`px-4 py-3 text-xs ${at.textMuted}`}>
                          {user.normalized_country
                            ? <span className="flex items-center gap-1.5"><span>{getFlag(user.normalized_country)}</span>{user.normalized_country}</span>
                            : <span className={at.textDim}>--</span>}
                        </td>
                        <td className={`px-4 py-3 text-xs ${at.textMuted}`}>{user.city || <span className={at.textDim}>--</span>}</td>
                        <td className={`px-4 py-3 text-xs ${at.textFaint}`}>
                          {user.summit ? <span className="text-indigo-500">{user.summit}</span> : <span className={at.textDim}>--</span>}
                        </td>
                        <td className={`px-4 py-3 text-xs ${at.textFaint}`}>
                          {user.instagram ? <span className="text-pink-500">{user.instagram}</span> : <span className={at.textDim}>--</span>}
                        </td>
                      </tr>
                      );
                    })}
                    {ovPaged.length === 0 && (
                      <tr><td colSpan={11} className={`py-10 text-center text-sm ${at.textFaint}`}>No accounts match the current filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination page={ovPage} totalPages={ovTotalPages} setPage={setOvPage} isDark={isDark} />
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════ */}
        {/*  TAB: CUSTOMER SUCCESS                                       */}
        {/* ══════════════════════════════════════════════════════════════ */}
        {activeTab === "success" && (
          <>
            {/* ── Status KPI Command Bar ── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {([
                { key: "active" as StatusFilter,   label: "Active This Week", value: kpis.active,   icon: Activity,    gradient: "from-emerald-500 to-green-600",  ring: "ring-emerald-500/30" },
                { key: "at_risk" as StatusFilter,  label: "At Risk (7–14d)",  value: kpis.atRisk,   icon: AlertTriangle,gradient: "from-amber-500 to-yellow-600",  ring: "ring-amber-500/30" },
                { key: "critical" as StatusFilter, label: "Critical (14d+)",  value: kpis.critical, icon: ShieldAlert, gradient: "from-red-500 to-rose-600",        ring: "ring-red-500/30" },
                { key: "recovered" as StatusFilter,label: "Recovered",        value: kpis.recovered,icon: UserCheck,   gradient: "from-blue-500 to-cyan-600",       ring: "ring-blue-500/30" },
                { key: "churned" as StatusFilter,  label: "Churned (30d+)",   value: kpis.churned,  icon: TrendingDown,gradient: "from-gray-500 to-slate-600",      ring: "ring-gray-500/30" },
              ]).map(({ key, label, value, icon: Icon, gradient, ring }) => (
                <button key={key} onClick={() => setStatusFilter(statusFilter === key ? "all" : key)}
                  className={`relative overflow-hidden rounded-2xl border p-4 text-left transition-all group ${
                    statusFilter === key ? `${at.card} ring-2 ${ring}` : `${at.card} ${at.cardHover}`
                  }`}>
                  <div className={`absolute -top-4 -right-4 w-16 h-16 rounded-full bg-gradient-to-br ${gradient} opacity-10 group-hover:opacity-20 transition blur-xl`} />
                  <Icon className={`w-4 h-4 mb-2 ${at.textFaint}`} />
                  <p className={`text-2xl font-bold tracking-tight ${at.textPrimary}`}>{value}</p>
                  <p className={`text-[11px] mt-0.5 ${at.textMuted}`}>{label}</p>
                </button>
              ))}
            </div>

            {/* ── Quick Toggles ── */}
            <div className="flex flex-wrap items-center gap-2">
              {([
                { key: "at_risk" as const, label: "Only At Risk", icon: AlertTriangle },
                { key: "new" as const, label: "Only New", icon: Star },
                { key: "high_potential" as const, label: "High Potential (5+)", icon: Zap },
              ]).map(({ key, label, icon: Icon }) => (
                <button key={key} onClick={() => setQuickToggle(quickToggle === key ? "" : key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition border ${quickToggle === key ? at.tagActive : at.tagInactive}`}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
              {(statusFilter !== "all" || quickToggle) && (
                <button onClick={() => { setStatusFilter("all"); setQuickToggle(""); }} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs text-red-500 hover:bg-red-500/10 transition">
                  <X className="w-3.5 h-3.5" /> Clear
                </button>
              )}
            </div>

            {/* ── Attention Today ── */}
            {attentionList.length > 0 && (
              <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
                <div className={`px-5 py-4 border-b ${at.border} flex items-center justify-between`}>
                  <h3 className={`text-sm font-medium flex items-center gap-2 ${at.textSec}`}>
                    <Eye className="w-4 h-4 text-amber-400" /> Attention Today
                    <span className={`font-normal ${at.textFaint}`}>Top 10 to act on</span>
                  </h3>
                </div>
                <div className={`${at.rowDivide} divide-y`}>
                  {attentionList.map((u, i) => {
                    const ac = ACTION_CONFIG[u.cs_action]; const AcIcon = ac.icon;
                    return (
                      <div key={u.id} className={`flex items-center gap-4 px-5 py-3 transition ${at.rowHover}`}>
                        <span className={`text-xs font-mono w-5 text-right ${at.textDim}`}>{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${at.text90}`}>{u.salon_name}</p>
                          <p className={`text-[11px] truncate ${at.textFaint}`}>{u.normalized_country ? `${getFlag(u.normalized_country)} ${u.normalized_country}` : ""}{u.city ? ` / ${u.city}` : ""}</p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className={`text-xs ${at.textFaint}`}>{u.days_inactive !== null ? `${u.days_inactive}d inactive` : "Never mixed"}</span>
                          <HealthBadge score={u.health.score} status={u.health.status} />
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium ${ac.bg} ${ac.color} transition`}><AcIcon className="w-3 h-3" /> {ac.label}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Charts ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <CountryPieChart data={countryChartData} countryFilter={countryFilter} setCountryFilter={setCountryFilter} isDark={isDark} />
              <VersionBarChart data={byVersion} allVersions={allVersions} versionFilter={versionFilter} setVersionFilter={setVersionFilter} isDark={isDark} />
            </div>

            {/* ── CS Search & Filters ── */}
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${at.textFaint}`} />
                  <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone, city…"
                    className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-1 transition ${at.input}`} />
                  {search && <button onClick={() => setSearch("")} className={`absolute right-3 top-1/2 -translate-y-1/2 ${at.textFaint}`}><X className="w-4 h-4" /></button>}
                </div>
                <button onClick={() => setShowFilters(f => !f)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm transition ${showFilters ? at.filterActive : at.filterInactive}`}>
                  <Filter className="w-4 h-4" /> Filters
                </button>
              </div>
              {showFilters && (
                <div className={`flex flex-wrap gap-3 p-4 rounded-xl border ${at.filterPanel}`}>
                  <div className="space-y-1">
                    <label className={`text-[10px] uppercase tracking-wider font-medium ${at.textFaint}`}>Country</label>
                    <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)}
                      className={`block w-44 px-3 py-2 rounded-lg border text-sm focus:outline-none appearance-none cursor-pointer ${at.select}`}>
                      <option value="all" style={{ background: at.selectBg }}>All Countries</option>
                      {allCountries.map(c => <option key={c} value={c} style={{ background: at.selectBg }}>{c}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className={`text-[10px] uppercase tracking-wider font-medium ${at.textFaint}`}>Version</label>
                    <select value={versionFilter} onChange={e => setVersionFilter(e.target.value)}
                      className={`block w-36 px-3 py-2 rounded-lg border text-sm focus:outline-none appearance-none cursor-pointer ${at.select}`}>
                      <option value="all" style={{ background: at.selectBg }}>All Versions</option>
                      {allVersions.map(v => <option key={v} value={v} style={{ background: at.selectBg }}>v{v}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className={`text-[10px] uppercase tracking-wider font-medium ${at.textFaint}`}>Lifecycle</label>
                    <select value={lifecycleFilter} onChange={e => setLifecycleFilter(e.target.value)}
                      className={`block w-40 px-3 py-2 rounded-lg border text-sm focus:outline-none appearance-none cursor-pointer ${at.select}`}>
                      <option value="all" style={{ background: at.selectBg }}>All Stages</option>
                      {(Object.entries(LIFECYCLE_CONFIG) as [Lifecycle, typeof LIFECYCLE_CONFIG[Lifecycle]][]).map(([k, v]) => (
                        <option key={k} value={k} style={{ background: at.selectBg }}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button onClick={() => { setCountryFilter("all"); setVersionFilter("all"); setLifecycleFilter("all"); setStatusFilter("all"); setQuickToggle(""); }}
                      className="px-3 py-2 rounded-lg text-sm text-red-500 hover:bg-red-500/10 transition">Clear All</button>
                  </div>
                </div>
              )}
              <p className={`text-xs ${at.textFaint}`}>Showing <span className={`font-medium ${at.textSec}`}>{csFiltered.length}</span> of {users.length} customers</p>
            </div>

            {/* ── CS Customer Table ── */}
            <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${at.border}`}>
                      {([
                        { field: "salon_name" as CSSortField, label: "Salon",    w: "min-w-[180px]" },
                        { field: "profiles" as CSSortField,   label: "Profiles", w: "min-w-[70px]" },
                        { field: "days_inactive" as CSSortField, label: "Inactive", w: "min-w-[80px]" },
                        { field: "health_score" as CSSortField, label: "Health",  w: "min-w-[70px]" },
                      ]).map(({ field, label, w }) => (
                        <th key={field} onClick={() => handleCsSort(field)}
                          className={`${w} px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium cursor-pointer transition select-none ${at.textFaint}`}>
                          <span className="flex items-center gap-1.5">{label} <SortIcon active={csSortField === field} dir={csSortDir} /></span>
                        </th>
                      ))}
                      <th className={`min-w-[90px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Lifecycle</th>
                      <th className={`min-w-[120px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Risk Tags</th>
                      <th className={`min-w-[100px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium cursor-pointer select-none ${at.textFaint}`} onClick={() => handleCsSort("state")}>
                        <span className="flex items-center gap-1.5">Country <SortIcon active={csSortField === "state"} dir={csSortDir} /></span>
                      </th>
                      <th className={`min-w-[140px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Action</th>
                      <th className="w-10 px-2 py-3" />
                    </tr>
                  </thead>
                  <tbody className={`${at.rowDivide} divide-y`}>
                    {csPaged.map(user => {
                      const ac = ACTION_CONFIG[user.cs_action]; const AcIcon = ac.icon;
                      const lc = LIFECYCLE_CONFIG[user.lifecycle];
                      const isExpanded = expandedId === user.id;
                      return (
                        <React.Fragment key={user.id}>
                          <tr className={`transition group ${at.rowHover}`}>
                            <td className="px-4 py-3">
                              <span className={`font-medium block truncate max-w-[200px] ${at.text90}`}>{user.salon_name}</span>
                              <span className={`text-[11px] font-mono ${at.textFaint}`}>{user.phone_number}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold ${
                                user.profiles === 0 ? (isDark ? "bg-white/5 text-white/50" : "bg-gray-100 text-gray-400")
                                : user.profiles >= 5 ? "bg-indigo-500/20 text-indigo-500"
                                : isDark ? "bg-white/[0.06] text-white/60" : "bg-gray-100 text-gray-600"
                              }`}>{user.profiles}</span>
                            </td>
                            <td className="px-4 py-3">
                              {user.days_inactive !== null ? (
                                <span className={`text-xs font-medium ${
                                  user.days_inactive <= 1 ? "text-emerald-500"
                                  : user.days_inactive <= 7 ? "text-emerald-500/70"
                                  : user.days_inactive <= 14 ? "text-amber-500"
                                  : user.days_inactive <= 30 ? "text-red-500/80"
                                  : at.textFaint
                                }`}>{user.days_inactive}d</span>
                              ) : <span className={`text-xs ${at.textDim}`}>Never</span>}
                            </td>
                            <td className="px-4 py-3"><HealthBadge score={user.health.score} status={user.health.status} /></td>
                            <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium border ${lc.bg} ${lc.color}`}>{lc.label}</span></td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {user.risk_tags.length > 0 ? user.risk_tags.map(t => (
                                  <span key={t} className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${RISK_TAG_CONFIG[t].color}`}>{RISK_TAG_CONFIG[t].label}</span>
                                )) : <span className={`text-[10px] ${at.textDim}`}>--</span>}
                              </div>
                            </td>
                            <td className={`px-4 py-3 text-xs ${at.textMuted}`}>
                              {user.normalized_country
                                ? <span className="flex items-center gap-1"><span>{getFlag(user.normalized_country)}</span>{user.normalized_country}</span>
                                : <span className={at.textDim}>--</span>}
                            </td>
                            <td className="px-4 py-3">
                              <button className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium ${ac.bg} ${ac.color} transition`}><AcIcon className="w-3 h-3" /> {ac.label}</button>
                            </td>
                            <td className="px-2 py-3">
                              <button onClick={() => setExpandedId(isExpanded ? null : user.id)} className={`transition ${at.textFaint}`}>
                                <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className={at.expandRow}>
                              <td colSpan={9} className="px-6 py-4">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                                  <div className="space-y-2">
                                    <p className={`text-[10px] uppercase tracking-wider font-medium ${at.textFaint}`}>Health Details</p>
                                    <p className={at.textMuted}>Score: <span className={`font-bold ${at.textPrimary}`}>{user.health.score}</span>/100</p>
                                    {user.health.factors.map((f, idx) => (
                                      <p key={idx} className={`${at.textMuted} flex items-center gap-1.5`}><AlertTriangle className="w-3 h-3 text-amber-400/60" /> {f}</p>
                                    ))}
                                  </div>
                                  <div className="space-y-2">
                                    <p className={`text-[10px] uppercase tracking-wider font-medium ${at.textFaint}`}>Timeline</p>
                                    <p className={at.textFaint}>First Mix: <span className={at.textSec}>{user.first_mix_date}</span></p>
                                    <p className={at.textFaint}>Last Mix: <span className={at.textSec}>{user.last_mix_date}</span></p>
                                    <p className={at.textFaint}>Tenure: <span className={at.textSec}>{user.tenure_days !== null ? `${user.tenure_days} days` : "--"}</span></p>
                                  </div>
                                  <div className="space-y-2">
                                    <p className={`text-[10px] uppercase tracking-wider font-medium ${at.textFaint}`}>Details</p>
                                    <p className={at.textFaint}>Version: <span className={at.textSec}>v{user.version}</span></p>
                                    <p className={at.textFaint}>City: <span className={at.textSec}>{user.city || "--"}</span></p>
                                    <p className={at.textFaint}>Summit: <span className="text-indigo-500">{user.summit || "--"}</span></p>
                                    <p className={at.textFaint}>Instagram: <span className="text-pink-500">{user.instagram || "--"}</span></p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination page={csPage} totalPages={csTotalPages} setPage={setCsPage} isDark={isDark} />
            </div>

            {/* ── Insights & Systemic Patterns ── */}
            <div className={`rounded-2xl border p-5 space-y-4 ${at.card}`}>
              <h3 className={`text-sm font-medium flex items-center gap-2 ${at.textSec}`}><Layers className="w-4 h-4 text-indigo-400" />Insights &amp; Systemic Patterns</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { value: `${insights.churnedPct}%`, label: "of churned users had only 1 profile", sub: `${insights.churnedSingleProfile} of ${insights.totalChurned} churned` },
                  { value: `${insights.noFirstMixPct}%`, label: "of all users never did a first mix", sub: `${insights.noFirstMix} users with no first mix` },
                  { value: String(insights.powerUsers), label: "Power Users (5+ profiles, active)", sub: `Avg profiles: ${insights.avgProfiles}`, color: "text-emerald-500" },
                  { value: String(insights.recoveredCount), label: "Recovered customers", sub: "Retention win", color: "text-blue-500" },
                ].map(({ value, label, sub, color }) => (
                  <div key={label} className={`rounded-xl border p-4 space-y-1 ${at.subCard}`}>
                    <p className={`text-2xl font-bold ${color || at.textPrimary}`}>{value}</p>
                    <p className={`text-[11px] ${at.textMuted}`}>{label}</p>
                    <p className={`text-[10px] ${at.textFaint}`}>{sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Success Opportunities ── */}
            <div className={`rounded-2xl border p-5 space-y-3 ${at.card}`}>
              <h3 className={`text-sm font-medium flex items-center gap-2 ${at.textSec}`}><Star className="w-4 h-4 text-amber-400" />Success Opportunities</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl bg-gradient-to-br from-violet-500/5 to-indigo-500/5 border border-violet-500/10 p-4">
                  <p className="text-xs font-medium text-violet-500 mb-1">Training Opportunity</p>
                  <p className={`text-[11px] ${at.textMuted}`}>{users.filter(u => u.profiles >= 3 && u.days_inactive !== null && u.days_inactive > 14).length} users with 3+ profiles but low activity.</p>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-emerald-500/5 to-green-500/5 border border-emerald-500/10 p-4">
                  <p className="text-xs font-medium text-emerald-500 mb-1">Advocacy Candidates</p>
                  <p className={`text-[11px] ${at.textMuted}`}>{users.filter(u => u.lifecycle === "power_user").length} power users could be brand advocates.</p>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-blue-500/5 to-cyan-500/5 border border-blue-500/10 p-4">
                  <p className="text-xs font-medium text-blue-500 mb-1">Recovery Wins</p>
                  <p className={`text-[11px] ${at.textMuted}`}>{insights.recoveredCount} customers were recovered. Replicate the approach.</p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════ */}
        {/*  TAB: RETENTION / CHURN INTELLIGENCE                        */}
        {/* ══════════════════════════════════════════════════════════════ */}
        {activeTab === "retention" && (
          <>
            {/* ── Sub-tab toggle: status overview vs churn reasons ── */}
            <div className={`inline-flex items-center gap-1 p-1 rounded-xl border ${at.tabWrap}`}>
              {([
                { id: "overview" as const,       label: "Retention Overview", icon: Heart },
                { id: "churn_reasons" as const,  label: "Churn Reasons",      icon: ShieldAlert },
              ]).map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => setRetentionView(id)}
                  className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition ${retentionView === id ? at.tabActive : `${at.textMuted} hover:${at.textPrimary}`}`}>
                  <Icon className="w-4 h-4" /> {label}
                  {id === "churn_reasons" && <span className={`text-[11px] ${at.textFaint}`}>{churnKpis.total}</span>}
                </button>
              ))}
            </div>

            {retentionView === "overview" && (
            <>
            {/* ── KPI Cards (positive first, then risk) ── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {([
                { label: "Healthy / Growing", value: retentionKpis.healthyGrowing,  color: "text-emerald-500", grad: "from-emerald-500 to-green-600",  sub: "stable or rising" },
                { label: "Recovered",          value: retentionKpis.recovered,      color: "text-cyan-500",    grad: "from-cyan-500 to-sky-600",       sub: "re-engaged" },
                { label: "Active This Week",   value: retentionKpis.activeThisWeek, color: "text-green-500",   grad: "from-green-500 to-emerald-600",  sub: "mixed ≤ 7d" },
                { label: "At Risk · Value",    value: retentionKpis.atRiskHighValue,color: "text-amber-500",   grad: "from-amber-500 to-yellow-600",   sub: "salvageable" },
                { label: "Declining Usage",    value: retentionKpis.declining,      color: "text-orange-500",  grad: "from-orange-500 to-amber-600",   sub: "trending down" },
                { label: "Churned Power",      value: retentionKpis.churnedPower,   color: "text-red-500",     grad: "from-red-500 to-rose-600",       sub: "30+ mixes, gone" },
                { label: "Never Activated",    value: retentionKpis.neverActivated, color: "text-gray-400",    grad: "from-gray-500 to-slate-600",     sub: "<5 mixes ever" },
                { label: "Est. Mixes Lost/mo", value: retentionKpis.mixesLost,      color: "text-rose-500",    grad: "from-rose-500 to-pink-600",      sub: "declining+churned" },
              ]).map(({ label, value, color, grad, sub }) => (
                <div key={label} className={`relative overflow-hidden rounded-2xl border p-4 ${at.card} ${at.cardHover} transition group`}>
                  <div className={`absolute -top-4 -right-4 w-16 h-16 rounded-full bg-gradient-to-br ${grad} opacity-10 group-hover:opacity-20 transition blur-xl`} />
                  <p className={`text-2xl font-bold tracking-tight ${color}`}>{value.toLocaleString()}</p>
                  <p className={`text-[11px] mt-0.5 ${at.textMuted}`}>{label}</p>
                  <p className={`text-[10px] mt-0.5 ${at.textFaint}`}>{sub}</p>
                </div>
              ))}
            </div>

            {/* ── Charts: status pie + value-weighted inactivity ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className={`rounded-2xl border p-5 ${at.card}`}>
                <h3 className={`text-sm font-medium mb-3 flex items-center gap-2 ${at.textSec}`}><Heart className="w-4 h-4 text-rose-400" /> Retention Status Mix</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={retentionPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}
                      onClick={(d: any) => d?.status && setRetentionFilter(prev => prev === d.status ? "all" : d.status)}>
                      {retentionPieData.map((d) => (
                        <Cell key={d.status} fill={RETENTION_CONFIG[d.status].pie} stroke={retentionFilter === d.status ? "#fff" : "transparent"} strokeWidth={2} className="cursor-pointer" />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: isDark ? "#15151f" : "#fff", border: "1px solid rgba(127,127,127,0.2)", borderRadius: 12, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2 mt-2">
                  {retentionPieData.map(d => (
                    <button key={d.status} onClick={() => setRetentionFilter(prev => prev === d.status ? "all" : d.status)}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] border transition ${retentionFilter === d.status ? RETENTION_CONFIG[d.status].bg : at.tagInactive}`}>
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: RETENTION_CONFIG[d.status].pie }} />
                      {RETENTION_CONFIG[d.status].label} <span className={at.textFaint}>{d.value}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className={`rounded-2xl border p-5 ${at.card}`}>
                <h3 className={`text-sm font-medium mb-3 flex items-center gap-2 ${at.textSec}`}><AlertTriangle className="w-4 h-4 text-amber-400" /> Inactivity Cohorts · Mixes at Stake</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={retentionInactivityBars}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: isDark ? "rgba(255,255,255,0.5)" : "#888" }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: isDark ? "rgba(255,255,255,0.5)" : "#888" }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: isDark ? "rgba(255,255,255,0.5)" : "#888" }} />
                    <Tooltip contentStyle={{ background: isDark ? "#15151f" : "#fff", border: "1px solid rgba(127,127,127,0.2)", borderRadius: 12, fontSize: 12 }} />
                    <Bar yAxisId="left" dataKey="customers" name="Customers" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="mixesAtRisk" name="Mixes at risk" fill="#f97316" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ── Monthly mix trend ── */}
            {retentionMonthlyTrend.length > 0 && (
              <div className={`rounded-2xl border p-5 ${at.card}`}>
                <h3 className={`text-sm font-medium mb-3 flex items-center gap-2 ${at.textSec}`}><TrendingDown className="w-4 h-4 text-indigo-400" /> Total Mixes by Month <span className={`font-normal ${at.textFaint}`}>(last 12 months)</span></h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={retentionMonthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: isDark ? "rgba(255,255,255,0.5)" : "#888" }} />
                    <YAxis tick={{ fontSize: 11, fill: isDark ? "rgba(255,255,255,0.5)" : "#888" }} />
                    <Tooltip contentStyle={{ background: isDark ? "#15151f" : "#fff", border: "1px solid rgba(127,127,127,0.2)", borderRadius: 12, fontSize: 12 }} />
                    <Bar dataKey="mixes" name="Mixes" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* ── Attention: high-value customers at risk ── */}
            {retentionAttentionList.length > 0 && (
              <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
                <div className={`px-5 py-4 border-b ${at.border} flex items-center justify-between`}>
                  <h3 className={`text-sm font-medium flex items-center gap-2 ${at.textSec}`}>
                    <ShieldAlert className="w-4 h-4 text-red-400" /> High-Value Customers at Risk
                    <span className={`font-normal ${at.textFaint}`}>Ranked by value × risk</span>
                  </h3>
                </div>
                <div className={`${at.rowDivide} divide-y`}>
                  {retentionAttentionList.map((u, i) => {
                    const cfg = RETENTION_CONFIG[u.retention.status];
                    return (
                      <div key={u.id} className={`flex items-center gap-4 px-5 py-3 transition ${at.rowHover}`}>
                        <span className={`text-xs font-mono w-5 text-right ${at.textDim}`}>{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${at.text90}`}>{u.salon_name}</p>
                          <p className={`text-[11px] truncate ${at.textFaint}`}>{u.normalized_country ? `${getFlag(u.normalized_country)} ${u.normalized_country}` : ""}{u.city ? ` / ${u.city}` : ""}</p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className={`text-xs ${at.textSec}`}>{u.retention.totalMixes.toLocaleString()} mixes</span>
                          {u.retention.trendPct !== null && (
                            <span className={`text-[11px] font-medium ${u.retention.trendPct < 0 ? "text-orange-500" : "text-emerald-500"}`}>
                              {u.retention.trendPct > 0 ? "+" : ""}{u.retention.trendPct}%
                            </span>
                          )}
                          <span className={`text-xs ${at.textFaint}`}>{u.days_inactive !== null ? `${u.days_inactive}d inactive` : "never"}</span>
                          <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium border ${cfg.bg} ${cfg.color}`}>{cfg.short}</span>
                          <span className={`inline-flex items-center justify-center w-10 h-7 rounded-lg text-xs font-bold border ${at.border} ${at.textPrimary}`}>{u.retention.priorityScore}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Search + Filters ── */}
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${at.textFaint}`} />
                  <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone, city…"
                    className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-1 transition ${at.input}`} />
                  {search && <button onClick={() => setSearch("")} className={`absolute right-3 top-1/2 -translate-y-1/2 ${at.textFaint}`}><X className="w-4 h-4" /></button>}
                </div>
                <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)}
                  className={`px-3 py-2.5 rounded-xl border text-sm focus:outline-none appearance-none cursor-pointer ${at.select}`}>
                  <option value="all" style={{ background: at.selectBg }}>All Countries</option>
                  {allCountries.map(c => <option key={c} value={c} style={{ background: at.selectBg }}>{getFlag(c)} {c}</option>)}
                </select>
              </div>
              {/* Status segment chips */}
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => setRetentionFilter("all")}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${retentionFilter === "all" ? at.tagActive : at.tagInactive}`}>
                  All <span className={at.textFaint}>{users.length}</span>
                </button>
                {(Object.keys(RETENTION_CONFIG) as RetentionStatus[]).map(status => (
                  <button key={status} onClick={() => setRetentionFilter(prev => prev === status ? "all" : status)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${retentionFilter === status ? RETENTION_CONFIG[status].bg + " " + RETENTION_CONFIG[status].color : at.tagInactive}`}>
                    <span className="w-2 h-2 rounded-full" style={{ background: RETENTION_CONFIG[status].pie }} />
                    {RETENTION_CONFIG[status].label} <span className={at.textFaint}>{retentionStatusCounts[status]}</span>
                  </button>
                ))}
              </div>
              <p className={`text-xs ${at.textFaint}`}>Showing <span className={`font-medium ${at.textSec}`}>{retentionFiltered.length}</span> of {users.length} customers</p>
            </div>

            {/* ── Retention Table ── */}
            <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
              <div className="overflow-x-auto" onWheel={handleTableWheel}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${at.border}`}>
                      {([
                        { field: "salon_name" as RetentionSortField,    label: "Salon",        w: "min-w-[200px]" },
                        { field: "total_mixes" as RetentionSortField,   label: "Total Mixes",  w: "min-w-[120px]" },
                        { field: "trend" as RetentionSortField,         label: "Trend",        w: "min-w-[90px]" },
                        { field: "days_inactive" as RetentionSortField, label: "Last Mix",     w: "min-w-[100px]" },
                        { field: "profiles" as RetentionSortField,      label: "Profiles",     w: "min-w-[80px]" },
                        { field: "value_score" as RetentionSortField,   label: "Value",        w: "min-w-[80px]" },
                        { field: "priority_score" as RetentionSortField,label: "Priority",     w: "min-w-[90px]" },
                      ]).map(({ field, label, w }) => (
                        <th key={field} onClick={() => handleRetentionSort(field)}
                          className={`${w} px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium cursor-pointer transition select-none ${at.textFaint}`}>
                          <span className="flex items-center gap-1.5">{label} <SortIcon active={retentionSortField === field} dir={retentionSortDir} /></span>
                        </th>
                      ))}
                      <th className={`min-w-[120px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Status</th>
                      <th className={`min-w-[110px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Country</th>
                    </tr>
                  </thead>
                  <tbody className={`${at.rowDivide} divide-y`}>
                    {retentionPaged.map(user => {
                      const r = user.retention;
                      const cfg = RETENTION_CONFIG[r.status];
                      return (
                        <tr key={user.id} className={`transition ${at.rowHover}`}>
                          <td className="px-4 py-3">
                            <span className={`font-medium block truncate max-w-[210px] ${at.text90}`}>{user.salon_name}</span>
                            <span className={`text-[11px] font-mono ${at.textFaint}`}>{user.phone_number}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm font-semibold ${at.textPrimary}`}>{r.totalMixes.toLocaleString()}</span>
                            <span className={`block text-[10px] ${at.textFaint}`}>
                              {r.monthsActive > 0 ? `${Math.round(r.monthlyMixRate).toLocaleString()}/mo · ${r.monthsActive}mo` : "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {r.trendPct === null ? <span className={at.textDim}>--</span> : (
                              <span className={`inline-flex items-center gap-1 text-xs font-medium ${r.trendPct <= -20 ? "text-orange-500" : r.trendPct >= 20 ? "text-emerald-500" : at.textSec}`}>
                                {r.trendPct <= -20 ? <ArrowDown className="w-3 h-3" /> : r.trendPct >= 20 ? <ArrowUp className="w-3 h-3" /> : null}
                                {r.trendPct > 0 ? "+" : ""}{r.trendPct}%
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-medium ${
                              user.days_inactive === null ? at.textFaint
                              : user.days_inactive <= 14 ? "text-emerald-500"
                              : user.days_inactive <= 30 ? "text-blue-500"
                              : user.days_inactive <= 90 ? "text-amber-500"
                              : user.days_inactive <= 180 ? "text-orange-500"
                              : "text-red-500"
                            }`}>
                              {user.last_mix_date === "-" || !user.last_mix_date ? "Never" : user.last_mix_date}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold ${
                              user.profiles === 0 ? (isDark ? "bg-white/5 text-white/40" : "bg-gray-100 text-gray-400")
                              : user.profiles >= 5 ? "bg-indigo-500/20 text-indigo-500"
                              : isDark ? "bg-white/[0.06] text-white/60" : "bg-gray-100 text-gray-600"
                            }`}>{user.profiles}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-medium ${at.textSec}`}>{r.valueScore}</span>
                            {r.isPower && <span className="ml-1 inline-flex items-center text-[9px] font-bold text-violet-500">★</span>}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center justify-center min-w-[2.5rem] h-7 px-2 rounded-lg text-xs font-bold border ${
                              r.priorityScore >= 60 ? "bg-red-500/15 text-red-500 border-red-500/20"
                              : r.priorityScore >= 30 ? "bg-amber-500/15 text-amber-500 border-amber-500/20"
                              : `${at.border} ${at.textMuted}`
                            }`}>{r.priorityScore}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium border ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                          </td>
                          <td className={`px-4 py-3 text-xs ${at.textSec}`}>
                            {user.normalized_country
                              ? <span className="flex items-center gap-1.5"><span>{getFlag(user.normalized_country)}</span>{user.normalized_country}</span>
                              : <span className={at.textDim}>--</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {retentionPaged.length === 0 && (
                      <tr><td colSpan={9} className={`py-10 text-center text-sm ${at.textFaint}`}>No customers match the current filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination page={retentionPage} totalPages={retentionTotalPages} setPage={setRetentionPage} isDark={isDark} />
            </div>
            </>
            )}

            {retentionView === "churn_reasons" && (
            <>
              {/* ── Churn KPI cards ── */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {([
                  { label: "Churned Customers", value: churnKpis.total.toLocaleString(),       color: "text-red-500",     grad: "from-red-500 to-rose-600",     sub: "in current filter" },
                  { label: "Reason Assigned",   value: churnKpis.assigned.toLocaleString(),    color: "text-emerald-500", grad: "from-emerald-500 to-green-600", sub: `${churnKpis.coveragePct}% coverage` },
                  { label: "Needs Reason",      value: churnKpis.unassigned.toLocaleString(),  color: "text-amber-500",   grad: "from-amber-500 to-yellow-600",  sub: "unassigned" },
                  { label: "Top Reason",        value: churnKpis.topReason,                    color: "text-orange-500",  grad: "from-orange-500 to-amber-600",  sub: `${churnKpis.topReasonCount} customers`, small: true },
                  { label: "Mixes Lost",        value: churnKpis.lostMixes.toLocaleString(),   color: "text-rose-500",    grad: "from-rose-500 to-pink-600",     sub: "lifetime, churned" },
                ]).map(({ label, value, color, grad, sub, small }) => (
                  <div key={label} className={`relative overflow-hidden rounded-2xl border p-4 ${at.card} ${at.cardHover} transition group`}>
                    <div className={`absolute -top-4 -right-4 w-16 h-16 rounded-full bg-gradient-to-br ${grad} opacity-10 group-hover:opacity-20 transition blur-xl`} />
                    <p className={`${small ? "text-sm leading-tight pt-1" : "text-2xl"} font-bold tracking-tight ${color}`}>{value}</p>
                    <p className={`text-[11px] mt-0.5 ${at.textMuted}`}>{label}</p>
                    <p className={`text-[10px] mt-0.5 ${at.textFaint}`}>{sub}</p>
                  </div>
                ))}
              </div>

              {/* ── Charts: reason pie + ranked bars ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className={`rounded-2xl border p-5 ${at.card}`}>
                  <h3 className={`text-sm font-medium mb-3 flex items-center gap-2 ${at.textSec}`}><ShieldAlert className="w-4 h-4 text-red-400" /> Churn Reason Breakdown</h3>
                  {churnPieData.length === 0 ? (
                    <div className={`h-[260px] flex items-center justify-center text-sm ${at.textFaint}`}>No churned customers in this filter.</div>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie data={churnPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}
                            onClick={(d: any) => d?.id && setChurnReasonFilter(prev => prev === d.id ? "all" : d.id)}>
                            {churnPieData.map((d) => (
                              <Cell key={d.id} fill={d.pie} stroke={churnReasonFilter === d.id ? "#fff" : "transparent"} strokeWidth={2} className="cursor-pointer" />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ background: isDark ? "#15151f" : "#fff", border: "1px solid rgba(127,127,127,0.2)", borderRadius: 12, fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {churnPieData.map(d => (
                          <button key={d.id} onClick={() => setChurnReasonFilter(prev => prev === d.id ? "all" : d.id)}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] border transition ${churnReasonFilter === d.id ? (isDark ? "bg-white/10 border-white/20 text-white" : "bg-black/5 border-black/15 text-black") : at.tagInactive}`}>
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.pie }} />
                            {d.name} <span className={at.textFaint}>{d.value}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <div className={`rounded-2xl border p-5 ${at.card}`}>
                  <h3 className={`text-sm font-medium mb-3 flex items-center gap-2 ${at.textSec}`}><TrendingDown className="w-4 h-4 text-orange-400" /> Reasons Ranked</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={churnBarData} layout="vertical" margin={{ left: 8, right: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"} horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: isDark ? "rgba(255,255,255,0.5)" : "#888" }} />
                      <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: isDark ? "rgba(255,255,255,0.5)" : "#888" }} />
                      <Tooltip cursor={{ fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" }} contentStyle={{ background: isDark ? "#15151f" : "#fff", border: "1px solid rgba(127,127,127,0.2)", borderRadius: 12, fontSize: 12 }} />
                      <Bar dataKey="value" name="Customers" radius={[0, 4, 4, 0]}>
                        {churnBarData.map(d => <Cell key={d.id} fill={d.pie} className="cursor-pointer" onClick={() => setChurnReasonFilter(prev => prev === d.id ? "all" : d.id)} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* ── Search + reason filter chips ── */}
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${at.textFaint}`} />
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search churned salons by name, phone, city…"
                      className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-1 transition ${at.input}`} />
                    {search && <button onClick={() => setSearch("")} className={`absolute right-3 top-1/2 -translate-y-1/2 ${at.textFaint}`}><X className="w-4 h-4" /></button>}
                  </div>
                  <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)}
                    className={`px-3 py-2.5 rounded-xl border text-sm focus:outline-none appearance-none cursor-pointer ${at.select}`}>
                    <option value="all" style={{ background: at.selectBg }}>All Countries</option>
                    {allCountries.map(c => <option key={c} value={c} style={{ background: at.selectBg }}>{getFlag(c)} {c}</option>)}
                  </select>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => setChurnReasonFilter("all")}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${churnReasonFilter === "all" ? at.tagActive : at.tagInactive}`}>
                    All <span className={at.textFaint}>{churnKpis.total}</span>
                  </button>
                  {CHURN_REASONS.map(r => (
                    <button key={r.id} onClick={() => setChurnReasonFilter(prev => prev === r.id ? "all" : r.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${churnReasonFilter === r.id ? r.bg + " " + r.color : at.tagInactive}`}>
                      <span className="w-2 h-2 rounded-full" style={{ background: r.pie }} />
                      {r.label} <span className={at.textFaint}>{churnReasonCounts.counts[r.id]}</span>
                    </button>
                  ))}
                  <button onClick={() => setChurnReasonFilter(prev => prev === "unassigned" ? "all" : "unassigned")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${churnReasonFilter === "unassigned" ? (isDark ? "bg-white/10 border-white/20 text-white" : "bg-black/5 border-black/15 text-black") : at.tagInactive}`}>
                    <span className="w-2 h-2 rounded-full" style={{ background: CHURN_UNASSIGNED_COLOR }} />
                    Unassigned <span className={at.textFaint}>{churnReasonCounts.unassigned}</span>
                  </button>
                </div>
                <p className={`text-xs ${at.textFaint}`}>Showing <span className={`font-medium ${at.textSec}`}>{churnFiltered.length}</span> of {churnKpis.total} churned customers</p>
              </div>

              {/* ── Churn table with per-customer reason dropdown ── */}
              <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
                <div className="overflow-x-auto" onWheel={handleTableWheel}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${at.border}`}>
                        <th className={`min-w-[210px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Salon</th>
                        <th className={`min-w-[120px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Total Mixes</th>
                        <th className={`min-w-[90px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Trend</th>
                        <th className={`min-w-[100px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Last Mix</th>
                        <th className={`min-w-[80px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Value</th>
                        <th className={`min-w-[110px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Country</th>
                        <th className={`min-w-[240px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Churn Reason</th>
                      </tr>
                    </thead>
                    <tbody className={`${at.rowDivide} divide-y`}>
                      {churnPaged.map(user => {
                        const r = user.retention;
                        const currentReason = (user.churn_reason || "").trim();
                        const rdef = currentReason ? CHURN_REASON_MAP[currentReason] : null;
                        return (
                          <tr key={user.id} className={`transition ${at.rowHover}`}>
                            <td className="px-4 py-3">
                              <span className={`font-medium block truncate max-w-[210px] ${at.text90}`}>{user.salon_name}</span>
                              <span className={`text-[11px] font-mono ${at.textFaint}`}>{user.phone_number}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-sm font-semibold ${at.textPrimary}`}>{r.totalMixes.toLocaleString()}</span>
                              <span className={`block text-[10px] ${at.textFaint}`}>{r.monthsActive > 0 ? `${Math.round(r.monthlyMixRate).toLocaleString()}/mo · ${r.monthsActive}mo` : "—"}</span>
                            </td>
                            <td className="px-4 py-3">
                              {r.trendPct === null ? <span className={at.textDim}>--</span> : (
                                <span className={`inline-flex items-center gap-1 text-xs font-medium ${r.trendPct <= -20 ? "text-orange-500" : r.trendPct >= 20 ? "text-emerald-500" : at.textSec}`}>
                                  {r.trendPct <= -20 ? <ArrowDown className="w-3 h-3" /> : r.trendPct >= 20 ? <ArrowUp className="w-3 h-3" /> : null}
                                  {r.trendPct > 0 ? "+" : ""}{r.trendPct}%
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs font-medium text-red-500">{user.last_mix_date === "-" || !user.last_mix_date ? "Never" : user.last_mix_date}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-medium ${at.textSec}`}>{r.valueScore}</span>
                              {r.isPower && <span className="ml-1 inline-flex items-center text-[9px] font-bold text-violet-500">★</span>}
                            </td>
                            <td className={`px-4 py-3 text-xs ${at.textSec}`}>
                              {user.normalized_country
                                ? <span className="flex items-center gap-1.5"><span>{getFlag(user.normalized_country)}</span>{user.normalized_country}</span>
                                : <span className={at.textDim}>--</span>}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {rdef && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: rdef.pie }} />}
                                <select
                                  value={currentReason}
                                  disabled={savingChurnId === user.id}
                                  onChange={e => setChurnReason(user.id, e.target.value)}
                                  className={`w-full max-w-[220px] text-xs rounded-lg px-2.5 py-1.5 border transition focus:outline-none focus:ring-1 focus:ring-indigo-400/50 cursor-pointer ${at.select} ${currentReason ? "" : at.textFaint}`}>
                                  <option value="" style={{ background: at.selectBg }}>— Select reason —</option>
                                  {CHURN_REASONS.map(opt => (
                                    <option key={opt.id} value={opt.id} style={{ background: at.selectBg }}>{opt.label}</option>
                                  ))}
                                </select>
                                {savingChurnId === user.id && <span className={`text-[10px] ${at.textFaint}`}>Saving…</span>}
                                {churnSaveError === user.id && <span className="text-[10px] text-red-500">Save failed</span>}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {churnPaged.length === 0 && (
                        <tr><td colSpan={7} className={`py-10 text-center text-sm ${at.textFaint}`}>No churned customers match the current filters.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination page={churnPage} totalPages={churnTotalPages} setPage={setChurnPage} isDark={isDark} />
              </div>
            </>
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════ */}
        {/*  TAB: BILLING / SUMMIT CROSS-REFERENCE                      */}
        {/* ══════════════════════════════════════════════════════════════ */}
        {activeTab === "billing" && (() => {
          const payingNotUsing  = billingPayingNotUsing;
          const usingNotPaying  = billingUsingNotPaying;
          const needsMapping    = billingNeedsMapping;
          const withoutAutopay  = billingWithoutActiveAutopay;
          const visibleWithoutAutopay = billingWithoutActiveAutopayFiltered;
          const equipmentOnly   = billingEquipmentOnly;
          const stoppedSubs     = billingStoppedSubs;
          const confirmedDebtMonths = billingConfirmedDebts.reduce(
            (sum, debt) => sum + debt.missedMonths,
            0,
          );
          const currentAnnualSubscriptions = SUMMIT_BILLING_RECONCILIATION.filter(
            (reconciliation) =>
              reconciliation.confirmed &&
              reconciliation.status === "annual_subscription" &&
              monthLabelKey(SUMMIT_DATA.latestMonth) <= monthLabelKey(reconciliation.paidThroughMonth),
          );

          const activeSubs = SUMMIT_DATA.customers.filter(isSummitActivePayer).length;
          const internalRevenue = SUMMIT_DATA.customers
            .filter(isSummitInternalCustomer)
            .reduce((sum, customer) => sum + customer.ltv, 0);
          const ltv = Math.round(
            SUMMIT_DATA.customers
              .filter((customer) => !isSummitInternalCustomer(customer))
              .reduce((sum, customer) => sum + customer.ltv, 0),
          );

          const PAYMENT_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
            subscription: { label: "Subscription", color: "text-emerald-400" },
            equipment:    { label: "Equipment",    color: "text-blue-400" },
            both:         { label: "Sub+Equip",    color: "text-violet-400" },
            never:        { label: "Never paid",   color: "text-gray-400" },
          };
          const NO_AUTOPAY_CONFIG: Record<BillingNoAutopayCategory, { label: string; color: string }> = {
            no_subscription:      { label: "No Subscription", color: "bg-red-500/10 text-red-400 border-red-500/20" },
            no_active_payment_method: { label: "No Active Payment Method", color: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
            stopped:              { label: "Billing Stopped", color: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
            payment_issue:        { label: "Payment Issue", color: "bg-red-500/10 text-red-400 border-red-500/20" },
            annual_due:           { label: "Annual Renewal Due", color: "bg-rose-500/10 text-rose-400 border-rose-500/20" },
            annual_covered:       { label: "Annual Covered", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
            equipment_or_one_time:{ label: "Equipment / One-Time", color: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
            needs_mapping:        { label: "Needs Mapping", color: "bg-sky-500/10 text-sky-400 border-sky-500/20" },
            distributor_covered:  { label: "Distributor Covered", color: "bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/20" },
            identity_review:      { label: "Identity Review", color: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
          };

          return (
            <div className="space-y-5">
              {/* Summary cards row 1 */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-10 gap-3">
                {[
                  { label: "Summit Customers", value: SUMMIT_DATA.summary.total, color: "text-violet-400", sub: null },
                  { label: "Active Subscribers", value: activeSubs, color: "text-emerald-400", sub: SUMMIT_DATA.months.slice(-2).join(" + ") },
                  { label: "Annual Subscribers", value: currentAnnualSubscriptions.length, color: "text-teal-400", sub: currentAnnualSubscriptions.length ? `paid through ${currentAnnualSubscriptions[0].paidThroughMonth}` : null },
                  { label: "Stopped Sub", value: stoppedSubs.length, color: "text-amber-400", sub: "after reconciliations" },
                  { label: "Equipment Only", value: SUMMIT_DATA.summary.equipmentOnly, color: "text-blue-400", sub: "no subscription" },
                  { label: "Paid This Month · Inactive", value: payingNotUsing.length, color: "text-orange-400", sub: `${SUMMIT_DATA.latestMonth || "latest"} · no usage 30d+` },
                  { label: "Active · Not Paying", value: usingNotPaying.length, color: "text-red-400", sub: "matched billing evidence" },
                  { label: "No Active Autopay", value: withoutAutopay.length, color: "text-fuchsia-400", sub: "active use · no verified monthly charge" },
                  { label: "Est. Confirmed Debt", value: `₪${billingConfirmedDebtTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, color: "text-rose-400", sub: `${billingConfirmedDebts.length} confirmed issues · ${confirmedDebtMonths} monthly billing gaps` },
                  { label: "Needs Mapping", value: needsMapping.length, color: "text-sky-400", sub: "not counted as debt" },
                ].map(({ label, value, color, sub }) => (
                  <div key={label} className={`rounded-2xl border p-4 ${at.card}`}>
                    <p className={`text-2xl font-bold ${color}`}>{value}</p>
                    <p className={`text-[11px] mt-0.5 ${at.textMuted}`}>{label}</p>
                    {sub && <p className={`text-[10px] mt-0.5 ${at.textFaint}`}>{sub}</p>}
                  </div>
                ))}
              </div>

              {/* LTV banner */}
              <div className={`rounded-xl border px-5 py-3 flex items-center gap-3 ${at.freshnessBg}`}>
                <span className={`text-xs ${at.textMuted}`}>All-time Company Revenue (LTV)</span>
                <span className={`text-base font-bold text-emerald-400`}>₪{ltv ? ltv.toLocaleString() : "—"}</span>
                <span className={`text-[10px] ${at.textFaint} ml-auto`}>
                  SUMIT net ₪{SUMMIT_DATA.summary.totalLTV.toLocaleString()} · internal excluded ₪{internalRevenue.toLocaleString(undefined, { maximumFractionDigits: 2 })} · {SUMMIT_DATA._source}
                </span>
              </div>

              {/* Sub-tab toggle */}
              <div className={`flex flex-wrap gap-1 rounded-xl p-1 border ${at.tabWrap} w-fit`}>
                {([
                  { id: "summit_customers",  label: "Summit Customers",      count: summitPaidCustomers.length, emoji: "📒" },
                  { id: "without_autopay",   label: "Active · No Active Autopay", count: withoutAutopay.length, emoji: "📋" },
                  { id: "paying_not_using",  label: "Paid This Month · Inactive", count: payingNotUsing.length, emoji: "💳" },
                  { id: "using_not_paying",  label: "Active · Not Paying",   count: usingNotPaying.length,  emoji: "⚡" },
                  { id: "needs_mapping",     label: "Active · Needs Mapping", count: needsMapping.length,   emoji: "🔗" },
                  { id: "stopped",           label: "Stopped Subscription",  count: stoppedSubs.length,     emoji: "⏸" },
                  { id: "equipment",         label: "Equipment / Install",    count: equipmentOnly.length,   emoji: "🔧" },
                ] as { id: typeof billingView; label: string; count: number; emoji: string }[]).map(({ id, label, count, emoji }) => (
                  <button key={id} onClick={() => setBillingView(id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition whitespace-nowrap ${billingView === id ? at.tabActive : at.tabInactive}`}>
                    {emoji} {label} ({count})
                  </button>
                ))}
              </div>

              {/* ── VIEW: Summit Customers (Summit-only, no Spectra cross-reference) ── */}
              {billingView === "summit_customers" && (
                <div className="space-y-4">
                  {/* Summit-only KPI cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {[
                      { label: "Paid Customers",      value: summitKpis.paidCount.toLocaleString(),                                  color: "text-violet-400",  sub: `${summitDistributorCustomers.length} distributor excluded${summitExcluded.size > 0 ? ` · ${summitExcluded.size} manual` : ""}` },
                      { label: "Customer LTV",        value: `${SUMMIT_CURRENCY_SYMBOL}${Math.round(summitKpis.totalLTV).toLocaleString()}`, color: "text-emerald-400", sub: "All-time customer revenue" },
                      { label: "Average LTV",         value: `${SUMMIT_CURRENCY_SYMBOL}${summitKpis.avgLtv.toLocaleString()}`,           color: "text-emerald-300", sub: "Per paid customer" },
                      { label: "Avg Net Paid Month",  value: `${SUMMIT_CURRENCY_SYMBOL}${summitKpis.avgPaidMonth.toLocaleString()}`,      color: "text-sky-400",     sub: "Net revenue / paid month" },
                      { label: "Avg Months Paid",     value: summitKpis.avgPaidMonths.toFixed(1),                                     color: "text-sky-300",     sub: "Months per customer" },
                      { label: "Distributor Revenue", value: `${SUMMIT_CURRENCY_SYMBOL}${Math.round(summitDistributorRevenue).toLocaleString()}`, color: "text-fuchsia-400", sub: `Portugal · ${summitDistributorLicenses} annual licenses` },
                      { label: "Active Payers",       value: summitKpis.activePayers.toLocaleString(),                                color: "text-emerald-400", sub: `Last 2mo · ${summitKpis.latestMonth || "—"}` },
                      { label: "Churned",             value: summitKpis.churned.toLocaleString(),                                     color: "text-red-400",     sub: "No payment 3mo+" },
                      { label: "New (latest)",        value: summitKpis.newCustomers.toLocaleString(),                                color: "text-blue-400",    sub: "1st payment recently" },
                      { label: "Reactivated",         value: summitKpis.reactivated.toLocaleString(),                                 color: "text-amber-400",   sub: "Returned after gap" },
                      { label: "Revenue Retention",   value: `${Math.round(summitKpis.revenueRetention * 100)}%`,                     color: "text-violet-300",  sub: "Last 2mo vs prev 2mo" },
                      { label: "Customer Retention",  value: `${Math.round(summitKpis.customerRetention * 100)}%`,                    color: "text-indigo-300",  sub: "Last 2mo vs prev 2mo" },
                      { label: "Data Window",         value: SUMMIT_DATA.months[0] || "—",                                            color: "text-white/70",    sub: `→ ${summitKpis.latestMonth || "—"} (${SUMMIT_DATA.months.length}mo)` },
                    ].map(({ label, value, color, sub }) => (
                      <div key={label} className={`rounded-2xl border p-4 ${at.card}`}>
                        <p className={`text-xl font-bold tracking-tight ${color}`}>{value}</p>
                        <p className={`text-[11px] mt-0.5 ${at.textMuted}`}>{label}</p>
                        <p className={`text-[10px] mt-0.5 ${at.textFaint}`}>{sub}</p>
                      </div>
                    ))}
                  </div>

                  {/* Controls: search · status · min LTV · page size · reset */}
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[220px]">
                      <Search className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${at.textFaint}`} />
                      <input
                        type="text"
                        value={summitSearch}
                        onChange={(e) => { setSummitSearch(e.target.value); setSummitPage(1); }}
                        placeholder="Search Summit customer name…"
                        className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-1 transition ${at.input}`}
                      />
                      {summitSearch && (
                        <button onClick={() => { setSummitSearch(""); setSummitPage(1); }}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 ${at.textFaint}`}>
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <select
                      value={summitStatus}
                      onChange={(e) => { setSummitStatus(e.target.value as SummitStatusFilter); setSummitPage(1); }}
                      className={`px-3 py-2.5 rounded-xl border text-sm focus:outline-none appearance-none cursor-pointer ${at.select}`}
                    >
                      <option value="all"         style={{ background: at.selectBg }}>All statuses</option>
                      <option value="active"      style={{ background: at.selectBg }}>Active payers</option>
                      <option value="churned"     style={{ background: at.selectBg }}>Churned</option>
                      <option value="new"         style={{ background: at.selectBg }}>New</option>
                      <option value="reactivated" style={{ background: at.selectBg }}>Reactivated</option>
                    </select>

                    <label className={`flex items-center gap-2 text-xs ${at.textMuted}`}>
                      <span>Min LTV</span>
                      <input
                        type="number"
                        min={0}
                        step={100}
                        value={summitMinLtv || ""}
                        onChange={(e) => { setSummitMinLtv(Number(e.target.value) || 0); setSummitPage(1); }}
                        placeholder="0"
                        className={`w-24 px-2.5 py-1.5 rounded-lg border text-sm focus:outline-none focus:ring-1 transition ${at.input}`}
                      />
                    </label>

                    <label className={`flex items-center gap-2 text-xs ${at.textMuted}`}>
                      <span>Rows</span>
                      <select
                        value={summitPageSize}
                        onChange={(e) => { setSummitPageSize(Number(e.target.value)); setSummitPage(1); }}
                        className={`px-2.5 py-1.5 rounded-lg border text-sm focus:outline-none appearance-none cursor-pointer ${at.select}`}
                      >
                        <option value={25}  style={{ background: at.selectBg }}>25</option>
                        <option value={50}  style={{ background: at.selectBg }}>50</option>
                        <option value={100} style={{ background: at.selectBg }}>100</option>
                        <option value={250} style={{ background: at.selectBg }}>250</option>
                      </select>
                    </label>

                    {(summitSearch || summitStatus !== "all" || summitMinLtv > 0) && (
                      <button
                        onClick={() => { setSummitSearch(""); setSummitStatus("all"); setSummitMinLtv(0); setSummitPage(1); }}
                        className={`px-3 py-2 rounded-xl border text-xs transition ${at.filterInactive}`}
                      >
                        Reset filters
                      </button>
                    )}

                    {summitExcluded.size > 0 && (
                      <button
                        onClick={clearSummitExclusions}
                        title="Re-include every customer in KPI calculations"
                        className="px-3 py-2 rounded-xl border text-xs transition bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20"
                      >
                        Clear {summitExcluded.size} excluded
                      </button>
                    )}

                    <span className={`text-xs ${at.textFaint} ml-auto`}>
                      {summitSorted.length.toLocaleString()} of {summitPaidCustomers.length.toLocaleString()} paid customers
                      {summitExcluded.size > 0 && (
                        <span className="text-amber-400 font-medium"> · {summitExcluded.size.toLocaleString()} excluded from KPIs</span>
                      )}
                    </span>
                  </div>

                  {/* Sortable table */}
                  <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
                    <div className="overflow-x-auto" onWheel={handleTableWheel}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={`border-b ${at.border}`}>
                            <th
                              className={`w-[60px] px-3 py-3 text-center text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}
                              title="Include in KPI calculations"
                            >
                              In KPI
                            </th>
                            {([
                              { field: "name" as SummitSortField,             label: "Customer",      align: "text-left",  w: "min-w-[220px]" },
                              { field: "ltv" as SummitSortField,              label: "LTV",            align: "text-right", w: "min-w-[110px]" },
                              { field: "paidMonths" as SummitSortField,       label: "Paid Months",    align: "text-right", w: "min-w-[100px]" },
                              { field: "averagePaidMonth" as SummitSortField, label: "Avg / Mo",       align: "text-right", w: "min-w-[110px]" },
                              { field: "lastMonthAmount" as SummitSortField,  label: "Last Month",     align: "text-right", w: "min-w-[110px]" },
                              { field: "firstPaidMonth" as SummitSortField,   label: "First Paid",     align: "text-left",  w: "min-w-[100px]" },
                              { field: "lastPaidMonth" as SummitSortField,    label: "Last Paid",      align: "text-left",  w: "min-w-[100px]" },
                              { field: "churnMonths" as SummitSortField,      label: "Churn Gap",      align: "text-right", w: "min-w-[90px]" },
                              { field: "retentionRate" as SummitSortField,    label: "Retention",      align: "text-right", w: "min-w-[100px]" },
                            ]).map(({ field, label, align, w }) => (
                              <th
                                key={field}
                                onClick={() => handleSummitSort(field)}
                                className={`${w} ${align} px-4 py-3 text-[11px] uppercase tracking-wider font-medium cursor-pointer select-none transition ${at.textFaint}`}
                              >
                                <span className={`inline-flex items-center gap-1.5 ${align === "text-right" ? "justify-end w-full" : ""}`}>
                                  {label} <SortIcon active={summitSortField === field} dir={summitSortDir} />
                                </span>
                              </th>
                            ))}
                            <th className={`min-w-[90px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Currency</th>
                            <th className={`min-w-[110px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Country</th>
                            <th className={`min-w-[110px] px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint}`}>Status</th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${at.rowDivide}`}>
                          {summitPaged.map((c) => {
                            const paidMonths = c.paidMonths ?? c.activeMonths ?? 0;
                            const retentionPct = (c.retentionRate ?? 0) * 100;
                            const gap = c.churnMonths ?? 0;
                            const statusOverride = getSummitStatusOverride(c);
                            const billingReconciliation = getSummitBillingReconciliation(c);
                            const isAnnualSubscription =
                              billingReconciliation?.confirmed &&
                              billingReconciliation.status === "annual_subscription" &&
                              monthLabelKey(SUMMIT_DATA.latestMonth) <= monthLabelKey(billingReconciliation.paidThroughMonth);
                            const isAnnualRenewalDue =
                              billingReconciliation?.confirmed &&
                              billingReconciliation.status === "annual_renewal_due";
                            const hasNeverPaidSubscription =
                              billingReconciliation?.confirmed &&
                              billingReconciliation.status === "subscription_never_paid";
                            const isPendingSync = !!statusOverride;
                            const isActive = isSummitActivePayer(c);
                            const isChurned = isSummitChurned(c);
                            const isNew = !!c.newCustomer;
                            const isReact = !!c.reactivated;
                            const distributor = getSummitDistributor(c.name);
                            const isDistributor = !!distributor;
                            const isInternal = isSummitInternalCustomer(c);
                            const isExcluded = isDistributor || isInternal || summitExcluded.has(c.name);
                            const status = isInternal ? "Internal"
                              : isDistributor ? "Distributor"
                              : isAnnualSubscription ? "Active · annual"
                              : isAnnualRenewalDue ? "Annual · renewal due"
                              : hasNeverPaidSubscription ? "No subscription"
                              : isPendingSync ? "Active · pending sync"
                              : isReact ? "Reactivated"
                              : isNew ? "New"
                              : isActive ? "Active"
                              : isChurned ? "Churned"
                              : "Inactive";
                            const statusClasses = isInternal ? "bg-slate-500/10 text-slate-400 border-slate-500/20"
                              : isDistributor ? "bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/20"
                              : isAnnualSubscription ? "bg-teal-500/10 text-teal-400 border-teal-500/20"
                              : isAnnualRenewalDue ? "bg-red-500/10 text-red-400 border-red-500/20"
                              : isPendingSync ? "bg-blue-500/10 text-blue-400 border-blue-500/20"
                              : isReact ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                              : isNew ? "bg-blue-500/10 text-blue-400 border-blue-500/20"
                              : isActive ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                              : isChurned ? "bg-red-500/10 text-red-400 border-red-500/20"
                              : "bg-gray-500/10 text-gray-400 border-gray-500/20";
                            const dim = isDistributor || isInternal ? "opacity-65" : isExcluded ? "opacity-40 line-through" : "";
                            return (
                              <tr key={c.name} className={`${at.rowHover} transition ${isExcluded ? "bg-amber-500/[0.04]" : ""}`}>
                                <td className="px-3 py-3 text-center">
                                  <label
                                    className={`inline-flex items-center justify-center ${isDistributor || isInternal ? "cursor-not-allowed" : "cursor-pointer"}`}
                                    title={isInternal ? "Internal account · excluded from customer KPIs" : isDistributor ? `Distributor · excluded from customer KPIs · ${distributor.annualLicenses} annual licenses` : isExcluded ? "Excluded from KPI · click to include" : "Included in KPI · click to exclude"}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={!isExcluded}
                                      disabled={isDistributor || isInternal}
                                      onChange={() => toggleSummitExcluded(c.name)}
                                      className="w-4 h-4 rounded border-white/20 bg-white/[0.04] text-indigo-500 focus:ring-1 focus:ring-indigo-500/30 cursor-pointer accent-indigo-500"
                                    />
                                  </label>
                                </td>
                                <td className={`px-4 py-3 text-xs font-medium ${at.textPrimary} max-w-[280px] truncate ${dim}`} title={billingReconciliation?.note || statusOverride?.note || c.name}>{c.name}</td>
                                <td className={`px-4 py-3 text-xs font-semibold text-right text-emerald-400 ${dim}`}>{SUMMIT_CURRENCY_SYMBOL}{Math.round(c.ltv).toLocaleString()}</td>
                                <td className={`px-4 py-3 text-xs text-right ${at.textSec} ${dim}`}>{paidMonths}</td>
                                <td className={`px-4 py-3 text-xs text-right ${at.textSec} ${dim}`}>{c.averagePaidMonth ? `${SUMMIT_CURRENCY_SYMBOL}${Math.round(c.averagePaidMonth).toLocaleString()}` : "—"}</td>
                                <td className={`px-4 py-3 text-xs text-right ${(c.lastMonthAmount ?? 0) > 0 ? "text-emerald-400" : at.textFaint} ${dim}`}>
                                  {(c.lastMonthAmount ?? 0) > 0 ? `${SUMMIT_CURRENCY_SYMBOL}${Math.round(c.lastMonthAmount ?? 0).toLocaleString()}` : "—"}
                                </td>
                                <td className={`px-4 py-3 text-[11px] ${at.textFaint} ${dim}`}>{c.firstPaidMonth || "—"}</td>
                                <td className={`px-4 py-3 text-[11px] ${at.textFaint} ${dim}`}>{c.lastPaidMonth || "—"}</td>
                                <td className={`px-4 py-3 text-xs text-right font-medium ${isAnnualSubscription ? "text-teal-400" : isAnnualRenewalDue ? "text-red-400" : gap === 0 ? "text-emerald-400" : gap >= 3 ? "text-red-400" : "text-amber-400"} ${dim}`}>
                                  {isAnnualSubscription
                                    ? `paid → ${billingReconciliation.paidThroughMonth}`
                                    : isAnnualRenewalDue
                                      ? `due ${billingReconciliation.renewalDueMonth}`
                                    : isPendingSync
                                      ? "pending"
                                      : gap === 0 ? "0" : `${gap}mo`}
                                </td>
                                <td className={`px-4 py-3 text-xs text-right ${at.textSec} ${dim}`}>{retentionPct.toFixed(0)}%</td>
                                <td className={`px-4 py-3 text-[11px] ${at.textFaint} ${dim}`}>{c.currency || "ILS"}</td>
                                <td className={`px-4 py-3 text-[11px] ${at.textFaint} ${dim}`}>{c.country || "—"}</td>
                                <td className={`px-4 py-3 ${dim}`}>
                                  <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium border ${statusClasses}`}>{status}</span>
                                </td>
                              </tr>
                            );
                          })}
                          {summitPaged.length === 0 && (
                            <tr><td colSpan={13} className={`py-10 text-center text-sm ${at.textFaint}`}>No Summit customers match the current filters.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <Pagination page={summitPage} totalPages={summitTotalPages} setPage={setSummitPage} isDark={isDark} />
                  </div>
                </div>
              )}

              {/* ── VIEW: Active app users without a verified active standing order ── */}
              {billingView === "without_autopay" && (
                <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
                  <div className={`px-5 py-4 border-b ${at.border} space-y-3`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-sm font-semibold ${at.textPrimary}`}>Active · No Active Autopay</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-400 font-medium">{withoutAutopay.length}</span>
                      <span className={`text-[10px] ${at.textFaint}`}>
                        Last mix was fewer than 60 days ago, with no verified active monthly charge. Annual and distributor-covered accounts are labeled separately.
                      </span>
                    </div>
                    <div className="relative max-w-xl">
                      <Search className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${at.textFaint}`} />
                      <input
                        type="text"
                        value={billingNoAutopaySearch}
                        onChange={(event) => setBillingNoAutopaySearch(event.target.value)}
                        placeholder="Search salon, owner, phone or SUMIT account — e.g. Alo or Anton"
                        className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-1 transition ${at.input}`}
                      />
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${at.border}`}>
                          {["App Customer", "Phone", "Last Usage", "SUMIT Account", "Last Payment", "Classification", "Details"].map((heading) => (
                            <th key={heading} className={`px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint} whitespace-nowrap`}>{heading}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${at.rowDivide}`}>
                        {visibleWithoutAutopay.map((row) => {
                          const config = NO_AUTOPAY_CONFIG[row.category];
                          const lastPaymentIndex = row.customer?.lastPaidMonth
                            ? SUMMIT_DATA.months.indexOf(row.customer.lastPaidMonth)
                            : -1;
                          const lastPaymentAmount = lastPaymentIndex >= 0
                            ? row.customer?.monthly[lastPaymentIndex] || 0
                            : 0;
                          return (
                            <tr key={row.key} className={`${at.rowHover} transition`}>
                              <td className={`px-4 py-3 font-medium text-xs ${at.textPrimary} whitespace-nowrap`}>
                                {row.user?.salon_name || "No proven active identity"}
                              </td>
                              <td className={`px-4 py-3 text-xs font-mono ${at.textFaint}`} dir="ltr">
                                {row.user?.phone_number || "—"}
                              </td>
                              <td className={`px-4 py-3 text-xs ${at.textSec} whitespace-nowrap`}>
                                {row.user
                                  ? row.user.days_inactive !== null
                                    ? `${Math.round(row.user.days_inactive)} days`
                                    : "—"
                                  : "Unverified"}
                              </td>
                              <td className={`px-4 py-3 text-xs ${at.textSec} max-w-[220px]`}>
                                {row.customer?.sumitId
                                  ? <a
                                      href={summitCustomerUrl(row.customer.sumitId)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="hover:text-indigo-400"
                                    >
                                      {row.customer.name}
                                    </a>
                                  : row.customer?.name || "Not mapped yet"}
                              </td>
                              <td className={`px-4 py-3 text-xs ${at.textSec} whitespace-nowrap`}>
                                {row.customer?.lastPaidMonth
                                  ? `${row.customer.lastPaidMonth} · ₪${lastPaymentAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                                  : "—"}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-0.5 rounded-md border text-[10px] font-medium ${config.color}`}>
                                  {config.label}
                                </span>
                              </td>
                              <td className={`px-4 py-3 text-[10px] ${at.textFaint} min-w-[260px]`}>
                                {row.detail}
                              </td>
                            </tr>
                          );
                        })}
                        {visibleWithoutAutopay.length === 0 && (
                          <tr>
                            <td colSpan={7} className={`py-10 text-center text-sm ${at.textFaint}`}>
                              No customers match this search.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── VIEW: Paying active subscribers but not using ── */}
              {billingView === "paying_not_using" && (
                <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
                  <div className={`px-5 py-3 border-b ${at.border} flex items-center gap-2`}>
                    <span className={`text-sm font-semibold ${at.textPrimary}`}>Paid in {SUMMIT_DATA.latestMonth || "the latest month"} — no app usage for 30+ days</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 font-medium">{payingNotUsing.length}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${at.border}`}>
                          {["Summit Name", "App Match", "Phone", "Latest Charge", "LTV", "Type", "Last Paid", "Last Mix", "Status"].map(h => (
                            <th key={h} className={`px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint} whitespace-nowrap`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${at.rowDivide}`}>
                        {payingNotUsing.map(({ sc, user }) => {
                          const ptCfg = PAYMENT_TYPE_CONFIG[sc.paymentType];
                          return (
                            <tr key={sc.name} className={`${at.rowHover} transition`}>
                              <td className={`px-4 py-3 font-medium text-xs ${at.textPrimary} max-w-[160px] truncate`} title={sc.name}>{sc.name}</td>
                              <td className={`px-4 py-3 text-xs ${at.textMuted} max-w-[150px] truncate`} title={user?.salon_name}>
                                {user ? user.salon_name : <span className="text-orange-400 text-[10px] font-medium">Not found</span>}
                              </td>
                              <td className={`px-4 py-3 text-xs font-mono ${at.textFaint}`}>{user?.phone_number || "—"}</td>
                              <td className={`px-4 py-3 text-xs font-medium text-emerald-400`}>
                                {(sc.lastMonthAmount ?? 0) > 0 ? `₪${Math.round(sc.lastMonthAmount ?? 0).toLocaleString()}` : "—"}
                              </td>
                              <td className={`px-4 py-3 text-xs font-medium ${at.textSec}`}>
                                ₪{Math.round(sc.ltv || sc.total || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-[10px] font-medium ${ptCfg?.color}`}>{ptCfg?.label}</span>
                              </td>
                              <td className={`px-4 py-3 text-[10px] ${at.textFaint}`}>{sc.lastPaidMonth || "—"}</td>
                              <td className={`px-4 py-3 text-[10px] ${at.textFaint}`}>
                                {user ? (user.last_mix_date === "-" || !user.last_mix_date ? "Never" : user.last_mix_date) : "—"}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {!user
                                  ? <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-orange-500/10 text-orange-400 border border-orange-500/20">Not registered</span>
                                  : user.days_inactive === null
                                  ? <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-gray-500/10 text-gray-400 border border-gray-500/20">Never mixed</span>
                                  : <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">{user.days_inactive}d inactive</span>}
                              </td>
                            </tr>
                          );
                        })}
                        {payingNotUsing.length === 0 && (
                          <tr><td colSpan={9} className={`py-10 text-center text-sm ${at.textFaint}`}>All paying customers are active!</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── VIEW: Active app users not paying ── */}
              {billingView === "using_not_paying" && (
                <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
                  <div className={`px-5 py-3 border-b ${at.border} flex flex-wrap items-center gap-2`}>
                    <span className={`text-sm font-semibold ${at.textPrimary}`}>Active in app — no active Summit subscription</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 font-medium">{usingNotPaying.length}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 font-medium">
                      Confirmed estimated debt: ₪{billingConfirmedDebtTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${at.border}`}>
                          {["Salon", "Phone", "Country", "First Mix", "Last Mix", "Days Inactive", "Version", "Summit Account", "Billing", "Issue", "Est. Debt"].map(h => (
                            <th key={h} className={`px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint} whitespace-nowrap`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${at.rowDivide}`}>
                        {usingNotPaying.map((user) => {
                          const entries = summitMatchesByPhone.get(normalizePhone(user.phone_number)) || [];
                          const entry = [...entries].sort(
                            (a, b) => summitMonthKey(b.sc.lastPaidMonth) - summitMonthKey(a.sc.lastPaidMonth),
                          )[0];
                          const reconciliation = entry?.sc
                            ? getSummitBillingReconciliation(entry.sc)
                            : undefined;
                          const debt = entry?.sc.sumitId
                            ? billingDebtBySumitId.get(entry.sc.sumitId)
                            : undefined;
                          const debtActionUrl = debt
                            ? debt.issue.recurringBillingUrl || summitCustomerUrl(debt.issue.sumitId)
                            : "";
                          return (
                            <tr key={user.id} className={`${at.rowHover} transition`}>
                              <td className={`px-4 py-3 font-medium text-xs ${at.textPrimary} max-w-[180px] truncate`} title={user.salon_name}>{user.salon_name}</td>
                              <td className={`px-4 py-3 text-xs font-mono ${at.textFaint}`}>{user.phone_number}</td>
                              <td className={`px-4 py-3 text-xs ${at.textFaint}`}>{user.normalized_country || "—"}</td>
                              <td className={`px-4 py-3 text-[10px] ${at.textFaint}`}>{user.first_mix_date === "-" || !user.first_mix_date ? "—" : user.first_mix_date}</td>
                              <td className={`px-4 py-3 text-[10px] font-medium ${user.days_inactive !== null && user.days_inactive <= 14 ? "text-emerald-400" : at.textFaint}`}>
                                {user.last_mix_date === "-" || !user.last_mix_date ? "Never" : user.last_mix_date}
                              </td>
                              <td className={`px-4 py-3 text-xs font-medium ${user.days_inactive !== null && user.days_inactive <= 14 ? "text-emerald-400" : "text-amber-400"}`}>
                                {user.days_inactive !== null ? `${user.days_inactive}d` : "—"}
                              </td>
                              <td className={`px-4 py-3 text-[10px] ${at.textFaint}`}>v{user.version}</td>
                              <td className={`px-4 py-3 text-xs ${at.textSec} max-w-[210px] truncate`} title={entry?.sc.name}>
                                {entry?.sc.sumitId
                                  ? <a href={summitCustomerUrl(entry.sc.sumitId)} target="_blank" rel="noreferrer" className="hover:text-indigo-400">{entry.sc.name}</a>
                                  : entry?.sc.name || "—"}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {reconciliation?.confirmed && reconciliation.status === "subscription_never_paid"
                                  ? <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/20">
                                      No subscription — since {reconciliation.unpaidFromMonth}
                                    </span>
                                  : entry
                                  ? <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-orange-500/10 text-orange-400 border border-orange-500/20">
                                      Stopped — {entry.sc.lastPaidMonth}
                                    </span>
                                  : <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/20">Not in Summit</span>}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {debt
                                  ? <a
                                      href={debtActionUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      title={debt.issue.note}
                                      className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20"
                                    >
                                      {debt.issue.status === "annual_renewal_due" ? "Annual renewal due" : "Update payment method"}
                                    </a>
                                  : reconciliation?.confirmed && reconciliation.status === "subscription_never_paid"
                                  ? <a
                                      href={summitCustomerUrl(reconciliation.sumitId)}
                                      target="_blank"
                                      rel="noreferrer"
                                      title={reconciliation.note}
                                      className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20"
                                    >
                                      Never paid subscription
                                    </a>
                                  : <span className={`text-[10px] ${at.textFaint}`}>Status review</span>}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {debt
                                  ? <div>
                                      <p className="text-xs font-semibold text-rose-400">₪{debt.estimatedDebt.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                                      <p className={`text-[9px] ${at.textFaint}`}>{debt.basis}</p>
                                    </div>
                                  : <span className={at.textFaint}>—</span>}
                              </td>
                            </tr>
                          );
                        })}
                        {usingNotPaying.length === 0 && (
                          <tr><td colSpan={11} className={`py-10 text-center text-sm ${at.textFaint}`}>All active users are paying!</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── VIEW: Active app users whose billing identity is unresolved ── */}
              {billingView === "needs_mapping" && (
                <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
                  <div className={`px-5 py-3 border-b ${at.border} flex flex-wrap items-center gap-2`}>
                    <span className={`text-sm font-semibold ${at.textPrimary}`}>Active in app — billing identity needs mapping</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-400 font-medium">{needsMapping.length}</span>
                    <span className={`text-[10px] ${at.textFaint}`}>These customers are not counted as non-paying until a Summit account is matched.</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${at.border}`}>
                          {["Salon", "Phone", "Country", "Last Mix", "Days Inactive", "Version", "Reconciliation"].map(h => (
                            <th key={h} className={`px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint} whitespace-nowrap`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${at.rowDivide}`}>
                        {needsMapping.map((user) => {
                          const summitHref = getExternalHref(user.summit, "summit");
                          return (
                            <tr key={user.id} className={`${at.rowHover} transition`}>
                              <td className={`px-4 py-3 font-medium text-xs ${at.textPrimary} max-w-[210px] truncate`} title={user.salon_name}>{user.salon_name}</td>
                              <td className={`px-4 py-3 text-xs font-mono ${at.textFaint}`}>{user.phone_number}</td>
                              <td className={`px-4 py-3 text-xs ${at.textFaint}`}>{user.normalized_country || "—"}</td>
                              <td className={`px-4 py-3 text-[10px] font-medium ${user.days_inactive !== null && user.days_inactive <= 14 ? "text-emerald-400" : at.textFaint}`}>{user.last_mix_date}</td>
                              <td className={`px-4 py-3 text-xs font-medium ${user.days_inactive !== null && user.days_inactive <= 14 ? "text-emerald-400" : "text-amber-400"}`}>{user.days_inactive !== null ? `${user.days_inactive}d` : "—"}</td>
                              <td className={`px-4 py-3 text-[10px] ${at.textFaint}`}>v{user.version}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {summitHref
                                  ? <a href={summitHref} target="_blank" rel="noreferrer" className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20">
                                      Linked Summit ID · missing billing row
                                    </a>
                                  : <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-sky-500/10 text-sky-400 border border-sky-500/20">
                                      Match owner / business name
                                    </span>}
                              </td>
                            </tr>
                          );
                        })}
                        {needsMapping.length === 0 && (
                          <tr><td colSpan={7} className={`py-10 text-center text-sm ${at.textFaint}`}>Every active user has a billing identity.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── VIEW: Stopped subscriptions ── */}
              {billingView === "stopped" && (
                <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
                  <div className={`px-5 py-3 border-b ${at.border} flex items-center gap-2`}>
                    <span className={`text-sm font-semibold ${at.textPrimary}`}>Stopped subscription — paid in the past, no longer paying</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 font-medium">{stoppedSubs.length}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${at.border}`}>
                          {["Summit Name", "App Match", "Type", "Monthly Sub", "LTV", "Started", "Stopped", "Last Mix App"].map(h => (
                            <th key={h} className={`px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint} whitespace-nowrap`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${at.rowDivide}`}>
                        {stoppedSubs.map(({ sc, user }) => {
                          const ptCfg = PAYMENT_TYPE_CONFIG[sc.paymentType];
                          return (
                            <tr key={sc.name} className={`${at.rowHover} transition`}>
                              <td className={`px-4 py-3 font-medium text-xs ${at.textPrimary} max-w-[160px] truncate`} title={sc.name}>{sc.name}</td>
                              <td className={`px-4 py-3 text-xs ${at.textMuted} max-w-[150px] truncate`} title={user?.salon_name}>
                                {user ? user.salon_name : <span className="text-orange-400 text-[10px]">Not found</span>}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-[10px] font-medium ${ptCfg?.color}`}>{ptCfg?.label}</span>
                              </td>
                              <td className={`px-4 py-3 text-xs ${at.textFaint}`}>
                                {sc.typicalMonthly ? `₪${sc.typicalMonthly}/mo` : "—"}
                              </td>
                              <td className={`px-4 py-3 text-xs font-medium ${at.textSec}`}>
                                ₪{Math.round(sc.ltv || 0).toLocaleString()}
                              </td>
                              <td className={`px-4 py-3 text-[10px] ${at.textFaint}`}>{sc.firstPaidMonth || "—"}</td>
                              <td className={`px-4 py-3 text-[10px] text-amber-400 font-medium`}>{sc.lastPaidMonth || "—"}</td>
                              <td className={`px-4 py-3 text-[10px] ${at.textFaint}`}>
                                {user ? (user.last_mix_date === "-" || !user.last_mix_date ? "Never" : user.last_mix_date) : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── VIEW: Equipment / installation only ── */}
              {billingView === "equipment" && (
                <div className={`rounded-2xl border overflow-hidden ${at.card}`}>
                  <div className={`px-5 py-3 border-b ${at.border} flex items-center gap-2`}>
                    <span className={`text-sm font-semibold ${at.textPrimary}`}>Equipment & Installation — one-time payments, no recurring subscription</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 font-medium">{equipmentOnly.length}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${at.border}`}>
                          {["Summit Name", "App Match", "Equipment Total", "Payment Month", "Last Mix App", "Active?"].map(h => (
                            <th key={h} className={`px-4 py-3 text-left text-[11px] uppercase tracking-wider font-medium ${at.textFaint} whitespace-nowrap`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${at.rowDivide}`}>
                        {equipmentOnly.map(({ sc, user }) => (
                          <tr key={sc.name} className={`${at.rowHover} transition`}>
                            <td className={`px-4 py-3 font-medium text-xs ${at.textPrimary} max-w-[160px] truncate`} title={sc.name}>{sc.name}</td>
                            <td className={`px-4 py-3 text-xs ${at.textMuted} max-w-[150px] truncate`} title={user?.salon_name}>
                              {user ? user.salon_name : <span className="text-orange-400 text-[10px]">Not found</span>}
                            </td>
                            <td className={`px-4 py-3 text-xs font-medium text-blue-400`}>₪{Math.round(sc.ltv || 0).toLocaleString()}</td>
                            <td className={`px-4 py-3 text-[10px] ${at.textFaint}`}>{sc.firstPaidMonth || "—"}</td>
                            <td className={`px-4 py-3 text-[10px] ${at.textFaint}`}>
                              {user ? (user.last_mix_date === "-" || !user.last_mix_date ? "Never" : user.last_mix_date) : "—"}
                            </td>
                            <td className="px-4 py-3">
                              {user && user.days_inactive !== null && user.days_inactive <= 30
                                ? <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Active</span>
                                : <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-gray-500/10 text-gray-400 border border-gray-500/20">Inactive</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === "import" && (
          <UsageImportPanel isDark={isDark} at={at} onUsageDataChanged={refreshGlobalMixIndex} />
        )}

        {activeTab === "catalog" && (
          <ProductCatalogImportPanel isDark={isDark} at={at} />
        )}

        {activeTab === "truth" && (
          <ProductTruthCenterPanel
            isDark={isDark}
            at={at}
            initialTab={
              activeDataTab === "review" ? "review"
              : activeDataTab === "ai"   ? "ai"
              : undefined
            }
          />
        )}

        {activeTab === "products" && (
          <CatalogBrowserPanel isDark={isDark} at={at} />
        )}

        {activeDataTab === "truss" && adminDomain === "data" && (
          <React.Suspense fallback={<div className={`py-8 text-center text-sm ${at.textMuted}`}>Loading TRUSS catalog…</div>}>
            <TrussCatalogPanel isDark={isDark} at={at} />
          </React.Suspense>
        )}

        {activeTab === "beauty" && (
          <BeautyIntelligencePanel isDark={isDark} at={at} />
        )}

        {activeDataTab === "database" && adminDomain === "data" && (
          <ProductDatabasePage />
        )}

        {activeDataTab === "resolution" && adminDomain === "data" && (
          <ProductResolutionPage />
        )}

        {/* Footer */}
        <div className="text-center py-6">
          <p className={`text-[11px] ${at.textDim}`}>Spectra CI — Admin Dashboard</p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// 14. EXPORT (with theme provider)
// ═══════════════════════════════════════════════════════════════════════

export const AdminDashboard: React.FC = () => (
  <SiteThemeProvider>
    <AdminDashboardInner />
  </SiteThemeProvider>
);
